import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
  forwardRef,
  useImperativeHandle,
} from "react";
import { useAuth } from "../../auth/contexts/AuthContext";
import { useTranslation } from "react-i18next";
import {
  useSelection,
  AssemblyProject,
  AssemblyFolder,
  SelectableItem,
} from "../../common/contexts/SelectionContext";
import { useTags } from "../../common/contexts/TagsContext";
import { useProjects } from "../../common/contexts/ProjectContext";
import { CustomDialog } from "../../common/components/CustomDialog";
import { DateCarousel, DayInfo } from "../../executive/components/DateCarousel";

import { estimateSpokenDurationMs } from "../../../utils/estimateSpokenDuration";
import { supabase } from "../../../services/supabase";
import { LogCard } from "./LogCard";
import CortesEditor from "./CortesEditor";
import { BLOCK_PLECA } from "./CortesEditor/schema";
import { RelayClipPlayer, type RelayClipPlayerHandle } from "./RelayClipPlayer";
import { CLIP_BEFORE_SEC, getLogWallClock, resolveRelayChannel } from "../utils/relayClip";
import { StarRating } from "../../../components/ui/star-rating";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogClose,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { socketService } from "../../../services/socket";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import logoUrl from "../../../assets/app-logo.png";

const RATING_COLORS: Record<number, string> = {
  0: "White",
  1: "Yellow",
  2: "Red",
};

const DEFAULT_COLOR = "White";
const DEFAULT_TRACK = "V1";
const DEFAULT_FRAME_DURATION = "1";

// ─── VTR Workflow ────────────────────────────────────────────────────────────
type VtrStatus = "Por asignar" | "LISTO EDICION" | "REV FIO" | "PT" | "APROBADO" | "CANCELADO";

/** Full background color for the entire row, keyed by status */
const VTR_ROW_BG: Record<VtrStatus, string> = {
  "Por asignar": "transparent",
  "LISTO EDICION": "rgba(234,179,8,0.07)",
  "REV FIO": "rgba(168,85,247,0.08)",
  PT: "rgba(126,58,242,0.10)",
  APROBADO: "rgba(34,197,94,0.08)",
  CANCELADO: "rgba(239,68,68,0.06)",
};

/** Left border accent per status */
const VTR_ROW_BORDER: Record<VtrStatus, string> = {
  "Por asignar": "3px solid transparent",
  "LISTO EDICION": "3px solid #eab308",
  "REV FIO": "3px solid #a855f7",
  PT: "3px solid #7e3af2",
  APROBADO: "3px solid #22c55e",
  CANCELADO: "3px solid #ef4444",
};

/** Text badge color per status */
const VTR_STATUS_COLOR: Record<VtrStatus, string> = {
  "Por asignar": "#6b7280",
  "LISTO EDICION": "#eab308",
  "REV FIO": "#a855f7",
  PT: "#7e3af2",
  APROBADO: "#22c55e",
  CANCELADO: "#ef4444",
};

/** Display label per status */
const VTR_STATUS_LABEL: Record<VtrStatus, string> = {
  "Por asignar": "POR ASIGNAR",
  "LISTO EDICION": "LISTO EDICIÓN",
  "REV FIO": "REV FIO",
  PT: "PT",
  APROBADO: "APROBADO",
  CANCELADO: "CANCELADO",
};

interface VtrTransition {
  label: string;
  next: VtrStatus;
  icon: string;
}

/**
 * Allowed transitions per role per current status.
 * If the array is empty → read-only for that role in that state.
 */
const VTR_TRANSITIONS: Record<string, Partial<Record<VtrStatus, VtrTransition[]>>> = {
  // Content Director drives most of the workflow
  content_director: {
    "Por asignar": [{ label: "Listo para edición", next: "LISTO EDICION", icon: "bx-send" }],
    "REV FIO": [
      { label: "Aprobar revisión → PT", next: "PT", icon: "bx-check-circle" },
      { label: "Cancelar", next: "CANCELADO", icon: "bx-x-circle" },
    ],
    PT: [
      { label: "Aprobar", next: "APROBADO", icon: "bx-badge-check" },
      { label: "Cancelar", next: "CANCELADO", icon: "bx-x-circle" },
    ],
    APROBADO: [],
    CANCELADO: [],
  },
  // Editor solo puede actuar cuando el estado es LISTO EDICION o PT
  editor: {
    "LISTO EDICION": [{ label: "Enviar a revisión", next: "REV FIO", icon: "bx-revision" }],
    // Once director approves (→ PT), editor works in PT — no additional state change needed
  },
  // Admin: same visibility as content_director for management
  admin: {
    "Por asignar": [{ label: "Listo para edición", next: "LISTO EDICION", icon: "bx-send" }],
    "LISTO EDICION": [{ label: "Enviar a revisión", next: "REV FIO", icon: "bx-revision" }],
    "REV FIO": [
      { label: "Aprobar revisión → PT", next: "PT", icon: "bx-check-circle" },
      { label: "Cancelar", next: "CANCELADO", icon: "bx-x-circle" },
    ],
    PT: [
      { label: "Aprobar", next: "APROBADO", icon: "bx-badge-check" },
      { label: "Cancelar", next: "CANCELADO", icon: "bx-x-circle" },
    ],
  },
};

/** Normalize any role variant → canonical key used in VTR_TRANSITIONS */
function normalizeVtrRole(role: string): string {
  const r = role.toLowerCase().replace(/[\s_-]+/g, "");
  if (r.includes("contentdirector") || r.includes("director")) return "content_director";
  if (r.includes("editor")) return "editor";
  if (r.includes("superadmin") || r.includes("sadmin")) return "admin";
  if (r.includes("admin")) return "admin";
  return "editor"; // default: least privileges
}

function getVtrTransitions(role: string, status: VtrStatus): VtrTransition[] {
  const key = normalizeVtrRole(role);
  const byRole = VTR_TRANSITIONS[key] || {};
  return byRole[status] || [];
}
// ─────────────────────────────────────────────────────────────────────────────

function colorForRating(rating?: number) {
  if (rating === undefined || rating === null) return DEFAULT_COLOR;
  return RATING_COLORS[rating] || DEFAULT_COLOR;
}

function normalizeTc(tc?: string) {
  if (!tc) return "00:00:00:00";

  let timeStr = tc;
  if (/^\d{4}-\d{2}-\d{2}T/.test(tc)) {
    // ISO timestamp: add Z if missing, then extract UTC hours (same as UI's formatDisplayTc)
    const ts = tc.includes("Z") || tc.includes("+") ? tc : tc + "Z";
    try {
      const d = new Date(ts);
      if (!Number.isNaN(d.getTime())) {
        const pad = (n: number) => String(n).padStart(2, "0");
        const ms = d.getUTCMilliseconds();
        const frames = String(Math.floor((ms / 1000) * 25)).padStart(2, "0");
        return `${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}:${pad(d.getUTCSeconds())}:${frames}`;
      }
    } catch {
      /* fallback below */
    }
    // Fallback: extract from string
    const match = tc.match(/T(\d{2}[:;]\d{2}[:;]\d{2})/);
    if (match) timeStr = match[1];
  }

  let clean = timeStr.trim().replace(/;/g, ":");
  clean = clean.replace(/\.\d+/g, "");

  const parts = clean.split(":");
  if (parts.length >= 4) return parts.slice(0, 4).join(":");
  if (parts.length === 3) return `${clean}:00`;
  if (parts.length === 2) return `00:${clean}:00`;
  if (parts.length === 1) return `00:00:${clean}:00`;
  return "00:00:00:00";
}

function sanitizeField(value?: string | null) {
  if (value == null) return "";
  return String(value)
    .replace(/[\t\r\n]+/g, " ")
    .trim();
}

// Local-time YYYY-MM-DD from an ISO timestamp or epoch ms so editors see
// the recording date inline next to the TC. Empty string when the input
// isn't a parseable date.
function extractDate(value?: string | number | null) {
  if (value == null || value === "") return "";
  let input: string | number = value;
  // `start_at` arrives from the DB as a timezone-less ISO string (e.g.
  // "2026-06-03T04:45:44.038") but represents UTC. Without a Z suffix the
  // browser parses it as local time, shifting the date by a day in zones
  // west of UTC. Force UTC interpretation when no offset is present.
  if (typeof value === "string") {
    const hasTz = /(Z|[+-]\d{2}:?\d{2})$/.test(value);
    const isIsoDateTime = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(value);
    if (isIsoDateTime && !hasTz) input = `${value}Z`;
  }
  const d = new Date(input);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function buildFilename(projectName: string) {
  const slug =
    (projectName || "project")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/(^-|-$)/g, "") || "project";
  const now = new Date();
  const pad = (n: number) => String(n).padStart(2, "0");
  const hhmm = `${pad(now.getHours())}${pad(now.getMinutes())}`;
  const ymd = `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}`;
  return `${slug}_${hhmm}_${ymd}.txt`;
}

// Prefijo que identifica el tipo del item en exportes de VTR. Para items
// promovidos por OTF MODE (type === "otf" + originType set) muestra el origen
// — "[OTF←LOG]" / "[OTF←TRANS]" — para que el editor pueda rastrearlo al
// abrir el AVID/DOC/PDF. Items que nacieron como OTF en origen se exportan
// con el prefijo neutral "[OTF]". Logs y transcripts no llevan prefijo.
function itemTypePrefix(item: SelectableItem): string {
  if (item.type !== "otf") return "";
  if (item.originType === "log") return "[OTF←LOG] ";
  if (item.originType === "transcript" || item.originType === "transcription")
    return "[OTF←TRANS] ";
  return "[OTF] ";
}

function exportActiveProjectMarker(project: AssemblyProject) {
  if (!project || project.items.length === 0) {
    toast.error("No hay cortes para exportar");
    return;
  }

  try {
    const lines = project.items.map((item) => {
      const tc = normalizeTc(item.tc || item.tcIn);
      const color = colorForRating(item.rating);
      // Use selectedText if available, otherwise title
      const content = item.selectedText || item.title || "(sin comentario)";
      const comment = sanitizeField(`${itemTypePrefix(item)}${content}`);
      // For transcripts, speakerName or author might be present
      const user = sanitizeField(item.speakerName || item.author) || "unknown";

      // AVID marker format (8 columns):
      // <User>\t<TC>\t<Track>\t<Color>\t<Comment>\t<FrameDuration>\t<Empty>\t<Color>
      return [user, tc, DEFAULT_TRACK, color, comment, DEFAULT_FRAME_DURATION, "", color].join(
        "\t",
      );
    });

    const body = lines.join("\n") + "\n";
    const filename = buildFilename(project.name);

    const blob = new Blob([body], { type: "text/plain; charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();

    // Fix: Defer removal and revocation to ensure the browser has time to register the download
    setTimeout(() => {
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }, 1000);
    toast.success("Descarga de marcadores iniciada");
  } catch (error: any) {
    console.error("Error al exportar marcadores:", error);
    toast.error("Error al exportar marcadores: " + error.message);
  }
}

function exportActiveProjectTxt(project: AssemblyProject) {
  if (!project || project.items.length === 0) {
    toast.error("No hay cortes para exportar");
    return;
  }
  if (!projectHasPleca(project)) {
    toast.warning("El corte debe tener al menos una pleca");
    return;
  }

  try {
    // Group items by rundown in their existing order. We key on rundownId so
    // a rename doesn't split into two groups; the header uses the snapshot
    // name stored at insert time. Items without rundown info fall under a
    // "Sin rundown" bucket so legacy rows still export cleanly.
    const groups: Array<{ key: string; label: string; items: SelectableItem[] }> = [];
    const groupIndex = new Map<string, number>();
    for (const item of project.items) {
      const key = item.rundownId || "__none__";
      const label = item.rundownName || (item.rundownId ? item.rundownId : "Sin rundown");
      let idx = groupIndex.get(key);
      if (idx === undefined) {
        idx = groups.length;
        groupIndex.set(key, idx);
        groups.push({ key, label, items: [] });
      }
      groups[idx].items.push(item);
    }

    const sections = groups.map((g) => {
      const header = `── RUNDOWN: ${g.label} ──`;
      const lines = g.items.map((item) => {
        const tc = normalizeTc(item.tc || item.tcIn);
        const date = extractDate(item.tcIn);
        const content = item.selectedText || item.title || "(sin comentario)";
        const info = sanitizeField(`${itemTypePrefix(item)}${content}`);
        return `${date}\t${tc}\t${info}`;
      });
      return [header, ...lines].join("\n");
    });

    const body = sections.join("\n\n") + "\n";
    const filename = buildFilename(project.name);

    const blob = new Blob([body], { type: "text/plain; charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();

    setTimeout(() => {
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }, 1000);
    toast.success("Descarga de TXT iniciada");
  } catch (error: any) {
    console.error("Error al exportar TXT:", error);
    toast.error("Error al exportar TXT: " + error.message);
  }
}

function escapeHtml(s: unknown): string {
  return String(s ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function hasVtrSummary(project: AssemblyProject): boolean {
  return !!(project.vtrDescription && project.vtrDescription.trim());
}

// Un corte no puede exportarse (txt/doc/pdf) ni pasar a VTR sin al menos una
// pleca: es la que estructura el documento. Las plecas viven exclusivamente en
// `cortesDoc` (no se persisten en `items`), así que esa es la única fuente.
function projectHasPleca(project: AssemblyProject): boolean {
  const doc = Array.isArray(project.cortesDoc) ? project.cortesDoc : [];
  return doc.some((n) => (n as any)?.type === BLOCK_PLECA);
}

function exportActiveProjectDoc(project: AssemblyProject) {
  if (!project || project.items.length === 0) {
    toast.error("No hay cortes para exportar");
    return;
  }
  if (!hasVtrSummary(project)) {
    toast.error("Agregá una descripción al VTR antes de exportar");
    return;
  }
  if (!projectHasPleca(project)) {
    toast.warning("El corte debe tener al menos una pleca");
    return;
  }

  try {
    const rows = project.items
      .map((item) => {
        const tc = normalizeTc(item.tc || item.tcIn);
        const content = item.selectedText || item.title || "(sin comentario)";
        const user = item.speakerName || item.author || "unknown";
        const prefixedContent = `${itemTypePrefix(item)}${content}`;
        return `<tr><td>${escapeHtml(tc)}</td><td>${escapeHtml(DEFAULT_TRACK)}</td><td>${escapeHtml(prefixedContent)}</td><td>${escapeHtml(user)}</td></tr>`;
      })
      .join("");

    const meta: string[] = [];
    meta.push(`<p><strong>Descripción:</strong> ${escapeHtml(project.vtrDescription)}</p>`);
    if (project.vtrGrafismo)
      meta.push(`<p><strong>Grafismo:</strong> ${escapeHtml(project.vtrGrafismo)}</p>`);
    if (project.vtrNotas)
      meta.push(`<p><strong>Notas para edición:</strong> ${escapeHtml(project.vtrNotas)}</p>`);

    const html = `<!DOCTYPE html>
<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:w="urn:schemas-microsoft-com:office:word" xmlns="http://www.w3.org/TR/REC-html40">
<head>
<meta charset="utf-8">
<title>${escapeHtml(project.name)}</title>
<style>
  body { font-family: Calibri, Arial, sans-serif; font-size: 11pt; color: #1f2937; }
  h1 { font-size: 18pt; margin: 0 0 4pt 0; }
  .meta { color: #6b7280; font-size: 10pt; margin-bottom: 12pt; }
  table { border-collapse: collapse; width: 100%; }
  th, td { border: 1px solid #cbd5e1; padding: 6pt 8pt; text-align: left; vertical-align: top; }
  th { background: #16a085; color: #fff; }
</style>
</head>
<body>
  <h1>${escapeHtml(project.name)}</h1>
  <p class="meta">Generated: ${escapeHtml(new Date().toLocaleString())}</p>
  ${meta.join("")}
  <table>
    <thead><tr><th>Timecode</th><th>Track</th><th>Comment/Text</th><th>User</th></tr></thead>
    <tbody>${rows}</tbody>
  </table>
</body>
</html>`;

    const filename = buildFilename(project.name).replace(".txt", ".doc");
    const blob = new Blob(["﻿", html], { type: "application/msword" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();

    setTimeout(() => {
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }, 1000);
    toast.success("Descarga de DOC iniciada");
  } catch (error: any) {
    console.error("Error al exportar DOC:", error);
    toast.error("Error al exportar DOC: " + error.message);
  }
}

async function getBase64Logo(): Promise<string> {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext("2d");
      if (ctx) {
        ctx.drawImage(img, 0, 0);
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const data = imageData.data;
        // The logo has "br" (left), "Ai" (middle on pink square), "ndOg" (right)
        // Recolor white-ish pixels to pink (247, 45, 96), but ONLY in the left 25% and right 50%
        for (let i = 0; i < data.length; i += 4) {
          const x = (i / 4) % canvas.width;
          if (data[i] > 200 && data[i + 1] > 200 && data[i + 2] > 200 && data[i + 3] > 50) {
            if (x < canvas.width * 0.25 || x > canvas.width * 0.45) {
              data[i] = 247;
              data[i + 1] = 45;
              data[i + 2] = 96;
            }
          }
        }
        ctx.putImageData(imageData, 0, 0);
        resolve(canvas.toDataURL("image/png"));
      } else {
        resolve("");
      }
    };
    img.onerror = () => resolve("");
    img.src = logoUrl;
  });
}

async function exportActiveProjectPDF(
  project: AssemblyProject,
  allTagsData: any[],
  categories: any[],
  userName?: string,
) {
  if (!project || project.items.length === 0) {
    toast.error("No hay cortes para exportar");
    return;
  }
  if (!hasVtrSummary(project)) {
    toast.error("Agregá una descripción al VTR antes de exportar");
    return;
  }
  if (!projectHasPleca(project)) {
    toast.warning("El corte debe tener al menos una pleca");
    return;
  }

  try {
    const enrichedItems = await enrichProjectItems(project, allTagsData, categories);
    const logoB64 = await getBase64Logo();
    let totalMs = 0;
    enrichedItems.forEach((item) => {
      const realType = getItemRealType(item);
      const isV2 = realType === "pleca" || realType === "recurso";
      if (!isV2) {
        totalMs += getItemDurationMs(item);
      }
    });

    const doc = new jsPDF({ orientation: "landscape" });
    const pageWidth = doc.internal.pageSize.width;

    // Draw Header
    if (logoB64) {
      doc.addImage(logoB64, "PNG", 14, 15, 28, 8);
    }

    doc.setFontSize(16);
    doc.setTextColor(50);
    doc.text(project.name.toUpperCase(), pageWidth - 14, 21, { align: "right" });

    const projectDate = new Date(project.createdAt).toLocaleDateString("es-ES", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
    const creatorName = userName || "Desconocido";

    doc.setFontSize(10);
    doc.setTextColor(100);
    doc.text(`Creado por: ${creatorName} | Fecha del Corte: ${projectDate}`, pageWidth - 14, 27, {
      align: "right",
    });

    // Draw a subtle line under the header
    doc.setDrawColor(230);
    doc.setLineWidth(0.5);
    doc.line(14, 30, pageWidth - 14, 30);

    let currentY = 40;
    if (project.vtrDescription || project.vtrGrafismo || project.vtrNotas) {
      doc.setFontSize(10);
      doc.setTextColor(60);
      if (project.vtrDescription) {
        const lines = doc.splitTextToSize(`Descripción: ${project.vtrDescription}`, 260);
        doc.text(lines, 14, currentY);
        currentY += lines.length * 5 + 2;
      }
      if (project.vtrGrafismo) {
        const lines = doc.splitTextToSize(`Grafismo: ${project.vtrGrafismo}`, 260);
        doc.text(lines, 14, currentY);
        currentY += lines.length * 5 + 2;
      }
      if (project.vtrNotas) {
        const lines = doc.splitTextToSize(`Notas para edición: ${project.vtrNotas}`, 260);
        doc.text(lines, 14, currentY);
        currentY += lines.length * 5 + 2;
      }
      currentY += 4;
    }

    const tableData = enrichedItems.map((item) => {
      let typeStr = getItemRealType(item);
      let text = item.selectedText || item.title || (item as any).text || "";
      const upperText = text.toUpperCase();

      // Fallback a texto si es que tiene las marcas explícitamente en el texto
      if (typeStr !== "pleca" && typeStr !== "recurso") {
        if (upperText.includes("[PLECA]")) typeStr = "pleca";
        else if (
          upperText.includes("[RECURSO]") ||
          upperText.includes("[GRÁFICO]") ||
          upperText.includes("[GRAFICO]")
        )
          typeStr = "recurso";
      }

      const isV2 = typeStr === "pleca" || typeStr === "recurso";
      const tcIn = normalizeTc(item.tc || item.tcIn || "");
      const tcOut = isV2 ? "" : normalizeTc(item.tcOut || "");
      const dur = isV2 ? "" : formatTotalDuration(getItemDurationMs(item));
      const date = item.resolvedDate;
      const stream = item.resolvedStream;

      if (!text && isV2) text = `[${typeStr.toUpperCase()}]`;
      else if (!text) text = "(sin comentario)";

      const user = sanitizeField(item.speakerName || item.author) || "N/A";

      return [tcIn, tcOut, dur, date, stream, text, user, typeStr];
    });

    const PDF_COLORS: Record<string, { bg: number[]; text: number[] }> = {
      log: { bg: [252, 235, 240], text: [247, 45, 96] },
      transcription: { bg: [245, 234, 247], text: [156, 39, 176] },
      transcript: { bg: [245, 234, 247], text: [156, 39, 176] },
      otf: { bg: [230, 252, 255], text: [0, 180, 220] },
      pleca: { bg: [255, 243, 205], text: [180, 130, 0] },
      recurso: { bg: [230, 232, 240], text: [80, 90, 110] },
    };

    // Build styled body: embed colors directly into cell objects
    const styledBody = tableData.map((row) => {
      const rawType = row[7] as string;
      const type =
        rawType === "otf" ||
        rawType === "transcription" ||
        rawType === "transcript" ||
        rawType === "pleca" ||
        rawType === "recurso"
          ? rawType
          : "log";
      const theme = PDF_COLORS[type] || PDF_COLORS["log"];
      const isV2 = type === "pleca" || type === "recurso";
      const bg = theme.bg as [number, number, number];
      const fg = theme.text as [number, number, number];

      return row.slice(0, 7).map((cell, colIdx) => {
        if (isV2) {
          return {
            content: String(cell),
            styles: { fillColor: bg, textColor: fg, fontStyle: "bold" as const },
          };
        } else if (colIdx === 5) {
          return { content: String(cell), styles: { fillColor: bg, textColor: fg } };
        }
        return String(cell);
      });
    });

    autoTable(doc, {
      startY: currentY,
      head: [["TC IN", "TC OUT", "Duración", "Fecha", "Stream", "Contenido", "Operario"]],
      body: styledBody,
      theme: "grid",
      styles: { fontSize: 9, cellPadding: 3 },
      headStyles: { fillColor: [247, 45, 96], textColor: [255, 255, 255], fontStyle: "bold" },
      columnStyles: {
        0: { cellWidth: 25 },
        1: { cellWidth: 25 },
        2: { cellWidth: 20 },
        3: { cellWidth: 22 },
        4: { cellWidth: 25 },
        5: { cellWidth: "auto" },
        6: { cellWidth: 30 },
      },
      didDrawPage: function (data) {
        doc.setFontSize(9);
        doc.setTextColor(150);

        // Footer (left)
        const dateStr = new Date().toLocaleDateString("es-ES", {
          weekday: "long",
          year: "numeric",
          month: "long",
          day: "numeric",
        });
        const timeStr = new Date().toLocaleTimeString("es-ES");
        doc.text(
          `Generado el ${dateStr} a las ${timeStr} | Duración Total: ${formatTotalDuration(totalMs)}`,
          14,
          doc.internal.pageSize.height - 10,
        );

        // Footer (right)
        doc.text(
          `Página ${doc.getNumberOfPages()}`,
          doc.internal.pageSize.width - 14,
          doc.internal.pageSize.height - 10,
          { align: "right" },
        );
      },
    });

    const filename = buildFilename(project.name).replace(".txt", ".pdf");
    doc.save(filename);
    toast.success("Descarga de PDF iniciada");
  } catch (error: any) {
    console.error("Error al exportar PDF:", error);
    toast.error("Error al exportar PDF: " + error.message);
  }
}

function getStreamFromTags(itemTags: string[] | undefined, allTagsData: any[], categories: any[]) {
  if (!itemTags || !itemTags.length) return "N/A";
  for (const tagId of itemTags) {
    const t = allTagsData.find((x: any) => x.id === tagId);
    if (t) {
      const c = categories.find((cat: any) => cat.id === t.categoryId);
      if (c && /stream|cámara/i.test(c.name)) return t.name;
    }
  }
  return "N/A";
}

function extractDateLabel(item: any) {
  const candidates = [
    item.date,
    item.created_at,
    item.createdAt,
    item.start_at,
    item.startAt,
    item.tcIn,
    item.tc,
  ];

  for (const raw of candidates) {
    if (!raw) continue;
    // Fast fail if it looks like a pure timecode e.g. "21:53:33:00" and not an ISO date
    if (/^\d{2}[:;]\d{2}[:;]\d{2}/.test(raw) && !raw.includes("T")) continue;

    let input = raw;
    if (typeof raw === "string") {
      const hasTz = /(Z|[+-]\d{2}:?\d{2})$/.test(raw);
      const isIsoDateTime = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(raw);
      if (isIsoDateTime && !hasTz) input = `${raw}Z`;
    }
    const d = new Date(input);
    if (!Number.isNaN(d.getTime())) {
      return d.toLocaleDateString("es-ES", { day: "2-digit", month: "2-digit", year: "numeric" });
    }
  }
  return "N/A";
}

async function enrichProjectItems(
  project: AssemblyProject,
  allTagsData: any[],
  categories: any[],
): Promise<any[]> {
  const storyIds = Array.from(new Set(project.items.map((i) => i.storyId).filter(Boolean)));
  const storyMap = new Map();
  const allCameraIds = new Set<string>();

  if (storyIds.length > 0) {
    try {
      const { data: stories, error } = await supabase
        .from("stories")
        .select("id, start_at, created_at, tags, cameras")
        .in("id", storyIds);
      if (stories && !error) {
        stories.forEach((s: any) => {
          storyMap.set(s.id, s);
          if (s.cameras && Array.isArray(s.cameras)) {
            s.cameras.forEach((cid: string) => allCameraIds.add(cid));
          }
        });
      }
    } catch (e) {
      console.warn("Failed to fetch stories for enrichment", e);
    }
  }

  project.items.forEach((item: any) => {
    if (item.cameras && Array.isArray(item.cameras)) {
      item.cameras.forEach((cid: string) => allCameraIds.add(cid));
    }
  });

  const cameraMap = new Map();
  if (allCameraIds.size > 0) {
    try {
      const { data: cameras, error } = await supabase
        .from("cameras")
        .select("id, name")
        .in("id", Array.from(allCameraIds));
      if (cameras && !error) {
        cameras.forEach((c: any) => cameraMap.set(c.id, c.name));
      }
    } catch (e) {
      console.warn("Failed to fetch cameras for enrichment", e);
    }
  }

  let creatorName = "Desconocido";
  if (project.createdBy) {
    try {
      const { data: user } = await supabase
        .from("users")
        .select("name")
        .eq("id", project.createdBy)
        .single();
      if (user) creatorName = user.name || "Desconocido";
    } catch (e) {
      /* ignore */
    }
  }

  return project.items.map((item) => {
    let date = extractDateLabel(item);
    let storyTags: string[] = [];
    let storyCameras: string[] = [];

    if (item.storyId) {
      const story = storyMap.get(item.storyId);
      if (story) {
        if (!date || date === "N/A") {
          date = extractDateLabel({ start_at: story.start_at, created_at: story.created_at });
        }
        if (story.tags && Array.isArray(story.tags)) {
          storyTags = story.tags;
        }
        if (story.cameras && Array.isArray(story.cameras)) {
          storyCameras = story.cameras;
        }
      }
    }

    const combinedTags = [...(item.tags || []), ...storyTags];
    let stream = getStreamFromTags(combinedTags, allTagsData, categories);

    if (stream === "N/A") {
      const combinedCameras = [...((item as any).cameras || []), ...storyCameras];
      if (combinedCameras.length > 0) {
        stream = combinedCameras.map((cid) => cameraMap.get(cid) || cid).join(", ");
      }
    }

    return {
      ...item,
      resolvedDate: date || "N/A",
      resolvedStream: stream,
      _projectCreator: creatorName,
    };
  });
}

async function exportActiveProjectDOC(
  project: AssemblyProject,
  allTagsData: any[],
  categories: any[],
  userName?: string,
) {
  if (!project || project.items.length === 0) {
    toast.error("No hay cortes para exportar");
    return;
  }

  try {
    const enrichedItems = await enrichProjectItems(project, allTagsData, categories);
    const logoB64 = await getBase64Logo();
    let totalMs = 0;
    enrichedItems.forEach((item) => {
      const realType = getItemRealType(item);
      const isV2 = realType === "pleca" || realType === "recurso";
      if (!isV2) {
        totalMs += getItemDurationMs(item);
      }
    });

    let content = `<style>
      @page { size: landscape; margin: 2cm; } 
      body { font-family: Arial, sans-serif; }
      .header { display: flex; justify-content: space-between; align-items: center; border-bottom: 1px solid #e5e7eb; padding-bottom: 10px; margin-bottom: 15px; }
      .header-right { font-size: 10pt; color: #6b7280; text-transform: uppercase; }
      .subtitle { font-size: 10pt; color: #6b7280; margin-bottom: 20px; }
    </style>`;

    const dateStr = new Date().toLocaleDateString("es-ES", {
      weekday: "long",
      day: "numeric",
      month: "long",
      year: "numeric",
    });

    content += `<div class="header">`;
    if (logoB64) {
      content += `<img src="${logoB64}" style="height: 24px;" />`;
    } else {
      content += `<div></div>`;
    }
    content += `<div class="header-right">${project.name}</div>`;
    content += `</div>`;

    const projectDate = new Date(project.createdAt).toLocaleDateString("es-ES", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
    const creatorName = userName || "Desconocido";

    content += `<div class="subtitle">Creado por: ${creatorName} | Fecha del Corte: ${projectDate}</div>`;
    content += `<div class="subtitle" style="margin-bottom:12px;">Generado a las ${new Date().toLocaleTimeString("es-ES")} | Duración Total: ${formatTotalDuration(totalMs)}</div>`;

    if (project.vtrDescription)
      content += `<p><strong>Descripción:</strong> ${project.vtrDescription}</p>`;
    if (project.vtrGrafismo) content += `<p><strong>Grafismo:</strong> ${project.vtrGrafismo}</p>`;
    if (project.vtrNotas) content += `<p><strong>Notas:</strong> ${project.vtrNotas}</p><br>`;

    content += `<table border="1" style="border-collapse: collapse; width: 100%; text-align: left; font-size: 11px;">`;
    content += `<tr>
      <th style="background:#f72d60; color:white; padding:8px;">TC IN</th>
      <th style="background:#f72d60; color:white; padding:8px;">TC OUT</th>
      <th style="background:#f72d60; color:white; padding:8px;">Duración</th>
      <th style="background:#f72d60; color:white; padding:8px;">Fecha</th>
      <th style="background:#f72d60; color:white; padding:8px;">Stream</th>
      <th style="background:#f72d60; color:white; padding:8px; width: 40%;">Contenido</th>
      <th style="background:#f72d60; color:white; padding:8px;">Operario</th>
    </tr>`;

    const DOC_COLORS: Record<string, { bg: string; border: string; text: string }> = {
      log: { bg: "#fcebf0", border: "#f72d60", text: "#f72d60" },
      transcription: { bg: "#f5eaf7", border: "#9c27b0", text: "#9c27b0" },
      transcript: { bg: "#f5eaf7", border: "#9c27b0", text: "#9c27b0" },
      otf: { bg: "#e6fcff", border: "#00e5ff", text: "#00e5ff" },
      pleca: { bg: "#fdf7e6", border: "#eab308", text: "#eab308" },
      recurso: { bg: "#f0f1f4", border: "#64748b", text: "#64748b" },
    };

    enrichedItems.forEach((item) => {
      let typeStr = getItemRealType(item);
      let text = item.selectedText || item.title || (item as any).text || "";
      const upperText = text.toUpperCase();

      // Fallback a texto si es que tiene las marcas explícitamente en el texto
      if (typeStr !== "pleca" && typeStr !== "recurso") {
        if (upperText.includes("[PLECA]")) typeStr = "pleca";
        else if (
          upperText.includes("[RECURSO]") ||
          upperText.includes("[GRÁFICO]") ||
          upperText.includes("[GRAFICO]")
        )
          typeStr = "recurso";
      }

      const isV2 = typeStr === "pleca" || typeStr === "recurso";
      const tcIn = normalizeTc(item.tc || item.tcIn || "");
      const tcOut = isV2 ? "" : normalizeTc(item.tcOut || "");
      const dur = isV2 ? "" : formatTotalDuration(getItemDurationMs(item));
      const date = item.resolvedDate;
      const stream = item.resolvedStream;

      if (!text && isV2) text = `[${typeStr.toUpperCase()}]`;
      else if (!text) text = "(sin comentario)";

      const user = sanitizeField(item.speakerName || item.author) || "N/A";

      const typeKey =
        typeStr === "otf" ||
        typeStr === "transcription" ||
        typeStr === "transcript" ||
        typeStr === "pleca" ||
        typeStr === "recurso"
          ? typeStr
          : "log";
      const theme = DOC_COLORS[typeKey] || DOC_COLORS["log"];

      const isFullRow = typeKey === "pleca" || typeKey === "recurso";
      const rowStyle = isFullRow
        ? `background-color:${theme.bg}; color:${theme.text}; font-weight:bold;`
        : "";

      content += `<tr>
        <td style="padding:6px; ${rowStyle}">${tcIn}</td>
        <td style="padding:6px; ${rowStyle}">${tcOut}</td>
        <td style="padding:6px; ${rowStyle}">${dur}</td>
        <td style="padding:6px; ${rowStyle}">${date}</td>
        <td style="padding:6px; ${rowStyle}">${stream}</td>
        <td style="padding:6px; background-color:${theme.bg}; border-left: 3px solid ${theme.border}; color:${theme.text}; ${isFullRow ? "font-weight:bold;" : ""}">${text}</td>
        <td style="padding:6px; ${rowStyle}">${user}</td>
      </tr>`;
    });

    content += `</table>`;

    const blob = new Blob(
      [`<html><head><meta charset="utf-8"></head><body>${content}</body></html>`],
      { type: "application/msword;charset=utf-8" },
    );
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = buildFilename(project.name).replace(".txt", ".doc");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    toast.success("Descarga de DOC iniciada");
  } catch (error: any) {
    console.error("Error al exportar DOC:", error);
    toast.error("Error al exportar DOC: " + error.message);
  }
}

function formatTotalDuration(ms: number): string {
  const totalSec = Math.round(ms / 1000);
  if (totalSec < 60) return `${totalSec}s`;
  const minutes = Math.floor(totalSec / 60);
  const seconds = totalSec % 60;
  if (minutes < 60) return `${minutes}m ${seconds}s`;
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${hours}h ${mins}m`;
}

const LOG_CUT_DURATION_MS = 3000; // Fixed 3-second duration for cortes (log items)

// Ventana de reproducción por corte en el programa (Stream Program). El programa
// es un "montaje" de previsualización: toca ~2s de cada corte y salta al siguiente.
// No usamos la duración real del corte (a veces es enorme o errónea: el OUT puede
// quedar a horas del IN), porque entonces nunca se alcanzaría el OUT y el programa
// se quedaría pegado en el primer clip sin avanzar.
const PROGRAM_PREVIEW_MS = 2000;

function getItemDurationMs(item: SelectableItem): number {
  if (item.type === "log") {
    // Cortes always count as 3 seconds
    return LOG_CUT_DURATION_MS;
  }
  // Transcripciones y TOF: use tcIn/tcOut range
  if (item.tcInMs != null && item.tcOutMs != null) {
    return Math.max(0, item.tcOutMs - item.tcInMs);
  }
  // Fallback: estimate from text length
  return estimateSpokenDurationMs(item.selectedText || item.title);
}

export function getItemRealType(item: any): string {
  if (!item) return "log";
  const typeStr = String(item.type || "").toLowerCase();
  if (typeStr === "pleca" || item.sourceId?.startsWith("pleca_") || item.id?.startsWith("pleca_")) {
    return "pleca";
  }
  if (
    typeStr === "recurso" ||
    item.sourceId?.startsWith("recurso_") ||
    item.id?.startsWith("recurso_")
  ) {
    return "recurso";
  }
  return typeStr;
}

function getProjectEstimatedDuration(project: AssemblyProject): string {
  if (!project || !project.items) return "0s";
  const totalDuration = project.items.reduce((acc, item) => {
    const typeStr = getItemRealType(item);
    if (typeStr === "pleca" || typeStr === "recurso") return acc;
    return acc + getItemDurationMs(item);
  }, 0);
  return formatTotalDuration(totalDuration);
}

export function tcToMs(tc: string, fps = 25): number {
  if (!tc) return 0;
  const parts = tc.split(":");
  if (parts.length < 3) return 0;
  const hh = parseInt(parts[0], 10) || 0;
  const mm = parseInt(parts[1], 10) || 0;
  const ss = parseInt(parts[2], 10) || 0;
  let frames = 0;
  if (parts.length >= 4) {
    frames = parseInt(parts[3], 10) || 0;
  }
  return (hh * 3600 + mm * 60 + ss) * 1000 + (frames * 1000) / fps;
}

function formatMonitorTc(value?: string | null, fps = 25): { tc: string; date?: string } {
  if (!value) return { tc: "--:--:--:--" };
  const raw = value.trim();
  const isoLike = /^\d{4}-\d{2}-\d{2}T/.test(raw);
  if (!isoLike) return { tc: raw };

  const [datePart, timePart = ""] = raw.split("T");
  const compactDate = datePart.slice(5).replace("-", "/");
  const cleanTime = timePart.replace(/Z|[+-]\d{2}:?\d{2}$/g, "");
  const [h = "--", m = "--", secPart = "--"] = cleanTime.split(":");
  const [s = "--", msPart = "0"] = secPart.split(".");
  const ms = Number(`0.${msPart || "0"}`) * 1000;
  const frame = Number.isFinite(ms) ? Math.floor((ms / 1000) * fps) : 0;

  return {
    tc: `${h.padStart(2, "0")}:${m.padStart(2, "0")}:${s.padStart(2, "0")};${String(frame).padStart(2, "0")}`,
    date: compactDate,
  };
}

function formatCompactDuration(ms: number): string {
  const totalSeconds = Math.max(0, Math.round(ms / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, "0")}`;
}

export function getLogColor(type?: string): string {
  if (!type) return "var(--color-role-sp)";
  switch (type.toLowerCase()) {
    case "transcription":
    case "transcript":
      return "#8B5CF6"; // purple (morado)
    case "otf":
      return "#06B6D4"; // turquoise (azul turquesa)
    case "srt":
      return "#3B82F6"; // blue
    case "resource":
    case "recurso":
      return "#64748b"; // gray/slate
    case "pleca":
      return "#eab308"; // yellow/amber
    case "graphic":
    case "graphics":
      return "#EC4899"; // pink
    case "log":
    default:
      return "#EC4899"; // pink
  }
}

export function getScrubberDurationMs(it: any): number {
  if (it.tcInMs && it.tcOutMs) return Math.max(0, it.tcOutMs - it.tcInMs);
  if (it.tcIn && it.tcOut && it.tcIn.includes("T")) {
    const inD = new Date(it.tcIn).getTime();
    const outD = new Date(it.tcOut).getTime();
    if (!isNaN(inD) && !isNaN(outD)) return Math.max(0, outD - inD);
  }
  if (it.duration && it.duration.includes(":")) {
    return tcToMs(it.duration);
  }
  if (it.tcIn && it.tcOut) {
    const inMs = tcToMs(it.tcIn);
    const outMs = tcToMs(it.tcOut);
    if (inMs > 0 && outMs > 0) return Math.max(0, outMs - inMs);
  }
  return 3000; // LOG_CUT_DURATION_MS
}

import { toast } from "sonner";

const VtrPopupField: React.FC<{
  value: string;
  readOnly: boolean;
  onSave: (val: string) => void;
  title: string;
  placeholder: string;
}> = ({ value, readOnly, onSave, title, placeholder }) => {
  const [local, setLocal] = useState(value || "");
  const [open, setOpen] = useState(false);

  // Sync when external value changes
  useEffect(() => {
    if (!open) {
      setLocal(value || "");
    }
  }, [value, open]);

  const handleSave = () => {
    if (local !== (value || "")) {
      onSave(local);
      toast.success(`${title} guardado`, {
        position: "bottom-right",
        duration: 2000,
        icon: <i className="bx bx-check-circle" style={{ fontSize: "1.2rem" }}></i>,
      });
    }
    setOpen(false);
  };

  const handleCancel = () => {
    setLocal(value || "");
    setOpen(false);
  };

  const displayText = value
    ? value.length > 80
      ? value.substring(0, 80) + "..."
      : value
    : placeholder;

  if (readOnly) {
    return (
      <div
        style={{
          padding: "6px 10px",
          background: "rgba(255,255,255,0.04)",
          border: "1px solid rgba(255,255,255,0.08)",
          borderRadius: "6px",
          color: value ? "#ccc" : "#666",
          fontSize: "0.75rem",
          lineHeight: 1.4,
          whiteSpace: "normal",
          display: "-webkit-box",
          WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical",
          overflow: "hidden",
        }}
        title={value || ""}
      >
        {displayText}
      </div>
    );
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger
        render={<button type="button" />}
        style={{
          padding: "6px 10px",
          background: "rgba(255,255,255,0.04)",
          border: "1px solid rgba(255,255,255,0.1)",
          borderRadius: "6px",
          color: value ? "#ccc" : "#666",
          fontSize: "0.75rem",
          lineHeight: 1.4,
          cursor: "pointer",
          transition: "all 0.2s",
          whiteSpace: "normal",
          display: "-webkit-box",
          WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical",
          overflow: "hidden",
          minHeight: "34px",
          textAlign: "left",
          width: "100%",
        }}
        title={value || "Haz clic para editar"}
        onMouseEnter={(e: any) => (e.currentTarget.style.borderColor = "rgba(255,255,255,0.25)")}
        onMouseLeave={(e: any) => (e.currentTarget.style.borderColor = "rgba(255,255,255,0.1)")}
      >
        {displayText}
      </DialogTrigger>
      <DialogContent className="sm:max-w-[800px] bg-[#1f1f1f] border-[#333] p-6 text-white shadow-2xl">
        <DialogHeader>
          <DialogTitle className="text-gray-100 text-xl font-semibold">Editar {title}</DialogTitle>
        </DialogHeader>

        <div className="py-4">
          <textarea
            value={local}
            onChange={(e) => setLocal(e.target.value)}
            placeholder={placeholder}
            rows={12}
            autoFocus
            style={{
              width: "100%",
              padding: "16px",
              background: "rgba(0,0,0,0.3)",
              border: "1px solid rgba(255,255,255,0.15)",
              borderRadius: "8px",
              color: "#fff",
              fontSize: "1rem",
              resize: "vertical",
              fontFamily: "inherit",
              lineHeight: 1.6,
              outline: "none",
              boxShadow: "inset 0 2px 4px rgba(0,0,0,0.1)",
            }}
          />
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <button
            onClick={handleCancel}
            style={{
              padding: "10px 20px",
              background: "transparent",
              border: "1px solid rgba(255,255,255,0.2)",
              borderRadius: "6px",
              color: "#bbb",
              fontSize: "0.9rem",
              cursor: "pointer",
              fontWeight: 500,
              transition: "all 0.2s",
            }}
            onMouseEnter={(e: any) => {
              e.currentTarget.style.background = "rgba(255,255,255,0.05)";
              e.currentTarget.style.color = "#fff";
            }}
            onMouseLeave={(e: any) => {
              e.currentTarget.style.background = "transparent";
              e.currentTarget.style.color = "#bbb";
            }}
          >
            Cancelar
          </button>
          <button
            onClick={handleSave}
            style={{
              padding: "10px 20px",
              background: "var(--color-role-sp, #3b82f6)",
              border: "none",
              borderRadius: "6px",
              color: "#fff",
              fontSize: "0.9rem",
              cursor: "pointer",
              fontWeight: 600,
              boxShadow: "0 4px 12px rgba(59, 130, 246, 0.3)",
              transition: "transform 0.1s",
            }}
            onMouseDown={(e: any) => (e.currentTarget.style.transform = "scale(0.97)")}
            onMouseUp={(e: any) => (e.currentTarget.style.transform = "scale(1)")}
            onMouseLeave={(e: any) => (e.currentTarget.style.transform = "scale(1)")}
          >
            Guardar
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ─── VtrStatusControl ─────────────────────────────────────────────────────────
const VtrStatusControl: React.FC<{
  status: VtrStatus;
  transitions: VtrTransition[];
  readOnly: boolean;
  onTransition: (next: VtrStatus) => void;
}> = ({ status, transitions, readOnly, onTransition }) => {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const color = VTR_STATUS_COLOR[status] ?? "#6b7280";
  const label = VTR_STATUS_LABEL[status] ?? status;

  // Close on click outside
  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  return (
    <div ref={containerRef} style={{ position: "relative", display: "inline-block" }}>
      {/* Status pill — always visible */}
      <button
        type="button"
        onClick={() => !readOnly && transitions.length > 0 && setOpen((v) => !v)}
        title={readOnly || transitions.length === 0 ? label : "Click para cambiar estado"}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: "5px",
          padding: "3px 8px",
          background: `${color}1A`,
          border: `1px solid ${color}55`,
          borderRadius: "20px",
          color,
          fontSize: "0.67rem",
          fontWeight: 700,
          letterSpacing: "0.6px",
          textTransform: "uppercase",
          cursor: readOnly || transitions.length === 0 ? "default" : "pointer",
          transition: "all 0.2s",
          whiteSpace: "nowrap",
        }}
        onMouseEnter={(e) => {
          if (!readOnly && transitions.length > 0) {
            (e.currentTarget as HTMLElement).style.background = `${color}33`;
          }
        }}
        onMouseLeave={(e) => {
          (e.currentTarget as HTMLElement).style.background = `${color}1A`;
        }}
      >
        {/* Dot indicator */}
        <span
          style={{
            width: "6px",
            height: "6px",
            borderRadius: "50%",
            background: color,
            flexShrink: 0,
            boxShadow: `0 0 4px ${color}`,
          }}
        />
        {label}
        {!readOnly && transitions.length > 0 && (
          <i className="bx bx-chevron-down" style={{ fontSize: "0.75rem" }} />
        )}
      </button>

      {/* Transitions dropdown */}
      {open && transitions.length > 0 && (
        <div
          style={{
            position: "absolute",
            top: "calc(100% + 4px)",
            left: 0,
            zIndex: 200,
            background: "#1a1a2e",
            border: "1px solid rgba(255,255,255,0.12)",
            borderRadius: "8px",
            padding: "4px",
            minWidth: "190px",
            boxShadow: "0 8px 24px rgba(0,0,0,0.5)",
          }}
        >
          {transitions.map((t) => (
            <button
              key={t.next}
              type="button"
              onClick={() => {
                onTransition(t.next);
                setOpen(false);
              }}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                width: "100%",
                padding: "7px 10px",
                background: "transparent",
                border: "none",
                borderRadius: "5px",
                color: VTR_STATUS_COLOR[t.next] ?? "#ccc",
                fontSize: "0.78rem",
                fontWeight: 600,
                cursor: "pointer",
                textAlign: "left",
                transition: "background 0.15s",
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLElement).style.background = "rgba(255,255,255,0.07)";
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLElement).style.background = "transparent";
              }}
            >
              <span
                style={{
                  width: "7px",
                  height: "7px",
                  borderRadius: "50%",
                  background: VTR_STATUS_COLOR[t.next] ?? "#ccc",
                  flexShrink: 0,
                }}
              />
              <i className={`bx ${t.icon}`} style={{ fontSize: "0.9rem", opacity: 0.7 }} />
              {t.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
// ─────────────────────────────────────────────────────────────────────────────

export interface AssemblyManagerRef {
  toggleVtrPlay: () => void;
  handlePrevClip: () => void;
  handleNextClip: () => void;
  handleStopVtr: () => void;
  seekVtrBy: (secs: number) => void;
  seekVtrDirect: (secs: number) => void;
  scrubVtrAudio: (direction: number) => void;
  pauseVtr: () => void;
  setVtrSpeed: (rate: number) => void;
  isVtrPlaying: boolean;
}

interface AssemblyManagerProps {
  showOnlyApproved?: boolean;
  readOnly?: boolean;
  onVtrPlayStarted?: () => void;
  /** True cuando solo el módulo Corte está visible — activa layout de 2 columnas */
  isOnlyCutMode?: boolean;
}

interface RelayPreviewClip {
  at: Date;
  channel: string;
  title: string;
  itemId?: string;
}

type VtrDeckId = "a" | "b";

const AssemblyManager = forwardRef<AssemblyManagerRef, AssemblyManagerProps>(
  (
    { showOnlyApproved = false, readOnly = false, onVtrPlayStarted, isOnlyCutMode = false },
    ref,
  ) => {
    const { t } = useTranslation();
    const {
      assemblyProjects,
      activeProjectId,
      setActiveProject,
      createProject,
      deleteProject,
      renameProject,
      updateProject,
      removeItemFromProject,
      updateItemInProject,
      moveItemInProject,
      getActiveProject,
      selectedItems,
      addItemsToProject,
      clearSelection,
      // Folders
      assemblyFolders,
      activeFolderId,
      setActiveFolder,
      createFolder,
      deleteFolder,
      renameFolder,
      toggleFolderExpanded,
      moveProjectToFolder,
      moveFolderToFolder,
      // Navigate to source
      setNavigateToSource,
      // Loading state
      isLoaded,
      setCutDayWindow,
    } = useSelection();

    // VTR mode: own local state fed directly from the backend endpoint.
    // Folders are also fetched here (unscoped to the current user) so VTRs
    // created inside another user's folder render under the same hierarchy.
    const [approvedProjects, setApprovedProjects] = useState<AssemblyProject[]>([]);
    const [approvedFolders, setApprovedFolders] = useState<AssemblyFolder[]>([]);
    const [isLoadingApproved, setIsLoadingApproved] = useState(false);

    // CUT view = always renders the CortesEditor. VTR/read-only views keep the
    // legacy list rendering. The list/edit toggle was removed — there is no
    // longer a way (or need) to switch back to the list view in CUT.
    const canShowEditToggle = !showOnlyApproved && !readOnly;

    const { user: authUser } = useAuth();
    const userRole: string = (authUser as any)?.role || "editor";
    // Oversight roles (content director / admin) see every project in the VTR
    // view — approved and still-in-progress — so a freshly created (unapproved)
    // VTR surfaces here for them. Other roles only ever see approved VTRs, so we
    // don't expose "Nuevo VTR" to them (the row would vanish on refetch).
    const canManageVtr =
      normalizeVtrRole(userRole) === "content_director" || normalizeVtrRole(userRole) === "admin";

    const { activeProject: currentProdProject } = useProjects();
    const prodProjectId = currentProdProject?.id;
    const { tags: allTagsData, categories } = useTags() || { tags: [], categories: [] };

    // Declared before the approved-projects effect below because that effect
    // reads vtrDayFilter in its dependency array (evaluated during render) —
    // keeping the useState later would put it in the temporal dead zone.
    const [vtrDayFilter, setVtrDayFilter] = useState<string>(() => {
      const d = new Date();
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      return `${y}-${m}-${day}`;
    }); // YYYY-MM-DD — defaults to today

    useEffect(() => {
      if (!showOnlyApproved) {
        setApprovedProjects([]);
        setApprovedFolders([]);
        setIsLoadingApproved(false);
        return;
      }

      // Wait for project context to be resolved before fetching to avoid unscoped requests
      if (!prodProjectId) {
        setApprovedProjects([]);
        setApprovedFolders([]);
        return;
      }

      const fetchApproved = async () => {
        setIsLoadingApproved(true);
        try {
          const { authFetch } = await import("../../../services/authFetch");
          const projectQs = prodProjectId ? `&projectId=${prodProjectId}` : "";
          // Scope the fetch to the selected day so the server only ships that
          // day's VTRs (+items) instead of every approved project (25MB+). The
          // window matches the client-side filter in vtrFilteredProjects:
          // [day 00:00 local, next day 00:00 local). created_at is epoch-ms.
          let dayQs = "";
          if (vtrDayFilter) {
            const from = new Date(vtrDayFilter + "T00:00:00");
            const to = new Date(from);
            to.setDate(to.getDate() + 1);
            dayQs = `&createdFrom=${from.getTime()}&createdTo=${to.getTime()}`;
          }
          const [projectsRes, foldersRes] = await Promise.all([
            authFetch(`/api/assembly/projects?approvedOnly=true${projectQs}${dayQs}`),
            authFetch(`/api/assembly/folders?approvedOnly=true${projectQs}`),
          ]);
          const projectsData = await projectsRes.json();
          const foldersData = await foldersRes.json();
          if (projectsData.projects) {
            setApprovedProjects(projectsData.projects);
          }
          if (foldersData.folders) {
            setApprovedFolders(foldersData.folders);
          }
        } catch (e) {
          console.error("Failed to fetch approved projects:", e);
        } finally {
          setIsLoadingApproved(false);
        }
      };

      fetchApproved();

      // Refetch when any project is created/updated/deleted
      const handleUpdate = () => fetchApproved();
      socketService.on("assembly-projects-updated", handleUpdate);
      return () => {
        socketService.off("assembly-projects-updated", handleUpdate);
      };
    }, [showOnlyApproved, prodProjectId, vtrDayFilter]);

    // Don't render VTR data until prodProjectId is resolved to avoid showing cross-project data
    const isProjectReady = !!prodProjectId;

    // Determine if data is currently loading
    const isDataLoading = showOnlyApproved ? isLoadingApproved : !isLoaded;

    const { getTagLabel, getTagColor, getCameraName, vtrTags, getVtrTagLabel, getVtrTagColor } =
      useTags();

    const handleProjectUpdate = (id: string, updates: Partial<AssemblyProject>) => {
      if (showOnlyApproved) {
        setApprovedProjects((prev) => prev.map((p) => (p.id === id ? { ...p, ...updates } : p)));
      }
      updateProject(id, updates);
    };

    // Promote a CUT to VTR (or un-approve). When promoting, require a synopsis —
    // VTRs without one break downstream exports and the read-only VTR view.
    const handleToggleApproved = (project: AssemblyProject) => {
      const nextApproved = !project.isApproved;
      if (nextApproved && !(project.vtrDescription || "").trim()) {
        toast.error(t("storyproducer.assembly.synopsisRequired", "El corte debe tener sinopsis"), {
          position: "bottom-right",
        });
        return;
      }
      // Un corte no puede pasar a VTR sin al menos una pleca. Las plecas viven
      // exclusivamente en `cortesDoc` (no se persisten en `items`), así que esa
      // es la única fuente para validarlas.
      if (nextApproved) {
        const doc = Array.isArray(project.cortesDoc) ? project.cortesDoc : [];
        const hasPleca = doc.some((n) => (n as any)?.type === BLOCK_PLECA);
        if (!hasPleca) {
          toast.warning(
            t("storyproducer.assembly.plecaRequired", "El corte debe tener al menos una pleca"),
            { position: "bottom-right" },
          );
          return;
        }
      }
      handleProjectUpdate(project.id, { isApproved: nextApproved });
    };

    /**
     * Dedicated handler for VTR status transitions.
     * Calls PATCH /api/assembly/projects/:id/status (role-validated endpoint)
     * instead of the generic PUT. Uses optimistic update + rollback on rejection.
     */
    const handleVtrStatusTransition = async (
      projectId: string,
      previousStatus: VtrStatus,
      nextStatus: VtrStatus,
    ) => {
      if (previousStatus === "Por asignar" && nextStatus === "LISTO EDICION") {
        const target =
          assemblyProjects.find((p) => p.id === projectId) ||
          approvedProjects.find((p) => p.id === projectId);
        if (target && !hasVtrSummary(target)) {
          toast.error("Agregá una descripción al VTR antes de mandarlo a edición", {
            position: "bottom-right",
            duration: 3500,
          });
          return;
        }
      }

      // 1. Optimistic update — update UI immediately
      const applyStatus = (s: VtrStatus) => {
        if (showOnlyApproved) {
          setApprovedProjects((prev) =>
            prev.map((p) => (p.id === projectId ? { ...p, vtrEditingStatus: s } : p)),
          );
        }
        updateProject(projectId, { vtrEditingStatus: s });
      };

      applyStatus(nextStatus);

      try {
        const { authFetch } = await import("../../../services/authFetch");
        const res = await authFetch(`/api/assembly/projects/${projectId}/status`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ newStatus: nextStatus }),
        });

        if (!res.ok) {
          const err = await res.json().catch(() => ({ error: "Error desconocido" }));
          // 2. Rollback on rejection
          applyStatus(previousStatus);
          toast.error(err.error || "No tienes permiso para realizar este cambio", {
            position: "bottom-right",
            duration: 3500,
          });
        }
      } catch (e) {
        // 3. Rollback on network error
        applyStatus(previousStatus);
        toast.error("Error de red al cambiar el estado", { position: "bottom-right" });
      }
    };

    const activeProject = showOnlyApproved
      ? approvedProjects.find((p) => p.id === activeProjectId) || null
      : getActiveProject();

    const [participants, setParticipants] = useState<any[]>([]);

    useEffect(() => {
      const handleParticipantsUpdate = (data: any[]) => {
        // Accept any valid array — this event comes from intentional user actions (add/edit/delete)
        if (Array.isArray(data)) {
          setParticipants(data);
        }
      };

      const handleInitData = (data: any) => {
        if (data.participants && Array.isArray(data.participants)) {
          setParticipants(data.participants);
        }
      };

      // Subscribe to participants updates
      socketService.on("participants-updated", handleParticipantsUpdate);
      socketService.on("init-data", handleInitData);

      // Lightweight fetch for participants only — avoid the heavy get-init-data
      // which loads staff, banks, groups, tags, cameras, etc. that AssemblyManager
      // doesn't need and that may interfere with ControllerLayout's init flow.
      // NOTE: Do NOT pass activeProject.id here — that's the assembly project ID
      // (e.g. "proj_xxx"), not the production project UUID. The server's
      // get-explore-data will use the socket's stored projectId instead.
      if (socketService.connected) {
        socketService.emit("get-explore-data", (response: any) => {
          if (response?.success && response.participants) {
            setParticipants(response.participants);
          }
        });
      }

      return () => {
        socketService.off("participants-updated", handleParticipantsUpdate);
        socketService.off("init-data", handleInitData);
      };
    }, []);

    const [editingId, setEditingId] = useState<string | null>(null);
    const [editName, setEditName] = useState("");
    const [editingFolderId, setEditingFolderId] = useState<string | null>(null);
    const [editFolderName, setEditFolderName] = useState("");
    const [newProjectName, setNewProjectName] = useState("");
    const [newFolderName, setNewFolderName] = useState("");
    const [showNewInput, setShowNewInput] = useState(false);
    const [showNewFolderInput, setShowNewFolderInput] = useState(false);
    const [creatingInFolderId, setCreatingInFolderId] = useState<string | null>(null);
    const [creatingFolderInId, setCreatingFolderInId] = useState<string | null>(null);
    const [isDraggingOver, setIsDraggingOver] = useState(false);
    const [draggedItemIndex, setDraggedItemIndex] = useState<number | null>(null);
    const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
    const [externalDropIndex, setExternalDropIndex] = useState<number | null>(null);
    const [showExportDialog, setShowExportDialog] = useState(false);
    const [projectToExport, setProjectToExport] = useState<AssemblyProject | null>(null);
    const [folderMenuOpen, setFolderMenuOpen] = useState<string | null>(null);
    const [draggedProjectId, setDraggedProjectId] = useState<string | null>(null);
    const [folderDropTargetId, setFolderDropTargetId] = useState<string | null>(null);
    const [rootExpanded, setRootExpanded] = useState(true);
    const [hoverFolderId, setHoverFolderId] = useState<string | null>(null);
    // Delete confirmation states
    const [pendingDeleteProjectId, setPendingDeleteProjectId] = useState<string | null>(null);
    const [pendingDeleteFolderId, setPendingDeleteFolderId] = useState<string | null>(null);

    // VTR Filters (only used in showOnlyApproved mode)
    const [vtrTagFilter, setVtrTagFilter] = useState<string[]>([]); // array of VTR tag IDs
    const [vtrSearchFilter, setVtrSearchFilter] = useState<string>("");
    // CUT items date filter (filters cards inside the active CUT project)
    const [cutDayFilter, setCutDayFilter] = useState<string | null>(null); // YYYY-MM-DD
    const [explorerLayout, setExplorerLayout] = useState<"list" | "cards">("list");

    // Generate recording days based on Mexico City show schedule (start date: 2026-05-17)
    const recordingDays = useMemo<DayInfo[]>(() => {
      const days: DayInfo[] = [];
      let current = new Date("2026-05-17T12:00:00-06:00");
      const todayMXString = new Date().toLocaleString("en-US", { timeZone: "America/Mexico_City" });
      const todayMXDate = new Date(todayMXString);
      todayMXDate.setHours(23, 59, 59, 999);

      let dayNumber = 1;
      while (current <= todayMXDate) {
        const mxDateString = current
          .toLocaleString("sv-SE", { timeZone: "America/Mexico_City" })
          .slice(0, 10);
        const todayIsoMX = todayMXDate
          .toLocaleString("sv-SE", { timeZone: "America/Mexico_City" })
          .slice(0, 10);
        days.push({
          iso: mxDateString,
          date: new Date(current),
          isToday: mxDateString === todayIsoMX,
          dayNumber,
        });
        current.setDate(current.getDate() + 1);
        dayNumber++;
      }
      return days;
    }, []);

    // Resolve the actual date ISO for filtering
    const effectiveCutDayFilter = useMemo(() => {
      if (cutDayFilter) return cutDayFilter;
      if (recordingDays.length > 0) {
        const todayDay = recordingDays.find((d) => d.isToday);
        return todayDay ? todayDay.iso : recordingDays[recordingDays.length - 1].iso;
      }
      return null;
    }, [cutDayFilter, recordingDays]);

    // Push the selected day down to the backend so the CUT list only fetches
    // that day. Local day boundaries, to match extractProjectDate below.
    useEffect(() => {
      if (showOnlyApproved) return;
      if (!effectiveCutDayFilter) {
        setCutDayWindow(null);
        return;
      }
      const [y, m, d] = effectiveCutDayFilter.split("-").map(Number);
      const from = new Date(y, m - 1, d).getTime();
      const to = new Date(y, m - 1, d + 1).getTime();
      if (!Number.isFinite(from) || !Number.isFinite(to)) return;
      setCutDayWindow({ from, to });
    }, [effectiveCutDayFilter, showOnlyApproved, setCutDayWindow]);
    // VTR expanded row (accordion detail)
    const [expandedVtrId, setExpandedVtrId] = useState<string | null>(null);
    // Which VTR accordion is currently being dragged over (external drops from library)
    const [isDraggingOverVtrId, setIsDraggingOverVtrId] = useState<string | null>(null);

    const [viewMode, setViewMode] = useState<"editor" | "explorer">(
      activeProjectId ? "editor" : "explorer",
    );

    // An active project identifies the editor target, but it must not control
    // navigation by itself. Day-scoped refetches may restore/select a project
    // in SelectionContext; treating that state update as navigation made a date
    // click jump from the explorer into the first corte returned by the backend.
    useEffect(() => {
      if (!activeProjectId) {
        setViewMode("explorer");
      }
    }, [activeProjectId]);

    const handleCutDaySelect = useCallback((dateIso: string | null) => {
      setViewMode("explorer");
      setCutDayFilter(dateIso);
    }, []);
    const [activeMockTc, setActiveMockTc] = useState("");
    const [previewClip, setPreviewClip] = useState<RelayPreviewClip | null>(null);
    const [mockPlaybackSpeed, setMockPlaybackSpeed] = useState(1);
    // Stream Program (video/PROGRAM MONITOR) accordion starts collapsed in CUT —
    // the timeline of cortes is the focus, the player is opt-in.
    const [isMockPlayerCollapsed, setIsMockPlayerCollapsed] = useState(true);
    const [showVtrShortcutsModal, setShowVtrShortcutsModal] = useState(false);
    // Items del corte EN VIVO emitidos por el CortesEditor (modo CUT), sin pasar por
    // "Guardar". Alimentan el player de cortes para previsualizar mientras se edita.
    // null = el editor aún no emitió; se cae a activeProject.items.
    const [cutLiveItems, setCutLiveItems] = useState<SelectableItem[] | null>(null);

    // ─── VTR sequential playout (Stream Program) ────────────────────────────────
    // El programa reproduce los cortes en orden, saltando entre los distintos
    // wall-clocks: cada corte es un clip HLS independiente del relay. Al llegar al
    // OUT (IN + duración) encadena el siguiente.
    const vtrDeckARef = useRef<RelayClipPlayerHandle>(null);
    const vtrDeckBRef = useRef<RelayClipPlayerHandle>(null);
    const [activeVtrDeck, setActiveVtrDeck] = useState<VtrDeckId>("a");
    const activeVtrDeckRef = useRef<VtrDeckId>(activeVtrDeck);
    activeVtrDeckRef.current = activeVtrDeck;
    const [vtrDeckClips, setVtrDeckClips] = useState<Record<VtrDeckId, RelayPreviewClip | null>>({
      a: null,
      b: null,
    });
    const vtrDeckClipsRef = useRef(vtrDeckClips);
    vtrDeckClipsRef.current = vtrDeckClips;
    const [vtrDeckLoading, setVtrDeckLoading] = useState<Record<VtrDeckId, boolean>>({
      a: false,
      b: false,
    });
    const vtrDeckLoadingRef = useRef(vtrDeckLoading);
    vtrDeckLoadingRef.current = vtrDeckLoading;
    const [vtrDeckErrors, setVtrDeckErrors] = useState<Record<VtrDeckId, string | null>>({
      a: null,
      b: null,
    });
    const vtrDeckErrorsRef = useRef(vtrDeckErrors);
    vtrDeckErrorsRef.current = vtrDeckErrors;
    // Intención del programa (lo que el usuario quiere): play vs pausa. No depende
    // del breve "pause" físico que ocurre al recargar el <video> entre cortes.
    const [isVtrPlaying, setIsVtrPlaying] = useState(false);
    const [activeVtrSegId, setActiveVtrSegId] = useState<string | null>(null);
    const isVtrPlayingRef = useRef(isVtrPlaying);
    isVtrPlayingRef.current = isVtrPlaying;
    const [vtrLoading, setVtrLoading] = useState(false);
    const [vtrError, setVtrError] = useState<string | null>(null);
    const mockPlaybackSpeedRef = useRef(mockPlaybackSpeed);
    mockPlaybackSpeedRef.current = mockPlaybackSpeed;

    const [scrubPct, setScrubPct] = useState(0);
    const scrubPctRef = useRef(scrubPct);
    scrubPctRef.current = scrubPct;
    const [isScrubbing, setIsScrubbing] = useState(false);
    const scrubberRef = useRef<HTMLDivElement>(null);
    const sidebarRef = useRef<HTMLDivElement>(null);
    const [timelineZoom, setTimelineZoom] = useState(1);
    const [hoveredClipIdx, setHoveredClipIdx] = useState<number | null>(null);
    const [tooltipPos, setTooltipPos] = useState<{ x: number; y: number }>({ x: 0, y: 0 });

    const getVtrDeckRef = useCallback(
      (deck: VtrDeckId) => (deck === "a" ? vtrDeckARef.current : vtrDeckBRef.current),
      [],
    );
    const getActiveVtrPlayer = useCallback(
      () => getVtrDeckRef(activeVtrDeckRef.current),
      [getVtrDeckRef],
    );

    useEffect(() => {
      setActiveMockTc("");
      setPreviewClip(null);
      setVtrDeckClips({ a: null, b: null });
      setVtrDeckLoading({ a: false, b: false });
      setVtrDeckErrors({ a: null, b: null });
      setActiveVtrDeck("a");
      setActiveVtrSegId(null);
      setScrubPct(0);
      setIsVtrPlaying(false);
      setVtrError(null);
      setCutLiveItems(null);
    }, [activeProject?.id]);

    const streamTagLabelsForItem = useCallback(
      (item: SelectableItem): string[] => {
        const labels: string[] = [];

        // `streams` y `relayChannel` vienen de la fuente original del corte.
        // Se priorizan para que la reproducción no herede la cámara de la historia.
        for (const stream of item.streams || []) {
          if (typeof stream === "string" && stream.trim()) labels.push(stream.trim());
        }
        if (item.relayChannel?.trim()) labels.push(item.relayChannel.trim());

        for (const cameraId of item.cameras || []) {
          const label = getCameraName(cameraId);
          if (label) labels.push(label);
        }

        for (const tagId of item.tags || []) {
          const tag = allTagsData.find((tt: any) => tt.id === tagId);
          const cat = tag ? categories.find((c: any) => c.id === tag.categoryId) : null;
          const categoryName = ((cat as any)?.name || "").toString();
          const rawLabel = getTagLabel(tagId);
          const looksLikeStream =
            /stream|ubicaci|location|espacio|lugar|foro|canal|c[áa]m(?:ara|era)|\bset\b/i.test(
              categoryName,
            ) ||
            /stream|canal|c[áa]m(?:ara|era)|video|secundari|principal/i.test(rawLabel || tagId);
          if (looksLikeStream && rawLabel) labels.push(rawLabel);
        }

        return Array.from(new Set(labels.map((label) => label.trim()).filter(Boolean)));
      },
      [allTagsData, categories, getCameraName, getTagLabel],
    );

    const getClipTitle = useCallback((item: SelectableItem, at: Date): string => {
      if (item.tc) return item.tc;
      if (item.tcIn && !item.tcIn.includes("T")) return item.tcIn;
      return at.toLocaleTimeString("en-GB", {
        timeZone: "America/Mexico_City",
        hour12: false,
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      });
    }, []);

    const openPreviewClip = useCallback(
      (item: SelectableItem) => {
        const at = getLogWallClock(item);
        if (!at || at.getFullYear() < 2000) {
          toast.error(
            t(
              "storyproducer.relayClip.noTimestamp",
              "Este corte no tiene una marca de tiempo absoluta para ubicar el video.",
            ),
            { position: "bottom-right" },
          );
          return;
        }

        const channel = resolveRelayChannel(streamTagLabelsForItem(item));
        const title = getClipTitle(item, at);
        const clip = { at, channel, title, itemId: item.id };
        setActiveMockTc(item.tcIn || item.tc || title);
        setActiveVtrSegId(item.id);
        setVtrDeckClips((prev) => ({ ...prev, [activeVtrDeckRef.current]: clip }));
        setPreviewClip(clip);

        setTimeout(() => {
          // Auto-scroll en modo CUT (CortesEditor)
          const editorEl = document.getElementById(`cortes-item-${item.id}`);
          if (editorEl) editorEl.scrollIntoView({ behavior: "smooth", block: "center" });

          // Auto-scroll en modo VTR (LogCard list)
          const vtrEl = document.getElementById(`vtr-item-${item.id}`);
          if (vtrEl) vtrEl.scrollIntoView({ behavior: "smooth", block: "center" });
        }, 50);
      },
      [getClipTitle, streamTagLabelsForItem, t],
    );

    // Segmentos reproducibles del programa: items con un wall-clock absoluto válido.
    // `itemIndex` referencia la posición en project.items (para la escala del scrubber,
    // que sí incluye TODOS los items).
    // Fuente de items del player de cortes. En modo CUT usa los items EN VIVO del
    // editor (previsualización sin guardar); si el editor aún no emitió, o en VTR,
    // cae a los items persistidos del proyecto. `itemIndex` de vtrSegments referencia
    // ESTE array, así que todo el playout (scrubber incluido) debe leer de acá.
    const playoutItems = useMemo(
      () => (!showOnlyApproved && cutLiveItems ? cutLiveItems : activeProject?.items || []),
      [showOnlyApproved, cutLiveItems, activeProject?.items],
    );

    const vtrSegments = useMemo(() => {
      const items = playoutItems;
      return items
        .map((item, itemIndex) => {
          const at = getLogWallClock(item);
          if (!at || at.getFullYear() < 2000) return null;
          return {
            itemIndex,
            itemId: item.id,
            at,
            channel: resolveRelayChannel(streamTagLabelsForItem(item)),
            durationMs: getScrubberDurationMs(item),
            title: getClipTitle(item, at),
          };
        })
        .filter(Boolean) as {
        itemIndex: number;
        itemId: string;
        at: Date;
        channel: string;
        durationMs: number;
        title: string;
      }[];
    }, [playoutItems, streamTagLabelsForItem, getClipTitle]);

    const currentSegIdx = useMemo(
      () => vtrSegments.findIndex((s) => s.itemId === (activeVtrSegId ?? previewClip?.itemId)),
      [vtrSegments, activeVtrSegId, previewClip?.itemId],
    );
    const currentSeg = currentSegIdx >= 0 ? vtrSegments[currentSegIdx] : null;

    const buildVtrClip = useCallback(
      (segIdx: number): RelayPreviewClip | null => {
        const seg = vtrSegments[segIdx];
        return seg
          ? { at: seg.at, channel: seg.channel, title: seg.title, itemId: seg.itemId }
          : null;
      },
      [vtrSegments],
    );

    const preloadVtrSegment = useCallback(
      (segIdx: number, deck: VtrDeckId) => {
        const clip = buildVtrClip(segIdx);
        setVtrDeckClips((prev) => {
          if (!clip || prev[deck]?.itemId === clip.itemId) return prev;
          return { ...prev, [deck]: clip };
        });
      },
      [buildVtrClip],
    );

    // Activa el segmento `segIdx`. El deck oculto intenta traer el siguiente corte
    // por adelantado; al cambio, si ya está listo, solo se alterna el video visible.
    const playVtrSegment = useCallback(
      (segIdx: number, wallMs?: number) => {
        const seg = vtrSegments[segIdx];
        if (!seg) return;
        const item = playoutItems[seg.itemIndex];
        const clip = buildVtrClip(segIdx);
        if (!clip) return;
        setVtrError(null);
        setActiveMockTc(item?.tcIn || item?.tc || seg.title);

        const activeDeck = activeVtrDeckRef.current;
        const inactiveDeck = activeDeck === "a" ? "b" : "a";
        const activeLoadedClip = vtrDeckClipsRef.current[activeDeck];
        const inactiveLoadedClip = vtrDeckClipsRef.current[inactiveDeck];
        const inactiveReady =
          inactiveLoadedClip?.itemId === seg.itemId &&
          !vtrDeckLoadingRef.current[inactiveDeck] &&
          !vtrDeckErrorsRef.current[inactiveDeck];
        const segAtMs = seg.at.getTime();
        const targetWallMs = wallMs ?? segAtMs;
        const targetClip =
          targetWallMs === segAtMs ? clip : { ...clip, at: new Date(targetWallMs) };
        const loadedAtMs = activeLoadedClip?.at.getTime() ?? null;
        const isSameLoadedWindow =
          activeLoadedClip?.channel === seg.channel &&
          loadedAtMs != null &&
          segAtMs >= loadedAtMs - CLIP_BEFORE_SEC * 1000;

        if (inactiveReady) {
          getVtrDeckRef(activeDeck)?.pause();
          setActiveVtrDeck(inactiveDeck);
          setActiveVtrSegId(seg.itemId);
          setPreviewClip(clip);
          setVtrError(null);
          setVtrLoading(false);
          const nextPlayer = getVtrDeckRef(inactiveDeck);
          nextPlayer?.setRate(mockPlaybackSpeedRef.current);
          nextPlayer?.seekToWallClock(targetWallMs);
          if (isVtrPlayingRef.current) nextPlayer?.play();
          preloadVtrSegment(segIdx + 1, activeDeck);
          return;
        }

        const activePlayer = getVtrDeckRef(activeDeck);
        if (isSameLoadedWindow && activePlayer?.seekToWallClock(targetWallMs)) {
          setActiveVtrSegId(seg.itemId);
          setPreviewClip(clip);
          activePlayer.setRate(mockPlaybackSpeedRef.current);
          if (isVtrPlayingRef.current) activePlayer.play();
          preloadVtrSegment(segIdx + 1, inactiveDeck);
          return;
        }

        setActiveVtrSegId(seg.itemId);
        setPreviewClip(targetClip);
        setVtrDeckClips((prev) => ({ ...prev, [activeDeck]: targetClip }));
        preloadVtrSegment(segIdx + 1, inactiveDeck);
      },
      [vtrSegments, playoutItems, buildVtrClip, getVtrDeckRef, preloadVtrSegment],
    );

    // Encadena al siguiente corte cuando el actual alcanza su OUT. Al final, frena.
    const advanceVtr = useCallback(() => {
      const next = currentSegIdx + 1;
      if (next < vtrSegments.length) {
        playVtrSegment(next);
      } else {
        getActiveVtrPlayer()?.pause();
        setIsVtrPlaying(false);
      }
    }, [currentSegIdx, vtrSegments.length, playVtrSegment, getActiveVtrPlayer]);

    const startVtrPlayback = useCallback(
      (segIdx: number) => {
        setIsVtrPlaying(true);
        if (isVtrPlayingRef) isVtrPlayingRef.current = true;
        if (onVtrPlayStarted) onVtrPlayStarted();

        // Llamamos play() sincrónicamente durante el evento click (user gesture)
        // para desbloquear el autoplay en Safari/Chrome, aunque luego playVtrSegment cargue otro source.
        vtrDeckARef.current?.play();
        vtrDeckBRef.current?.play();

        playVtrSegment(segIdx);
      },
      [playVtrSegment, onVtrPlayStarted],
    );

    const handleCortesEditorTimecodeClick = useCallback(
      (item: any) => {
        const targetId = item.sourceId || item.id;
        const index = playoutItems.findIndex(
          (i) => i.id === targetId || (i as any).sourceId === targetId,
        );

        if (index >= 0) {
          const segIdx = vtrSegments.findIndex((seg) => seg.itemIndex === index);
          if (segIdx >= 0) {
            // Seek sin play: cargar el clip en el deck y posicionar, pero NO reproducir
            playVtrSegment(segIdx);
            // Actualizar scrubPct para reflejar la posición visual
            const items = playoutItems;
            const totalMs = items.reduce((acc, it) => acc + getScrubberDurationMs(it), 0) || 1;
            let accMs = 0;
            for (let j = 0; j < index; j++) accMs += getScrubberDurationMs(items[j]);
            const pct = (accMs / totalMs) * 100;
            scrubPctRef.current = pct;
            setScrubPct(pct);
          } else {
            openPreviewClip(playoutItems[index]);
          }
        } else {
          openPreviewClip(item);
        }
      },
      [playoutItems, vtrSegments, playVtrSegment, openPreviewClip],
    );

    // Play/Pausa del programa. Sin segmento cargado arranca por el primero.
    const toggleVtrPlay = useCallback(() => {
      if (currentSegIdx < 0) {
        if (vtrSegments.length) {
          startVtrPlayback(0);
        }
        return;
      }
      if (isVtrPlaying) {
        getActiveVtrPlayer()?.pause();
        setIsVtrPlaying(false);
      } else {
        getActiveVtrPlayer()?.play();
        setIsVtrPlaying(true);
        if (onVtrPlayStarted) onVtrPlayStarted();
      }
    }, [
      currentSegIdx,
      vtrSegments.length,
      isVtrPlaying,
      startVtrPlayback,
      getActiveVtrPlayer,
      onVtrPlayStarted,
    ]);

    const handlePrevClip = useCallback(() => {
      if (!playoutItems.length) return;
      const currentId = activeVtrSegId ?? previewClip?.itemId;
      const idx = playoutItems.findIndex((item) => item.id === currentId);
      const targetIdx = idx > 0 ? idx - 1 : 0;
      const item = playoutItems[targetIdx];
      setActiveMockTc(item.tcIn || item.tc || "");

      const totalMs = playoutItems.reduce((acc, it) => acc + getScrubberDurationMs(it), 0) || 1;
      let accMs = 0;
      for (let j = 0; j < targetIdx; j++) accMs += getScrubberDurationMs(playoutItems[j]);
      setScrubPct((accMs / totalMs) * 100);

      const segIdx = vtrSegments.findIndex((seg) => seg.itemIndex === targetIdx);
      if (isVtrPlayingRef.current) {
        // Si está en play → saltar al clip y reanudar reproducción
        if (segIdx >= 0) {
          startVtrPlayback(segIdx);
        } else {
          openPreviewClip(item);
        }
      } else {
        // Si está pausado → cargar el clip sin reproducir
        if (segIdx >= 0) {
          playVtrSegment(segIdx);
        } else {
          openPreviewClip(item);
        }
        // Asegurar que queda pausado
        setTimeout(() => {
          getActiveVtrPlayer()?.pause();
          setIsVtrPlaying(false);
          if (isVtrPlayingRef) isVtrPlayingRef.current = false;
        }, 80);
      }
    }, [
      playoutItems,
      activeVtrSegId,
      previewClip?.itemId,
      openPreviewClip,
      vtrSegments,
      startVtrPlayback,
      playVtrSegment,
      getActiveVtrPlayer,
    ]);

    const handleNextClip = useCallback(() => {
      if (!playoutItems.length) return;
      const currentId = activeVtrSegId ?? previewClip?.itemId;
      const idx = playoutItems.findIndex((item) => item.id === currentId);
      if (idx < playoutItems.length - 1) {
        const targetIdx = idx + 1;
        const item = playoutItems[targetIdx];
        setActiveMockTc(item.tcIn || item.tc || "");

        const totalMs = playoutItems.reduce((acc, it) => acc + getScrubberDurationMs(it), 0) || 1;
        let accMs = 0;
        for (let j = 0; j < targetIdx; j++) accMs += getScrubberDurationMs(playoutItems[j]);
        setScrubPct((accMs / totalMs) * 100);

        const segIdx = vtrSegments.findIndex((seg) => seg.itemIndex === targetIdx);
        if (isVtrPlayingRef.current) {
          if (segIdx >= 0) {
            startVtrPlayback(segIdx);
          } else {
            openPreviewClip(item);
          }
        } else {
          if (segIdx >= 0) {
            playVtrSegment(segIdx);
          } else {
            openPreviewClip(item);
          }
          setTimeout(() => {
            getActiveVtrPlayer()?.pause();
            setIsVtrPlaying(false);
            if (isVtrPlayingRef) isVtrPlayingRef.current = false;
          }, 80);
        }
      }
    }, [
      playoutItems,
      activeVtrSegId,
      previewClip?.itemId,
      openPreviewClip,
      vtrSegments,
      startVtrPlayback,
      playVtrSegment,
      getActiveVtrPlayer,
    ]);

    const handleStopVtr = useCallback(() => {
      if (!playoutItems.length) return;
      const item = playoutItems[0];
      setActiveMockTc(item.tcIn || item.tc || "");
      openPreviewClip(item);
      setScrubPct(0);
      setIsVtrPlaying(false);
      if (isVtrPlayingRef) isVtrPlayingRef.current = false;
      getActiveVtrPlayer()?.pause();
    }, [playoutItems, openPreviewClip, getActiveVtrPlayer]);

    const seekVtrToScrubPct = useCallback(
      (pct: number) => {
        const items = playoutItems;
        if (!items.length) return;

        const clampedPct = Math.max(0, Math.min(pct, 100));
        const total = items.reduce((acc, it) => acc + getScrubberDurationMs(it), 0) || 1;
        const targetTimelineMs = (clampedPct / 100) * total;

        let before = 0;
        let itemIndex = items.length - 1;
        for (let i = 0; i < items.length; i++) {
          const duration = getScrubberDurationMs(items[i]);
          if (targetTimelineMs < before + duration || i === items.length - 1) {
            itemIndex = i;
            break;
          }
          before += duration;
        }

        const segIdx = vtrSegments.findIndex((seg) => seg.itemIndex === itemIndex);
        if (segIdx < 0) {
          setScrubPct(clampedPct);
          return;
        }

        const segmentDuration = Math.max(getScrubberDurationMs(items[itemIndex]), 1);
        const segmentFrac = Math.max(0, Math.min((targetTimelineMs - before) / segmentDuration, 1));
        const targetWallMs = vtrSegments[segIdx].at.getTime() + segmentFrac * PROGRAM_PREVIEW_MS;

        setScrubPct(clampedPct);
        playVtrSegment(segIdx, targetWallMs);
      },
      [playoutItems, vtrSegments, playVtrSegment],
    );

    useImperativeHandle(
      ref,
      () => ({
        toggleVtrPlay: () => {
          if (!isVtrPlayingRef.current && onVtrPlayStarted) {
            onVtrPlayStarted();
          }
          toggleVtrPlay();
        },
        handlePrevClip,
        handleNextClip,
        handleStopVtr,
        seekVtrBy: (secs: number) => {
          const items = playoutItems;
          if (!items.length) return;
          const totalMs = items.reduce((acc, it) => acc + getScrubberDurationMs(it), 0) || 1;
          const currentMs = (scrubPctRef.current / 100) * totalMs;
          const targetMs = Math.max(0, Math.min(currentMs + secs * 1000, totalMs));
          seekVtrToScrubPct((targetMs / totalMs) * 100);
        },
        pauseVtr: () => {
          getActiveVtrPlayer()?.pause();
          setIsVtrPlaying(false);
          if (isVtrPlayingRef) isVtrPlayingRef.current = false;
        },
        // Seek directo en el video sin pasar por la máquina de estados VTR
        seekVtrDirect: (secs: number) => {
          getActiveVtrPlayer()?.seekBy(secs);
        },
        // Audio scrubbing frame-a-frame: usa seekBy del deck (igual que el library
        // player que SÍ funciona) y mantiene scrubPctRef sincronizado.
        scrubVtrAudio: (direction: number) => {
          const player = getActiveVtrPlayer();
          if (!player) return;

          // 1. Frame seek + audio scrub nativo (play→pause integrado en seekBy)
          player.seekBy(direction / 29.97);

          // 2. Mantener scrubPctRef sincronizado para que la siguiente
          //    pulsación de flecha calcule desde la posición correcta
          const items = playoutItems;
          if (!items.length) return;
          const totalMs = items.reduce((acc, it) => acc + getScrubberDurationMs(it), 0) || 1;
          const frameDeltaPct = (1000 / 29.97 / totalMs) * 100;
          const newPct = Math.max(
            0,
            Math.min(100, scrubPctRef.current + direction * frameDeltaPct),
          );
          scrubPctRef.current = newPct;
          setScrubPct(newPct);
        },
        setVtrSpeed: (rate: number) => {
          setMockPlaybackSpeed(rate);
          getActiveVtrPlayer()?.setRate(rate);
        },
        isVtrPlaying,
      }),
      [
        toggleVtrPlay,
        handlePrevClip,
        handleNextClip,
        handleStopVtr,
        playoutItems,
        seekVtrToScrubPct,
        getActiveVtrPlayer,
        isVtrPlaying,
        onVtrPlayStarted,
        setMockPlaybackSpeed,
      ],
    );

    // Loop de reproducción del programa. Mientras está en play, leemos por rAF la
    // posición real del <video> (getWallClockMs) — más fiable y suave que el evento
    // timeupdate. Con eso (1) movemos el playhead del scrubber en sync con el video y
    // (2) detectamos el OUT del corte para encadenar el siguiente. La posición global
    // del playhead = suma de duraciones de los items anteriores + avance en el corte.
    useEffect(() => {
      if (!isVtrPlaying || !currentSeg || isScrubbing) return;
      const items = playoutItems;
      if (!items?.length) return;
      let before = 0;
      for (let i = 0; i < currentSeg.itemIndex; i++) before += getScrubberDurationMs(items[i]);
      const total = items.reduce((acc, it) => acc + getScrubberDurationMs(it), 0) || 1;
      const inMs = currentSeg.at.getTime();
      // El programa toca solo una ventana corta de cada corte (preview/montaje), no su
      // duración real (que puede ser enorme y dejar el OUT inalcanzable). El OUT del
      // preview se alcanza tras PROGRAM_PREVIEW_MS de reproducción desde el IN.
      const previewMs = PROGRAM_PREVIEW_MS;
      const outMs = inMs + previewMs;
      // Ancho de este corte en la línea de tiempo (proporcional a su duración real).
      // El playhead barre esa franja completa durante el preview, así queda en el inicio
      // del siguiente corte justo al encadenar.
      const segWidthMs = currentSeg.durationMs;

      let raf = 0;
      let advanced = false;
      const tick = () => {
        const ms = getActiveVtrPlayer()?.getWallClockMs();
        if (ms != null) {
          const frac = Math.max(0, Math.min((ms - inMs) / previewMs, 1));
          setScrubPct(((before + frac * segWidthMs) / total) * 100);
          if (ms >= outMs && !advanced) {
            // Fin del preview → encadenar el siguiente corte (una sola vez).
            advanced = true;
            advanceVtr();
            return; // este corte terminó; el effect se reinicia para el siguiente.
          }
        }
        raf = requestAnimationFrame(tick);
      };
      raf = requestAnimationFrame(tick);
      return () => cancelAnimationFrame(raf);
    }, [isVtrPlaying, currentSeg, playoutItems, advanceVtr, getActiveVtrPlayer, isScrubbing]);

    useEffect(() => {
      if (!isScrubbing) return;
      const handleMouseMove = (e: MouseEvent) => {
        if (scrubberRef.current) {
          const rect = scrubberRef.current.getBoundingClientRect();
          let pct = ((e.clientX - rect.left) / rect.width) * 100;
          if (pct < 0) pct = 0;
          if (pct > 100) pct = 100;
          setScrubPct(pct);
          scrubPctRef.current = pct;
        }
      };
      const handleMouseUp = () => {
        setIsScrubbing(false);
        seekVtrToScrubPct(scrubPctRef.current);
      };
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
      return () => {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
      };
    }, [isScrubbing, seekVtrToScrubPct]);

    // ─── VTR Accordion Drop Handlers ────────────────────────────────────────────
    /**
     * Adds items to a VTR (approved) project. VTRs may be owned by other users,
     * so they don't live in the shared `assemblyProjects` context — they're in
     * the local `approvedProjects` state fetched via ?approvedOnly=true.
     * Context's `addItemsToProject` silently drops the update in that case
     * (its `prev.map` never matches). We replicate the merge here and PUT
     * directly via `updateProject` (which always syncs).
     */
    const addItemsToApprovedProject = (projectId: string, incoming: SelectableItem[]) => {
      const target = approvedProjects.find((p) => p.id === projectId);
      if (!target) return { added: 0, skipped: incoming.length };
      const newItems = incoming.map((i) => {
        const isVtrOrEvent = ["rundown", "event", "vtr"].includes(i.type);
        return {
          ...i,
          id: crypto.randomUUID(),
          sourceId: i.sourceId || i.id,
          storyId: i.type === "story" ? i.storyId || i.id : i.storyId,
          type: isVtrOrEvent ? "story" : i.type,
        };
      });
      const result = { added: newItems.length, skipped: 0 };
      if (newItems.length === 0) return result;
      const mergedItems = [...target.items, ...newItems];
      // Optimistic local update
      setApprovedProjects((prev) =>
        prev.map((p) => (p.id === projectId ? { ...p, items: mergedItems } : p)),
      );
      // Sync to backend (updateProject always issues PUT, even when project
      // isn't in assemblyProjects context state)
      updateProject(projectId, { items: mergedItems });
      return result;
    };

    const addItemsToTarget = (
      projectId: string,
      items: SelectableItem[],
      insertAtIndex?: number,
    ) => {
      const result = showOnlyApproved
        ? addItemsToApprovedProject(projectId, items)
        : addItemsToProject(projectId, items, insertAtIndex);
      notifyAddResult(result);
      return result;
    };

    const notifyAddResult = (result: { added: number; skipped: number }) => {
      if (result.skipped > 0 && result.added === 0) {
        toast.info(
          result.skipped === 1
            ? "Esa tarjeta ya está en el corte"
            : `${result.skipped} tarjetas ya estaban en el corte`,
        );
      } else if (result.skipped > 0) {
        toast.info(
          `${result.added} agregada${result.added === 1 ? "" : "s"} · ${result.skipped} ya estaba${result.skipped === 1 ? "" : "n"} en el corte`,
        );
      }
    };

    const handleVtrDrop = (e: React.DragEvent, projectId: string) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDraggingOverVtrId(null);
      // Also clear the outer panel's drag-over flag — handleDrop won't fire
      // because we stopPropagation, so we have to clear it ourselves.
      setIsDraggingOver(false);
      // Skip if this is an internal item reorder drag
      if (draggedItemIndex !== null) return;
      try {
        const multiData = e.dataTransfer.getData("application/x-braindog-multi");
        if (multiData) {
          const items: SelectableItem[] = JSON.parse(multiData);
          addItemsToTarget(projectId, items);
          return;
        }
        const data = e.dataTransfer.getData("application/json");
        if (data) {
          const item: SelectableItem = JSON.parse(data);
          addItemsToTarget(projectId, [item]);
        }
      } catch (err) {
        console.error("VTR accordion drop error:", err);
      }
    };

    const handleVtrDragOver = (e: React.DragEvent, projectId: string) => {
      // Only accept external library drops, not internal item reorders
      if (draggedItemIndex !== null) return;
      e.preventDefault();
      e.dataTransfer.dropEffect = "copy";
      setIsDraggingOverVtrId(projectId);
    };

    const handleVtrDragLeave = (e: React.DragEvent) => {
      if (!e.currentTarget.contains(e.relatedTarget as Node)) {
        setIsDraggingOverVtrId(null);
      }
    };
    // ─────────────────────────────────────────────────────────────────────────────

    // Handle drop from Discovery or Explore panels (External Drop)
    // Clean up VTR drag-over highlight if the drag ends anywhere (prevents stuck state)
    useEffect(() => {
      const cleanup = () => {
        setIsDraggingOverVtrId(null);
        setIsDraggingOver(false);
      };
      document.addEventListener("dragend", cleanup);
      document.addEventListener("drop", cleanup);
      return () => {
        document.removeEventListener("dragend", cleanup);
        document.removeEventListener("drop", cleanup);
      };
    }, []);

    const handleDrop = (e: React.DragEvent) => {
      e.preventDefault();
      const dropIdx = externalDropIndex;
      setIsDraggingOver(false);
      setIsDraggingOverVtrId(null);
      setExternalDropIndex(null);
      if (draggedItemIndex !== null) return;
      // In VTR mode, prefer the currently expanded row over a stale shared
      // activeProjectId (which may point to a CUT project owned by the user).
      const targetProjectId = showOnlyApproved
        ? expandedVtrId || activeProjectId
        : activeProjectId || expandedVtrId;
      if (!targetProjectId) return;
      try {
        const multiData = e.dataTransfer.getData("application/x-braindog-multi");
        if (multiData) {
          const items: SelectableItem[] = JSON.parse(multiData);
          addItemsToTarget(targetProjectId, items, dropIdx ?? undefined);
          return;
        }
        const data = e.dataTransfer.getData("application/json");
        if (data) {
          const item: SelectableItem = JSON.parse(data);
          addItemsToTarget(targetProjectId, [item], dropIdx ?? undefined);
        }
      } catch (err) {
        console.error("Drop error:", err);
      }
    };

    const handleDragOver = (e: React.DragEvent) => {
      // Internal reorder drags should not trigger the external-drop highlight
      if (draggedItemIndex !== null) return;
      e.preventDefault();
      e.dataTransfer.dropEffect = "copy";
      setIsDraggingOver(true);
    };

    const handleDragLeave = (e: React.DragEvent) => {
      if (!e.currentTarget.contains(e.relatedTarget as Node)) {
        setIsDraggingOver(false);
      }
    };

    // Internal Reorder Handlers
    const handleItemDragStart = (e: React.DragEvent, index: number) => {
      setDraggedItemIndex(index);
      e.dataTransfer.effectAllowed = "move";
      e.dataTransfer.setData("text/plain", index.toString());
      if (e.dataTransfer.setDragImage) {
        const target = e.currentTarget as HTMLElement;
        e.dataTransfer.setDragImage(target, 0, 0);
      }
    };

    const handleItemDragEnd = () => {
      setDraggedItemIndex(null);
      setDragOverIndex(null);
      setExternalDropIndex(null);
      setIsDraggingOver(false);
    };

    const handleItemDragOver = (e: React.DragEvent, index: number) => {
      if (draggedItemIndex === null) return; // Allow external drops to bubble to parent
      e.preventDefault();
      e.stopPropagation();
      e.dataTransfer.dropEffect = "move";
      if (draggedItemIndex !== index) {
        setDragOverIndex(index);
      }
    };

    const handleItemDragLeave = (e: React.DragEvent) => {
      if (draggedItemIndex === null) return; // Allow external drops to bubble to parent
      e.preventDefault();
      if (!e.currentTarget.contains(e.relatedTarget as Node)) {
        setDragOverIndex(null);
      }
    };

    const handleItemDrop = (e: React.DragEvent, targetIndex: number) => {
      e.preventDefault();
      e.stopPropagation();
      if (activeProjectId && draggedItemIndex !== null && draggedItemIndex !== targetIndex) {
        moveItemInProject(activeProjectId, draggedItemIndex, targetIndex);
      }
      setDraggedItemIndex(null);
      setDragOverIndex(null);
      setIsDraggingOver(false);
    };

    // ─── Shared items list renderer (CUT panel + VTR accordion) ───────────────────────
    // NOTE: invoked as a plain function (`renderProjectItems(...)`), not as a JSX
    // component, so React doesn't remount the LogCards on every parent render —
    // which would otherwise abort in-flight drags.
    const renderProjectItems = ({
      projectId,
      items,
    }: {
      projectId: string;
      items: SelectableItem[];
    }) => {
      if (items.length === 0) {
        return (
          <div
            style={{
              color: "#555",
              textAlign: "center",
              padding: "20px",
              fontSize: "0.82rem",
              border: "1px dashed rgba(255,255,255,0.08)",
              borderRadius: "6px",
            }}
          >
            <i
              className="bx bx-down-arrow-circle"
              style={{ fontSize: "1.5rem", display: "block", marginBottom: "6px", opacity: 0.4 }}
            />
            Arrastra cortes desde la biblioteca para agregarlos
          </div>
        );
      }
      // Helper: compute insert index from cursor Y relative to a card
      const computeInsertIdx = (e: React.DragEvent, cardIndex: number) => {
        const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
        const midY = rect.top + rect.height / 2;
        return e.clientY < midY ? cardIndex : cardIndex + 1;
      };

      // Helper: handle external drag over a card (detect top/bottom half)
      const handleExternalCardDragOver = (e: React.DragEvent, cardIndex: number) => {
        if (draggedItemIndex !== null) return; // internal reorder — let it handle itself
        e.preventDefault();
        e.dataTransfer.dropEffect = "copy";
        const idx = computeInsertIdx(e, cardIndex);
        setExternalDropIndex(idx);

        // Auto-scroll: find scrollable parent
        const scrollParent =
          (e.currentTarget as HTMLElement).closest('[style*="overflow"]') ||
          (e.currentTarget as HTMLElement).closest(".assembly-items-scroll");
        if (scrollParent) {
          const sr = scrollParent.getBoundingClientRect();
          const edgeZone = 60; // px from edge to trigger scroll
          const speed = 8;
          if (e.clientY < sr.top + edgeZone) {
            scrollParent.scrollTop -= speed;
          } else if (e.clientY > sr.bottom - edgeZone) {
            scrollParent.scrollTop += speed;
          }
        }
      };

      // Helper: handle drop on a card (insert at computed position)
      const handleExternalCardDrop = (e: React.DragEvent, cardIndex: number) => {
        if (draggedItemIndex !== null) return; // internal reorder — skip
        e.preventDefault();
        e.stopPropagation();
        const idx = computeInsertIdx(e, cardIndex);
        setExternalDropIndex(null);
        setIsDraggingOver(false);
        try {
          const multiData = e.dataTransfer.getData("application/x-braindog-multi");
          if (multiData) {
            const dropItems: SelectableItem[] = JSON.parse(multiData);
            addItemsToTarget(projectId, dropItems, idx);
            return;
          }
          const data = e.dataTransfer.getData("application/json");
          if (data) {
            const dropItem: SelectableItem = JSON.parse(data);
            addItemsToTarget(projectId, [dropItem], idx);
          }
        } catch (err) {
          console.error("Drop-on-card error:", err);
        }
      };

      // Render a glowing drop indicator line
      const dropLine = (
        <div
          style={{
            height: "3px",
            margin: "1px 0",
            borderRadius: "2px",
            background: "var(--color-role-sp)",
            boxShadow: "0 0 8px var(--color-role-sp), 0 0 16px var(--color-role-sp)",
            transition: "opacity 0.1s ease",
          }}
        />
      );

      return (
        <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
          {items.map((item, index) => (
            <React.Fragment key={item.id}>
              {/* Show drop indicator BEFORE this card */}
              {externalDropIndex === index && draggedItemIndex === null && dropLine}
              <div
                id={`vtr-item-${item.id}`}
                onClick={(e) => {
                  const target = e.target as HTMLElement;
                  if (
                    target.closest(".bx-trash") ||
                    target.closest('[title="Mostrar en biblioteca"]') ||
                    target.closest('[title="Ver Metadatos"]') ||
                    target.closest('[title="Ocultar Metadatos"]')
                  ) {
                    return;
                  }
                  setActiveMockTc(item.tcIn || item.tc || "");

                  const segIdx = vtrSegments.findIndex((seg) => seg.itemIndex === index);
                  if (segIdx >= 0) {
                    startVtrPlayback(segIdx);
                  } else {
                    openPreviewClip(item);
                  }

                  let accMs = 0;
                  for (let i = 0; i < index; i++) accMs += getScrubberDurationMs(items[i]);
                  const totalMs =
                    items.reduce((acc, it) => acc + getScrubberDurationMs(it), 0) || 1;
                  setScrubPct((accMs / totalMs) * 100);
                }}
                style={{
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                  borderRadius: "8px",
                  background:
                    (item.tcIn || item.tc) === activeMockTc
                      ? "rgba(255, 220, 0, 0.03)"
                      : "transparent",
                  padding: (item.tcIn || item.tc) === activeMockTc ? "4px" : "0",
                  transform: (item.tcIn || item.tc) === activeMockTc ? "scale(1.01)" : "scale(1)",
                }}
                onDragOver={(e) => handleExternalCardDragOver(e, index)}
                onDragLeave={(e) => {
                  if (!e.currentTarget.contains(e.relatedTarget as Node)) {
                    setExternalDropIndex((prev) => {
                      if (prev === index || prev === index + 1) return null;
                      return prev;
                    });
                  }
                }}
                onDrop={(e) => handleExternalCardDrop(e, index)}
              >
                <LogCard
                  log={{ ...item, text: item.title }}
                  isHighlighted={(item.tcIn || item.tc) === activeMockTc}
                  participants={participants}
                  accentColor="var(--color-role-sp)"
                  getCameraName={getCameraName}
                  getTagColor={getTagColor}
                  getTagLabel={getTagLabel}
                  isAssembly={true}
                  index={index}
                  showDragHandle={!readOnly}
                  isDragOver={readOnly ? false : dragOverIndex === index}
                  isDragging={readOnly ? false : draggedItemIndex === index}
                  onTimecodeClick={(item) => {
                    const segIdx = vtrSegments.findIndex((seg) => seg.itemIndex === index);
                    if (segIdx >= 0) {
                      startVtrPlayback(segIdx);
                    } else {
                      openPreviewClip(item);
                    }
                  }}
                  onDragStart={readOnly ? undefined : (e) => handleItemDragStart(e, index)}
                  onDragEnd={readOnly ? undefined : handleItemDragEnd}
                  onDragOver={readOnly ? undefined : (e) => handleItemDragOver(e, index)}
                  onDragLeave={readOnly ? undefined : handleItemDragLeave}
                  onDrop={
                    readOnly
                      ? undefined
                      : (e) => {
                          if (draggedItemIndex === null) return; // Allow external drops to bubble up
                          e.preventDefault();
                          e.stopPropagation();
                          if (draggedItemIndex !== index) {
                            moveItemInProject(projectId, draggedItemIndex, index);
                          }
                          setDraggedItemIndex(null);
                          setDragOverIndex(null);
                        }
                  }
                  onDelete={
                    readOnly
                      ? undefined
                      : (id) => {
                          if (id) removeItemFromProject(projectId, id);
                        }
                  }
                  onUpdateItem={
                    readOnly
                      ? undefined
                      : (id, updates) => {
                          if (id) updateItemInProject(projectId, id, updates);
                        }
                  }
                  onNavigateSource={(sourceId, itemId, itemType) =>
                    setNavigateToSource({
                      storyId: sourceId,
                      itemId,
                      itemType: itemType as "log" | "transcript",
                    })
                  }
                  draggable={!readOnly}
                  selectable={false}
                />
              </div>
              {/* Show drop indicator AFTER the last card */}
              {index === items.length - 1 &&
                externalDropIndex === items.length &&
                draggedItemIndex === null &&
                dropLine}
            </React.Fragment>
          ))}
        </div>
      );
    };
    // ─────────────────────────────────────────────────────────────────────────────

    const handleCreateProject = (folderId?: string | null) => {
      const name = folderId ? newProjectName : newProjectName;
      if (name.trim()) {
        const proj = createProject(name.trim(), folderId);
        setActiveProject(proj.id);
        setViewMode("editor");
        setNewProjectName("");
        setShowNewInput(false);
        setCreatingInFolderId(null);
      }
    };

    // Create a blank VTR directly from the VTR view. `createProject` inserts an
    // unapproved project (POST → socket "assembly-projects-updated" → the VTR
    // list refetches). We also add it optimistically to `approvedProjects` so it
    // appears instantly, then select + expand it so the user can rename it inline
    // and fill in its description.
    const handleCreateVtr = () => {
      const proj = createProject("");
      setApprovedProjects((prev) => [proj, ...prev]);
      setActiveProject(proj.id);
      setExpandedVtrId(proj.id);
      toast.success(t("storyproducer.assembly.vtrCreated", "VTR creado"), {
        position: "bottom-right",
      });
    };

    // Create a new CUT directly from the main header (editor mode) instead of
    // forcing the user into the explorer first. Mirrors the VTR flow: creates
    // the project (in the active folder, if any) and opens it in the editor.
    const handleCreateCut = () => {
      const proj = createProject("", activeFolderId);
      setActiveProject(proj.id);
      setViewMode("editor");
    };

    const handleCreateFolder = (parentId?: string | null) => {
      if (newFolderName.trim()) {
        createFolder(newFolderName.trim(), parentId);
        setNewFolderName("");
        setShowNewFolderInput(false);
        setCreatingFolderInId(null);
      }
    };

    const handleStartEdit = (project: AssemblyProject) => {
      setEditingId(project.id);
      setEditName(project.name);
    };

    const handleSaveEdit = () => {
      if (editingId && editName.trim()) {
        handleProjectUpdate(editingId, { name: editName.trim() });
      }
      setEditingId(null);
      setEditName("");
    };

    const handleStartEditFolder = (folder: AssemblyFolder) => {
      setEditingFolderId(folder.id);
      setEditFolderName(folder.name);
    };

    const handleSaveEditFolder = () => {
      if (editingFolderId && editFolderName.trim()) {
        renameFolder(editingFolderId, editFolderName.trim());
      }
      setEditingFolderId(null);
      setEditFolderName("");
    };

    const handleAddSelectedToProject = () => {
      if (activeProjectId && selectedItems.length > 0) {
        addItemsToTarget(activeProjectId, selectedItems);
        clearSelection();
      }
    };

    // Project and Folder drag & drop
    const [draggedFolderId, setDraggedFolderId] = useState<string | null>(null);

    const handleProjectDragStart = (e: React.DragEvent, projectId: string) => {
      e.stopPropagation();
      setDraggedProjectId(projectId);
      e.dataTransfer.effectAllowed = "move";
      e.dataTransfer.setData("text/project-id", projectId);
    };

    const handleProjectDragEnd = () => {
      setDraggedProjectId(null);
      setFolderDropTargetId(null);
      setIsDraggingOver(false);
    };

    const handleFolderDragStart = (e: React.DragEvent, folderId: string) => {
      e.stopPropagation();
      setDraggedFolderId(folderId);
      e.dataTransfer.effectAllowed = "move";
      e.dataTransfer.setData("text/folder-id", folderId);
    };

    const handleFolderDragEnd = () => {
      setDraggedFolderId(null);
      setFolderDropTargetId(null);
      setIsDraggingOver(false);
    };

    const handleFolderDragOver = (e: React.DragEvent, targetFolderId: string) => {
      if (!draggedProjectId && !draggedFolderId) return;
      if (draggedFolderId === targetFolderId) return; // Can't drop on itself
      e.preventDefault();
      e.stopPropagation();
      e.dataTransfer.dropEffect = "move";
      setFolderDropTargetId(targetFolderId);
    };

    const handleFolderDragLeave = (e: React.DragEvent) => {
      if (!e.currentTarget.contains(e.relatedTarget as Node)) {
        setFolderDropTargetId(null);
      }
    };

    const handleFolderDrop = (e: React.DragEvent, targetFolderId: string | null) => {
      e.preventDefault();
      e.stopPropagation();
      if (draggedProjectId) {
        moveProjectToFolder(draggedProjectId, targetFolderId);
        setDraggedProjectId(null);
      } else if (draggedFolderId) {
        moveFolderToFolder(draggedFolderId, targetFolderId);
        setDraggedFolderId(null);
      }
      setFolderDropTargetId(null);
      setIsDraggingOver(false);
    };

    // Extract YYYY-MM-DD (local) from a CUT project's createdAt timestamp
    const extractProjectDate = (proj: AssemblyProject): string | null => {
      if (!proj.createdAt) return null;
      const d = new Date(proj.createdAt);
      if (Number.isNaN(d.getTime())) return null;
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      return `${y}-${m}-${day}`;
    };

    // Computed: projects and folders grouped
    // VTR mode uses local approvedProjects from backend; CUT mode uses shared context
    const filteredAssemblyProjects = useMemo(() => {
      const base = showOnlyApproved ? approvedProjects : assemblyProjects;
      if (showOnlyApproved || !effectiveCutDayFilter) return base;
      // The backend already scopes CUT to this day; this pass only covers the
      // gap between changing the day and the refetch landing. The open corte is
      // exempt — the backend deliberately returns it even from another day.
      return base.filter(
        (p) => extractProjectDate(p) === effectiveCutDayFilter || p.id === activeProjectId,
      );
    }, [
      showOnlyApproved,
      approvedProjects,
      assemblyProjects,
      effectiveCutDayFilter,
      activeProjectId,
    ]);

    // Computed filtered projects for VTR view (applies day + tag filters)
    const vtrFilteredProjects = useMemo(() => {
      if (!showOnlyApproved) return filteredAssemblyProjects;
      let result = filteredAssemblyProjects;
      if (vtrDayFilter) {
        const filterDate = new Date(vtrDayFilter + "T00:00:00");
        const nextDay = new Date(filterDate);
        nextDay.setDate(nextDay.getDate() + 1);
        result = result.filter((p) => {
          const d = new Date(p.createdAt);
          return d >= filterDate && d < nextDay;
        });
      }
      if (vtrTagFilter.length > 0) {
        result = result.filter((p) =>
          vtrTagFilter.some((tagId) => (p.vtrTags || []).includes(tagId)),
        );
      }
      const q = vtrSearchFilter.trim().toLowerCase();
      if (q) {
        result = result.filter((p) => {
          const haystack = [p.name, p.vtrDescription || "", p.vtrGrafismo || "", p.vtrNotas || ""]
            .join(" ")
            .toLowerCase();
          return haystack.includes(q);
        });
      }
      // Newest first. Slice so we don't mutate the upstream filtered array.
      return result.slice().sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
    }, [showOnlyApproved, filteredAssemblyProjects, vtrDayFilter, vtrTagFilter, vtrSearchFilter]);

    // VTR mode reads from the unscoped approvedFolders list so VTRs that live
    // inside another user's folder render under that same folder. CUT mode keeps
    // the user-scoped folders from SelectionContext.
    const folderSource = showOnlyApproved ? approvedFolders : assemblyFolders;

    const hasVisibleItems = (folderId: string): boolean => {
      if (!showOnlyApproved) return true;
      const projs = approvedProjects.filter((p) => p.folderId === folderId);
      if (projs.length > 0) return true;
      const subs = folderSource.filter((f) => f.parentId === folderId);
      return subs.some((s) => hasVisibleItems(s.id));
    };

    const rootProjects = filteredAssemblyProjects.filter((p) => !p.folderId);

    const rootFolders = folderSource.filter(
      (f) => !f.parentId && (!showOnlyApproved || hasVisibleItems(f.id)),
    );
    const getProjectsInFolder = (folderId: string) =>
      filteredAssemblyProjects.filter((p) => p.folderId === folderId);
    const getSubfolders = (folderId: string) =>
      folderSource.filter(
        (f) => f.parentId === folderId && (!showOnlyApproved || hasVisibleItems(f.id)),
      );

    // Render a single project row
    const renderProjectRow = (project: AssemblyProject, depth: number = 0) => (
      <div key={project.id} style={{ position: "relative" }}>
        {/* Horizontal branch */}
        {depth > 0 && (
          <div
            style={{
              position: "absolute",
              left: `${18 + (depth - 1) * 18}px`,
              top: "16px",
              width: "10px",
              height: "1px",
              background: "rgba(255,255,255,0.2)",
              pointerEvents: "none",
              zIndex: 1,
            }}
          />
        )}
        <div
          data-testid={`assembly-project-${project.id}`}
          draggable={!readOnly}
          onDragStart={(e) => !readOnly && handleProjectDragStart(e, project.id)}
          onDragEnd={handleProjectDragEnd}
          onClick={() => {
            setActiveProject(project.id);
            setViewMode("editor");
          }}
          style={{
            padding: "8px 10px",
            paddingLeft: `${10 + depth * 18}px`,
            background:
              activeProjectId === project.id
                ? "rgba(16, 185, 129, 0.1)"
                : draggedProjectId === project.id
                  ? "rgba(255,255,255,0.06)"
                  : "rgba(255,255,255,0.02)",
            border:
              activeProjectId === project.id
                ? "1px solid var(--color-role-sp)"
                : "1px solid transparent",
            borderRadius: "6px",
            cursor: draggedProjectId ? "grabbing" : "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            transition: "all 0.15s ease",
            opacity: draggedProjectId === project.id ? 0.5 : 1,
          }}
        >
          {editingId === project.id ? (
            <input
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              onBlur={handleSaveEdit}
              onKeyDown={(e) => e.key === "Enter" && handleSaveEdit()}
              autoFocus
              onClick={(e) => e.stopPropagation()}
              style={{
                background: "transparent",
                border: "none",
                color: "#fff",
                fontSize: "0.82rem",
                flex: 1,
              }}
            />
          ) : (
            <div
              style={{ display: "flex", alignItems: "center", gap: "6px", flex: 1, minWidth: 0 }}
            >
              <i
                className="bx bx-grid-vertical"
                style={{ color: "#444", fontSize: "0.8rem", flexShrink: 0, cursor: "grab" }}
              ></i>
              <i
                className="bx bx-film"
                style={{
                  color: activeProjectId === project.id ? "var(--color-role-sp)" : "#555",
                  fontSize: "0.85rem",
                  flexShrink: 0,
                }}
              ></i>
              <span
                style={{
                  color: activeProjectId === project.id ? "#fff" : "#bbb",
                  fontSize: "0.82rem",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {project.name}
              </span>
              {/* VTR Tag pills */}
              {(project.vtrTags || []).length > 0 && (
                <div style={{ display: "flex", gap: "2px", flexShrink: 0 }}>
                  {(project.vtrTags || []).slice(0, 3).map((tagId) => (
                    <span
                      key={tagId}
                      style={{
                        background: getVtrTagColor(tagId),
                        color: "#fff",
                        fontSize: "0.55rem",
                        padding: "1px 4px",
                        borderRadius: "3px",
                        fontWeight: 600,
                        textTransform: "uppercase",
                        lineHeight: "1.3",
                        maxWidth: "50px",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                      title={getVtrTagLabel(tagId)}
                    >
                      {getVtrTagLabel(tagId)}
                    </span>
                  ))}
                  {(project.vtrTags || []).length > 3 && (
                    <span style={{ color: "#666", fontSize: "0.55rem" }}>
                      +{(project.vtrTags || []).length - 3}
                    </span>
                  )}
                </div>
              )}
              {/* VTR Stars compact display */}
              {(project.vtrStars ?? 0) > 0 && (
                <StarRating
                  value={project.vtrStars ?? 0}
                  max={2}
                  readOnly
                  size="0.6rem"
                  activeColor="#ffaa00"
                />
              )}
            </div>
          )}
          <div style={{ display: "flex", gap: "4px", alignItems: "center", flexShrink: 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
              <span style={{ color: "#555", fontSize: "0.7rem" }}>
                {project.items.length} {t("items", "items")}
              </span>
              <span
                style={{
                  color: "#888",
                  fontSize: "0.65rem",
                  padding: "1px 4px",
                  background: "#2a2a2a",
                  borderRadius: "4px",
                  border: "1px solid #333",
                }}
              >
                ~{getProjectEstimatedDuration(project)}
              </span>
            </div>
            {!readOnly && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleToggleApproved(project);
                }}
                title={
                  project.isApproved
                    ? t("controller.layout.isApproved", "Approved")
                    : t("controller.layout.markAsApproved", "Mark as approved")
                }
                style={{
                  background: "transparent",
                  border: "none",
                  color: project.isApproved ? "#10b981" : "#555",
                  cursor: "pointer",
                  padding: "2px",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <i
                  className={project.isApproved ? "bx bx-check-circle" : "bx bx-circle"}
                  style={{ fontSize: "0.9rem" }}
                ></i>
              </button>
            )}
            {readOnly && project.isApproved && (
              <i
                className="bx bx-check-circle"
                style={{ fontSize: "0.9rem", color: "#10b981" }}
              ></i>
            )}
            {!readOnly && (
              <>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleStartEdit(project);
                  }}
                  style={{
                    background: "transparent",
                    border: "none",
                    color: "#555",
                    cursor: "pointer",
                    padding: "2px",
                  }}
                >
                  <i className="bx bx-edit-alt" style={{ fontSize: "0.8rem" }}></i>
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setPendingDeleteProjectId(project.id);
                  }}
                  style={{
                    background: "transparent",
                    border: "none",
                    color: "#555",
                    cursor: "pointer",
                    padding: "2px",
                  }}
                >
                  <i className="bx bx-trash" style={{ fontSize: "0.8rem" }}></i>
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    );

    const renderFolder = (folder: AssemblyFolder, depth: number = 0) => {
      const folderProjects = getProjectsInFolder(folder.id);
      const subFolders = getSubfolders(folder.id);
      const isDropTarget = folderDropTargetId === folder.id;
      const isHover = hoverFolderId === folder.id && !draggedProjectId;

      return (
        <div key={folder.id} style={{ position: "relative" }}>
          {/* Horizontal branch */}
          {depth > 0 && (
            <div
              style={{
                position: "absolute",
                left: `${18 + (depth - 1) * 18}px`,
                top: "16px",
                width: "10px",
                height: "1px",
                background: "rgba(255,255,255,0.2)",
                pointerEvents: "none",
                zIndex: 1,
              }}
            />
          )}
          {/* Folder header */}
          <div
            draggable
            onDragStart={(e) => handleFolderDragStart(e, folder.id)}
            onDragEnd={handleFolderDragEnd}
            onClick={() => {
              setActiveFolder(folder.id);
              toggleFolderExpanded(folder.id);
            }}
            onDragOver={(e) => handleFolderDragOver(e, folder.id)}
            onDragLeave={handleFolderDragLeave}
            onDrop={(e) => handleFolderDrop(e, folder.id)}
            onMouseEnter={() => setHoverFolderId(folder.id)}
            onMouseLeave={() => setHoverFolderId(null)}
            style={{
              padding: "6px 8px",
              paddingLeft: `${8 + depth * 18}px`,
              borderRadius: "6px",
              cursor: draggedFolderId ? "grabbing" : "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              background: isDropTarget
                ? "rgba(16, 185, 129, 0.2)"
                : activeFolderId === folder.id
                  ? "rgba(16, 185, 129, 0.15)"
                  : isHover
                    ? "rgba(255,255,255,0.06)"
                    : "transparent",
              border: isDropTarget
                ? "1px dashed var(--color-role-sp)"
                : activeFolderId === folder.id
                  ? "1px solid var(--color-role-sp)"
                  : "1px solid transparent",
              transition: "all 0.15s ease",
              opacity: draggedFolderId === folder.id ? 0.5 : 1,
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                flex: 1,
                minWidth: 0,
              }}
            >
              <i
                className="bx bx-grid-vertical"
                style={{ color: "#444", fontSize: "0.8rem", flexShrink: 0, cursor: "grab" }}
              ></i>
              <i
                className={`bx ${folder.isExpanded ? "bx-chevron-down" : "bx-chevron-right"}`}
                style={{ color: "#666", fontSize: "0.85rem", flexShrink: 0 }}
              ></i>
              <i
                className={`bx ${folder.isExpanded ? "bx-folder-open" : "bx-folder"}`}
                style={{ color: "#DAA520", fontSize: "0.9rem", flexShrink: 0 }}
              ></i>
              {editingFolderId === folder.id ? (
                <input
                  value={editFolderName}
                  onChange={(e) => setEditFolderName(e.target.value)}
                  onBlur={handleSaveEditFolder}
                  onKeyDown={(e) => e.key === "Enter" && handleSaveEditFolder()}
                  autoFocus
                  onClick={(e) => e.stopPropagation()}
                  style={{
                    background: "transparent",
                    border: "none",
                    color: "#fff",
                    fontSize: "0.82rem",
                    flex: 1,
                  }}
                />
              ) : (
                <span
                  style={{
                    color: "#ddd",
                    fontSize: "0.82rem",
                    fontWeight: 600,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {folder.name}
                </span>
              )}
            </div>
            <div style={{ display: "flex", gap: "2px", alignItems: "center", flexShrink: 0 }}>
              <span style={{ color: "#555", fontSize: "0.65rem", marginRight: "4px" }}>
                {folderProjects.length + subFolders.length}
              </span>
              {!showOnlyApproved && (
                <>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setCreatingFolderInId(folder.id);
                      setShowNewFolderInput(true);
                      setNewFolderName("");
                      if (!folder.isExpanded) toggleFolderExpanded(folder.id);
                    }}
                    title="New subfolder"
                    style={{
                      background: "transparent",
                      border: "none",
                      color: "#555",
                      cursor: "pointer",
                      padding: "2px",
                    }}
                  >
                    <i className="bx bx-folder-plus" style={{ fontSize: "0.8rem" }}></i>
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setCreatingInFolderId(folder.id);
                      setShowNewInput(true);
                      setNewProjectName("");
                      if (!folder.isExpanded) toggleFolderExpanded(folder.id);
                    }}
                    title="New cut in folder"
                    style={{
                      background: "transparent",
                      border: "none",
                      color: "#555",
                      cursor: "pointer",
                      padding: "2px",
                    }}
                  >
                    <i className="bx bx-plus" style={{ fontSize: "0.8rem" }}></i>
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setFolderMenuOpen(folderMenuOpen === folder.id ? null : folder.id);
                    }}
                    style={{
                      background: "transparent",
                      border: "none",
                      color: "#555",
                      cursor: "pointer",
                      padding: "2px",
                    }}
                  >
                    <i className="bx bx-dots-vertical-rounded" style={{ fontSize: "0.8rem" }}></i>
                  </button>
                </>
              )}
            </div>
          </div>
          {/* Folder context menu */}
          {folderMenuOpen === folder.id && (
            <div
              style={{
                background: "var(--color-bg-base)",
                border: "1px solid rgba(255,255,255,0.1)",
                borderRadius: "6px",
                padding: "4px",
                marginLeft: `${24 + depth * 18}px`,
                marginTop: "2px",
                marginBottom: "4px",
              }}
            >
              <button
                onClick={() => {
                  setCreatingFolderInId(folder.id);
                  setShowNewFolderInput(true);
                  setNewFolderName("");
                  setFolderMenuOpen(null);
                  if (!folder.isExpanded) toggleFolderExpanded(folder.id);
                }}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  width: "100%",
                  background: "transparent",
                  border: "none",
                  color: "#fff",
                  cursor: "pointer",
                  padding: "6px 8px",
                  borderRadius: "4px",
                  fontSize: "0.75rem",
                  textAlign: "left",
                }}
                onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(255,255,255,0.05)")}
                onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
              >
                <i className="bx bx-folder-plus"></i> New Subfolder
              </button>
              <button
                onClick={() => {
                  handleStartEditFolder(folder);
                  setFolderMenuOpen(null);
                }}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  width: "100%",
                  background: "transparent",
                  border: "none",
                  color: "#bbb",
                  cursor: "pointer",
                  padding: "6px 8px",
                  borderRadius: "4px",
                  fontSize: "0.75rem",
                  textAlign: "left",
                }}
                onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(255,255,255,0.05)")}
                onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
              >
                <i className="bx bx-edit-alt"></i> Rename
              </button>
              <button
                onClick={() => {
                  setPendingDeleteFolderId(folder.id);
                  setFolderMenuOpen(null);
                }}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  width: "100%",
                  background: "transparent",
                  border: "none",
                  color: "#ef4444",
                  cursor: "pointer",
                  padding: "6px 8px",
                  borderRadius: "4px",
                  fontSize: "0.75rem",
                  textAlign: "left",
                }}
                onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(239,68,68,0.08)")}
                onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
              >
                <i className="bx bx-trash"></i> Delete Folder
              </button>
            </div>
          )}
          {/* New Folder inside folder */}
          {showNewFolderInput && creatingFolderInId === folder.id && (
            <div style={{ padding: `4px 4px 4px ${32 + depth * 18}px`, marginTop: "2px" }}>
              <div style={{ display: "flex", gap: "4px" }}>
                <input
                  value={newFolderName}
                  onChange={(e) => setNewFolderName(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleCreateFolder(folder.id)}
                  placeholder="Folder name..."
                  autoFocus
                  style={{
                    flex: 1,
                    padding: "5px 8px",
                    background: "rgba(255,255,255,0.1)",
                    border: "1px solid rgba(255,255,255,0.2)",
                    borderRadius: "4px",
                    color: "#fff",
                    fontSize: "0.78rem",
                  }}
                />
                <button
                  onClick={() => handleCreateFolder(folder.id)}
                  style={{
                    background: "var(--color-role-sp)",
                    border: "none",
                    color: "#fff",
                    padding: "5px 8px",
                    borderRadius: "4px",
                    cursor: "pointer",
                  }}
                >
                  <i className="bx bx-check"></i>
                </button>
                <button
                  onClick={() => {
                    setShowNewFolderInput(false);
                    setCreatingFolderInId(null);
                    setNewFolderName("");
                  }}
                  style={{
                    background: "rgba(255,255,255,0.1)",
                    border: "none",
                    color: "#888",
                    padding: "5px 8px",
                    borderRadius: "4px",
                    cursor: "pointer",
                  }}
                >
                  <i className="bx bx-x"></i>
                </button>
              </div>
            </div>
          )}
          {/* New Project inside folder */}
          {showNewInput && creatingInFolderId === folder.id && (
            <div style={{ padding: `4px 4px 4px ${32 + depth * 18}px`, marginTop: "2px" }}>
              <div style={{ display: "flex", gap: "4px" }}>
                <input
                  value={newProjectName}
                  onChange={(e) => setNewProjectName(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleCreateProject(folder.id)}
                  placeholder="Cut name..."
                  autoFocus
                  style={{
                    flex: 1,
                    padding: "5px 8px",
                    background: "rgba(255,255,255,0.1)",
                    border: "1px solid rgba(255,255,255,0.2)",
                    borderRadius: "4px",
                    color: "#fff",
                    fontSize: "0.78rem",
                  }}
                />
                <button
                  onClick={() => handleCreateProject(folder.id)}
                  style={{
                    background: "var(--color-role-sp)",
                    border: "none",
                    color: "#fff",
                    padding: "5px 8px",
                    borderRadius: "4px",
                    cursor: "pointer",
                  }}
                >
                  <i className="bx bx-check"></i>
                </button>
                <button
                  onClick={() => {
                    setShowNewInput(false);
                    setCreatingInFolderId(null);
                    setNewProjectName("");
                  }}
                  style={{
                    background: "rgba(255,255,255,0.1)",
                    border: "none",
                    color: "#888",
                    padding: "5px 8px",
                    borderRadius: "4px",
                    cursor: "pointer",
                  }}
                >
                  <i className="bx bx-x"></i>
                </button>
              </div>
            </div>
          )}
          {/* Children (Folders and Projects) in folder */}
          {folder.isExpanded && (subFolders.length > 0 || folderProjects.length > 0) && (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "2px",
                marginTop: "2px",
                position: "relative",
              }}
            >
              {/* Vertical trunk for children */}
              <div
                style={{
                  position: "absolute",
                  left: `${18 + depth * 18}px`,
                  top: "-14px",
                  bottom: "16px",
                  width: "1px",
                  background: "rgba(255,255,255,0.2)",
                  pointerEvents: "none",
                  zIndex: 1,
                }}
              />
              {subFolders.map((sf) => renderFolder(sf, depth + 1))}
              {folderProjects.map((p) => renderProjectRow(p, depth + 1))}
            </div>
          )}
        </div>
      );
    };

    return (
      <div
        data-testid="assembly-manager"
        style={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
          background: isDraggingOver ? "rgba(16, 185, 129, 0.1)" : "var(--color-panel-bg)",
          borderRadius: "var(--border-radius-lg)",
          overflow: "hidden",
          border: isDraggingOver
            ? "2px dashed var(--color-role-sp)"
            : "1px solid var(--color-panel-border)",
          transition: "all 0.2s ease",
        }}
        onDrop={readOnly || canShowEditToggle ? undefined : handleDrop}
        onDragOver={readOnly || canShowEditToggle ? undefined : handleDragOver}
        onDragLeave={readOnly || canShowEditToggle ? undefined : handleDragLeave}
      >
        {/* Header */}
        <div
          data-testid="assembly-manager-header"
          style={{
            padding: "12px 15px",
            borderBottom: "1px solid var(--color-panel-border)",
            background: "var(--color-bg-base)",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexShrink: 0,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <i
              className="bx bx-movie"
              style={{ color: "var(--color-role-sp)", fontSize: "1.2rem" }}
            ></i>
            <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
              <span
                style={{
                  fontWeight: 900,
                  letterSpacing: "2px",
                  fontSize: "1rem",
                  color: "#fff",
                  textTransform: "uppercase",
                }}
              >
                {showOnlyApproved
                  ? t("controller.layout.approved", "VTR")
                  : t("controller.layout.cut", "CUT")}
              </span>
              {activeProject && (
                <span
                  style={{
                    color: "#888",
                    fontSize: "0.85rem",
                    fontWeight: 500,
                    maxWidth: "200px",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                  title={activeProject.name}
                >
                  - {activeProject.name}
                </span>
              )}
              {activeFolderId && (
                <>
                  <i className="bx bx-chevron-right" style={{ color: "#555" }}></i>
                  <span
                    style={{
                      color: "#DAA520",
                      fontWeight: 600,
                      fontSize: "0.85rem",
                      maxWidth: "150px",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                    title={folderSource.find((f) => f.id === activeFolderId)?.name}
                  >
                    {folderSource.find((f) => f.id === activeFolderId)?.name}
                  </span>
                  <button
                    onClick={() => setActiveFolder(null)}
                    title="Clear folder selection"
                    style={{
                      background: "transparent",
                      border: "none",
                      color: "#888",
                      cursor: "pointer",
                      padding: "2px",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <i className="bx bx-x" style={{ fontSize: "1rem" }}></i>
                  </button>
                </>
              )}
            </div>
          </div>
          <div style={{ display: "flex", gap: "6px", alignItems: "center" }}>
            {/* New VTR — only for oversight roles, who see unapproved VTRs here */}
            {showOnlyApproved && !readOnly && canManageVtr && (
              <button
                data-testid="vtr-new-btn"
                onClick={handleCreateVtr}
                title={t("storyproducer.assembly.newVtr", "Nuevo VTR")}
                style={{
                  background: "var(--color-role-sp)",
                  border: "none",
                  color: "#fff",
                  padding: "5px 10px",
                  borderRadius: "6px",
                  cursor: "pointer",
                  fontSize: "0.8rem",
                  fontWeight: 600,
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                }}
              >
                <i className="bx bx-plus"></i> {t("storyproducer.assembly.newVtr", "Nuevo VTR")}
              </button>
            )}
            {/* New CUT — always in the main header (both editor and explorer
                modes) so it no longer lives inside the explorer toolbar */}
            {!showOnlyApproved && !readOnly && (
              <button
                data-testid="cut-new-btn"
                onClick={handleCreateCut}
                title={t("storyproducer.assembly.new")}
                style={{
                  background: "var(--color-role-sp)",
                  border: "none",
                  color: "#fff",
                  padding: "5px 10px",
                  borderRadius: "6px",
                  cursor: "pointer",
                  fontSize: "0.8rem",
                  fontWeight: 600,
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                }}
              >
                <i className="bx bx-plus"></i> {t("storyproducer.assembly.new")}
              </button>
            )}
            {!showOnlyApproved && viewMode === "explorer" && (
              <>
                {/* Layout switcher: list vs cards */}
                <div
                  style={{
                    display: "flex",
                    background: "rgba(255,255,255,0.06)",
                    padding: "2px",
                    borderRadius: "6px",
                    border: "1px solid rgba(255,255,255,0.12)",
                    marginRight: "6px",
                  }}
                >
                  <button
                    onClick={() => setExplorerLayout("list")}
                    title="Vista de lista"
                    style={{
                      background:
                        explorerLayout === "list" ? "rgba(255,255,255,0.12)" : "transparent",
                      border: "none",
                      color: explorerLayout === "list" ? "#fff" : "#888",
                      padding: "4px 8px",
                      borderRadius: "4px",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <i className="bx bx-list-ul" style={{ fontSize: "1.05rem" }}></i>
                  </button>
                  <button
                    onClick={() => setExplorerLayout("cards")}
                    title="Vista de tarjetas"
                    style={{
                      background:
                        explorerLayout === "cards" ? "rgba(255,255,255,0.12)" : "transparent",
                      border: "none",
                      color: explorerLayout === "cards" ? "#fff" : "#888",
                      padding: "4px 8px",
                      borderRadius: "4px",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <i className="bx bx-grid-alt" style={{ fontSize: "1.05rem" }}></i>
                  </button>
                </div>
                {/* New Folder */}
                <button
                  data-testid="assembly-new-folder-btn"
                  onClick={() => {
                    if (activeFolderId) {
                      setCreatingFolderInId(activeFolderId);
                      const folder = assemblyFolders.find((f) => f.id === activeFolderId);
                      if (folder && !folder.isExpanded) toggleFolderExpanded(activeFolderId);
                    } else {
                      setCreatingFolderInId(null);
                    }
                    setShowNewFolderInput(true);
                  }}
                  title={t("storyproducer.assembly.newFolder")}
                  style={{
                    background: "rgba(255,255,255,0.08)",
                    border: "none",
                    color: "#aaa",
                    padding: "5px 8px",
                    borderRadius: "6px",
                    cursor: "pointer",
                    fontSize: "0.85rem",
                  }}
                >
                  <i className="bx bx-folder-plus"></i>
                </button>
                {/* New Project moved to the main header (see cut-new-btn) */}
                {activeProjectId && (
                  <button
                    onClick={() => setViewMode("editor")}
                    style={{
                      background: "rgba(255,255,255,0.08)",
                      border: "1px solid rgba(255,255,255,0.15)",
                      color: "#fff",
                      padding: "5px 12px",
                      borderRadius: "6px",
                      cursor: "pointer",
                      fontSize: "0.8rem",
                      fontWeight: 600,
                    }}
                  >
                    Volver al Editor
                  </button>
                )}
              </>
            )}
            {!showOnlyApproved && viewMode === "editor" && (
              <button
                onClick={() => setViewMode("explorer")}
                style={{
                  background: "rgba(255,255,255,0.08)",
                  border: "1px solid rgba(255,255,255,0.15)",
                  color: "#fff",
                  padding: "5px 12px",
                  borderRadius: "6px",
                  cursor: "pointer",
                  fontSize: "0.8rem",
                  fontWeight: 600,
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                }}
              >
                <i className="bx bx-folder-open"></i> Explorador
              </button>
            )}

            {/* Export Button (in header) */}
            {activeProject &&
              activeProject.items.length > 0 &&
              (!showOnlyApproved || activeProject.isApproved) && (
                <button
                  data-testid="assembly-export-sequence-btn"
                  onClick={() => {
                    setProjectToExport(activeProject);
                    setShowExportDialog(true);
                  }}
                  title={t("storyproducer.assembly.exportSequence")}
                  style={{
                    background: "rgba(16, 185, 129, 0.15)",
                    border: "1px solid rgba(16, 185, 129, 0.3)",
                    color: "var(--color-role-sp)",
                    padding: "5px 10px",
                    borderRadius: "6px",
                    cursor: "pointer",
                    fontSize: "0.8rem",
                    fontWeight: 600,
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                  }}
                >
                  <i className="bx bx-export"></i> {t("storyproducer.assembly.export")}
                </button>
              )}
          </div>
        </div>
        {/* Main Content Area — sidebar always rendered for CUT; the right panel
          swaps between CortesEditor (CUT edit mode) and the legacy cards list
          (VTR / read-only). */}
        {showOnlyApproved ? (
          <div
            style={{
              flex: 1,
              overflow: "auto",
              padding: "10px",
              display: "flex",
              flexDirection: "column",
            }}
          >
            {/* ── VTR Filters Bar ── */}
            <div
              data-testid="vtr-filters-bar"
              style={{
                display: "flex",
                alignItems: "center",
                gap: "12px",
                flexWrap: "wrap",
                padding: "8px 4px",
                marginBottom: "10px",
                borderBottom: "1px solid rgba(255,255,255,0.06)",
                flexShrink: 0,
              }}
            >
              {/* Day Filter */}
              <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                <i className="bx bx-calendar" style={{ color: "#888", fontSize: "0.95rem" }}></i>
                <input
                  data-testid="vtr-day-filter"
                  type="date"
                  value={vtrDayFilter}
                  onChange={(e) => setVtrDayFilter(e.target.value)}
                  style={{
                    background: "rgba(255,255,255,0.06)",
                    border: "1px solid rgba(255,255,255,0.12)",
                    borderRadius: "6px",
                    color: "#ccc",
                    padding: "4px 8px",
                    fontSize: "0.78rem",
                    outline: "none",
                    cursor: "pointer",
                    colorScheme: "dark",
                  }}
                />
                {vtrDayFilter && (
                  <button
                    data-testid="vtr-day-filter-clear"
                    onClick={() => setVtrDayFilter("")}
                    title="Limpiar filtro de día"
                    style={{
                      background: "transparent",
                      border: "none",
                      color: "#888",
                      cursor: "pointer",
                      padding: "2px",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <i className="bx bx-x" style={{ fontSize: "1rem" }}></i>
                  </button>
                )}
              </div>

              {/* Tag Filter */}
              <div style={{ display: "flex", alignItems: "center", gap: "6px", flexWrap: "wrap" }}>
                <i
                  className="bx bx-purchase-tag"
                  style={{ color: "#888", fontSize: "0.95rem" }}
                ></i>
                {vtrTags.map((vt) => {
                  const isActive = vtrTagFilter.includes(vt.id);
                  return (
                    <button
                      key={vt.id}
                      data-testid={`vtr-tag-filter-${vt.id}`}
                      onClick={() =>
                        setVtrTagFilter((prev) =>
                          isActive ? prev.filter((id) => id !== vt.id) : [...prev, vt.id],
                        )
                      }
                      style={{
                        background: isActive ? vt.color : "rgba(255,255,255,0.06)",
                        color: isActive ? "#fff" : "#999",
                        border: isActive
                          ? `1px solid ${vt.color}`
                          : "1px solid rgba(255,255,255,0.1)",
                        borderRadius: "12px",
                        padding: "3px 10px",
                        fontSize: "0.68rem",
                        fontWeight: 600,
                        cursor: "pointer",
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                        transition: "all 0.15s ease",
                        opacity: isActive ? 1 : 0.7,
                      }}
                    >
                      {vt.label}
                    </button>
                  );
                })}
                {vtrTagFilter.length > 0 && (
                  <button
                    data-testid="vtr-tag-filter-clear"
                    onClick={() => setVtrTagFilter([])}
                    title="Limpiar filtro de etiquetas"
                    style={{
                      background: "transparent",
                      border: "none",
                      color: "#888",
                      cursor: "pointer",
                      padding: "2px",
                      display: "flex",
                      alignItems: "center",
                      fontSize: "0.7rem",
                      gap: "2px",
                    }}
                  >
                    <i className="bx bx-x" style={{ fontSize: "0.9rem" }}></i>
                    Limpiar
                  </button>
                )}
              </div>

              {/* Search filter */}
              <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                <i className="bx bx-search" style={{ color: "#888", fontSize: "0.95rem" }}></i>
                <input
                  data-testid="vtr-search-filter"
                  type="text"
                  value={vtrSearchFilter}
                  onChange={(e) => setVtrSearchFilter(e.target.value)}
                  placeholder="Buscar VTRs..."
                  style={{
                    background: "rgba(255,255,255,0.06)",
                    border: "1px solid rgba(255,255,255,0.12)",
                    borderRadius: "6px",
                    color: "#ccc",
                    padding: "4px 8px",
                    fontSize: "0.78rem",
                    outline: "none",
                    minWidth: "180px",
                  }}
                />
                {vtrSearchFilter && (
                  <button
                    data-testid="vtr-search-filter-clear"
                    onClick={() => setVtrSearchFilter("")}
                    title="Limpiar búsqueda"
                    style={{
                      background: "transparent",
                      border: "none",
                      color: "#888",
                      cursor: "pointer",
                      padding: "2px",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <i className="bx bx-x" style={{ fontSize: "1rem" }}></i>
                  </button>
                )}
              </div>

              {/* Active filters summary */}
              {(vtrDayFilter || vtrTagFilter.length > 0 || vtrSearchFilter) && (
                <span style={{ color: "#666", fontSize: "0.72rem", marginLeft: "auto" }}>
                  {`${vtrFilteredProjects.length} de ${filteredAssemblyProjects.length} VTRs`}
                </span>
              )}
            </div>

            {isDataLoading && filteredAssemblyProjects.length === 0 ? (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  flex: 1,
                  gap: "16px",
                  color: "#666",
                }}
              >
                <div
                  style={{
                    width: "24px",
                    height: "24px",
                    border: "2px solid rgba(255,255,255,0.1)",
                    borderTopColor: "var(--color-role-sp)",
                    borderRadius: "50%",
                    animation: "spin 0.8s linear infinite",
                  }}
                />
                <span style={{ fontSize: "0.85rem" }}>Cargando proyectos...</span>
                <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
              </div>
            ) : (
              <table
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  color: "#ddd",
                  fontSize: "0.85rem",
                }}
              >
                <thead>
                  <tr
                    style={{
                      borderBottom: "1px solid rgba(255,255,255,0.1)",
                      textAlign: "left",
                      color: "#888",
                    }}
                  >
                    <th style={{ padding: "8px", width: "28px" }} />
                    <th style={{ padding: "8px", fontWeight: 600 }}>Nombre de VTR</th>
                    <th style={{ padding: "8px", fontWeight: 600 }}>Etiqueta</th>
                    <th style={{ padding: "8px", fontWeight: 600 }}>Duración</th>
                    <th style={{ padding: "8px", fontWeight: 600 }}>Estrellas</th>
                    <th style={{ padding: "8px", fontWeight: 600 }}>Descripción</th>
                    <th style={{ padding: "8px", fontWeight: 600 }}>Status edición</th>
                    <th style={{ padding: "8px", fontWeight: 600 }}>Fecha de creación</th>
                  </tr>
                </thead>
                <tbody>
                  {vtrFilteredProjects.map((project) => {
                    const vtrStatus = (project.vtrEditingStatus || "Por asignar") as VtrStatus;
                    const rowBg = VTR_ROW_BG[vtrStatus] ?? "transparent";
                    const rowBorderL = VTR_ROW_BORDER[vtrStatus] ?? "3px solid transparent";
                    const availableTransitions = getVtrTransitions(userRole, vtrStatus);
                    const isRowInteractive = availableTransitions.length > 0;
                    const isExpanded = expandedVtrId === project.id;
                    return (
                      <React.Fragment key={project.id}>
                        <tr
                          onClick={() => {
                            const willExpand = expandedVtrId !== project.id;
                            setExpandedVtrId(willExpand ? project.id : null);
                            // Also set as active project so outer drop handler targets this VTR
                            if (willExpand) setActiveProject(project.id);
                          }}
                          style={{
                            borderBottom: isExpanded ? "none" : "1px solid rgba(255,255,255,0.05)",
                            background: isExpanded ? "rgba(16,185,129,0.06)" : rowBg,
                            borderLeft: rowBorderL,
                            transition: "background 0.3s ease, border-left-color 0.3s ease",
                            opacity: vtrStatus === "CANCELADO" ? 0.55 : 1,
                            cursor: "pointer",
                          }}
                          onMouseEnter={(e) => {
                            if (!isExpanded)
                              (e.currentTarget as HTMLElement).style.background =
                                "rgba(255,255,255,0.03)";
                          }}
                          onMouseLeave={(e) => {
                            if (!isExpanded)
                              (e.currentTarget as HTMLElement).style.background = rowBg;
                          }}
                        >
                          {/* Expand chevron */}
                          <td style={{ padding: "8px", width: "28px" }}>
                            <i
                              className={`bx ${isExpanded ? "bx-chevron-down" : "bx-chevron-right"}`}
                              style={{
                                color: isExpanded ? "var(--color-role-sp)" : "#555",
                                fontSize: "1rem",
                                transition: "color 0.2s",
                              }}
                            />
                          </td>
                          <td style={{ padding: "8px", fontWeight: 600 }}>
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "6px",
                                minWidth: 0,
                              }}
                            >
                              {editingId === project.id ? (
                                <input
                                  value={editName}
                                  onChange={(e) => setEditName(e.target.value)}
                                  onBlur={handleSaveEdit}
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter") handleSaveEdit();
                                    if (e.key === "Escape") {
                                      setEditingId(null);
                                      setEditName("");
                                    }
                                  }}
                                  onClick={(e) => e.stopPropagation()}
                                  autoFocus
                                  style={{
                                    background: "rgba(255,255,255,0.08)",
                                    border: "1px solid rgba(255,255,255,0.18)",
                                    borderRadius: "4px",
                                    color: "#fff",
                                    fontSize: "0.85rem",
                                    fontWeight: 600,
                                    padding: "2px 6px",
                                    flex: 1,
                                    minWidth: 0,
                                    outline: "none",
                                  }}
                                />
                              ) : (
                                <>
                                  <span
                                    onDoubleClick={(e) => {
                                      if (readOnly) return;
                                      e.stopPropagation();
                                      handleStartEdit(project);
                                    }}
                                    title={readOnly ? undefined : "Doble clic para renombrar"}
                                    style={{ cursor: readOnly ? "default" : "text" }}
                                  >
                                    {project.name}
                                  </span>
                                  {!readOnly && (
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleStartEdit(project);
                                      }}
                                      title="Renombrar VTR"
                                      style={{
                                        background: "transparent",
                                        border: "none",
                                        color: "#666",
                                        cursor: "pointer",
                                        padding: "0 2px",
                                        display: "flex",
                                        alignItems: "center",
                                        opacity: 0.6,
                                      }}
                                      onMouseEnter={(e) =>
                                        ((e.currentTarget as HTMLElement).style.opacity = "1")
                                      }
                                      onMouseLeave={(e) =>
                                        ((e.currentTarget as HTMLElement).style.opacity = "0.6")
                                      }
                                    >
                                      <i className="bx bx-pencil" style={{ fontSize: "0.85rem" }} />
                                    </button>
                                  )}
                                  <span
                                    style={{
                                      color: "#555",
                                      fontSize: "0.65rem",
                                      fontWeight: 400,
                                    }}
                                  >
                                    {project.items.length} items
                                  </span>
                                </>
                              )}
                            </div>
                          </td>
                          <td style={{ padding: "8px" }}>
                            <div style={{ display: "flex", gap: "4px", flexWrap: "wrap" }}>
                              {(project.vtrTags || []).map((tagId) => (
                                <span
                                  key={tagId}
                                  style={{
                                    background: getVtrTagColor(tagId),
                                    color: "#fff",
                                    fontSize: "0.6rem",
                                    padding: "2px 6px",
                                    borderRadius: "4px",
                                    fontWeight: 600,
                                    textTransform: "uppercase",
                                  }}
                                >
                                  {getVtrTagLabel(tagId)}
                                </span>
                              ))}
                            </div>
                          </td>
                          <td style={{ padding: "8px" }}>{getProjectEstimatedDuration(project)}</td>
                          <td style={{ padding: "8px" }}>
                            <StarRating
                              value={project.vtrStars ?? 0}
                              max={2}
                              readOnly={readOnly}
                              onChange={(val) =>
                                !readOnly && handleProjectUpdate(project.id, { vtrStars: val })
                              }
                              size="0.8rem"
                              activeColor="#ffaa00"
                              inactiveColor="#444"
                            />
                          </td>
                          <td style={{ padding: "8px", minWidth: "150px" }}>
                            <VtrPopupField
                              value={project.vtrDescription || ""}
                              readOnly={!!readOnly}
                              onSave={(val) =>
                                handleProjectUpdate(project.id, { vtrDescription: val })
                              }
                              title="Descripción"
                              placeholder="Descripción del VTR..."
                            />
                          </td>
                          <td
                            style={{ padding: "8px", minWidth: "160px" }}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <VtrStatusControl
                              status={vtrStatus}
                              transitions={availableTransitions}
                              readOnly={!!readOnly || !isRowInteractive}
                              onTransition={(next) =>
                                handleVtrStatusTransition(project.id, vtrStatus, next)
                              }
                            />
                          </td>
                          <td
                            style={{ padding: "8px", color: "#aaa", whiteSpace: "nowrap" }}
                            title={
                              project.createdAt
                                ? new Date(project.createdAt).toLocaleString("es-ES")
                                : ""
                            }
                          >
                            {project.createdAt
                              ? new Date(project.createdAt).toLocaleDateString("es-ES", {
                                  day: "2-digit",
                                  month: "2-digit",
                                  year: "numeric",
                                })
                              : "—"}
                          </td>
                        </tr>

                        {/* Accordion detail row */}
                        {isExpanded && (
                          <tr
                            style={{
                              borderBottom: "1px solid rgba(255,255,255,0.08)",
                              borderLeft: rowBorderL,
                            }}
                          >
                            <td
                              colSpan={8}
                              style={{ padding: "0", background: "rgba(0,0,0,0.25)" }}
                            >
                              <div
                                onDrop={readOnly ? undefined : (e) => handleVtrDrop(e, project.id)}
                                onDragOver={
                                  readOnly ? undefined : (e) => handleVtrDragOver(e, project.id)
                                }
                                onDragLeave={readOnly ? undefined : handleVtrDragLeave}
                                style={{
                                  padding: "12px 16px 16px",
                                  borderTop: "1px solid rgba(255,255,255,0.05)",
                                  background:
                                    isDraggingOverVtrId === project.id
                                      ? "rgba(16,185,129,0.08)"
                                      : "transparent",
                                  border:
                                    isDraggingOverVtrId === project.id
                                      ? "2px dashed var(--color-role-sp)"
                                      : "2px solid transparent",
                                  borderRadius: "6px",
                                  transition: "all 0.2s ease",
                                  minHeight: "80px",
                                }}
                              >
                                {/* Detail header */}
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "8px",
                                    marginBottom: "12px",
                                  }}
                                >
                                  <i
                                    className="bx bx-film"
                                    style={{ color: "var(--color-role-sp)", fontSize: "1rem" }}
                                  />
                                  <span
                                    style={{
                                      color: "#fff",
                                      fontWeight: 700,
                                      fontSize: "0.85rem",
                                      letterSpacing: "0.5px",
                                    }}
                                  >
                                    {project.name}
                                  </span>
                                  <span
                                    style={{
                                      color: "#555",
                                      fontSize: "0.7rem",
                                      marginLeft: "4px",
                                    }}
                                  >
                                    — {project.items.length} cortes · ~
                                    {getProjectEstimatedDuration(project)}
                                  </span>
                                </div>

                                {/* CortesEditor — same editor as CUT view, fed
                                  the approved project directly so it can read
                                  /write against AssemblyManager's local
                                  `approvedProjects` state. Respects the global
                                  `readOnly` flag from AssemblyManager. */}
                                <div
                                  style={{
                                    display: "flex",
                                    flexDirection: "column",
                                    minHeight: "600px",
                                  }}
                                >
                                  <CortesEditor
                                    project={project}
                                    onUpdateProject={handleProjectUpdate}
                                    readOnly={!!readOnly}
                                    onTimecodeClick={handleCortesEditorTimecodeClick}
                                    activeMockTc={activeMockTc}
                                  />
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                  {vtrFilteredProjects.length === 0 && (
                    <tr>
                      <td
                        colSpan={8}
                        style={{ textAlign: "center", padding: "20px", color: "#555" }}
                      >
                        {vtrDayFilter || vtrTagFilter.length > 0 || vtrSearchFilter
                          ? "No hay VTRs que coincidan con los filtros seleccionados."
                          : t("controller.layout.noApprovedSelected", "No approved VTR selected.")}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            )}
          </div>
        ) : (
          <div
            style={{
              display: "flex",
              flex: 1,
              minHeight: 0,
              flexDirection: "column",
              background: "var(--color-panel-bg)",
            }}
          >
            {viewMode === "explorer" && (
              /* EXPLORER VIEW (takes full space) */
              <div
                style={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  padding: "20px",
                  overflowY: "auto",
                  overflowX: "hidden",
                  maxWidth: "800px",
                  margin: "0 auto",
                  width: "100%",
                  gap: "12px",
                }}
              >
                {/* Date filter carousel */}
                {!showOnlyApproved && (
                  <div style={{ flexShrink: 0, marginBottom: "12px", width: "100%" }}>
                    <DateCarousel
                      recordingDays={recordingDays}
                      selectedDate={cutDayFilter}
                      onSelectDate={handleCutDaySelect}
                    />
                  </div>
                )}

                {/* New Folder Input (root level) */}
                {showNewFolderInput && !creatingFolderInId && (
                  <div
                    style={{
                      padding: "12px",
                      background: "rgba(255,255,255,0.02)",
                      border: "1px solid rgba(255,255,255,0.05)",
                      borderRadius: "8px",
                    }}
                  >
                    <div style={{ display: "flex", gap: "6px" }}>
                      <i
                        className="bx bx-folder-plus"
                        style={{
                          color: "var(--color-role-sp)",
                          fontSize: "1.2rem",
                          alignSelf: "center",
                        }}
                      ></i>
                      <input
                        value={newFolderName}
                        onChange={(e) => setNewFolderName(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && handleCreateFolder()}
                        placeholder={t("storyproducer.assembly.folderNamePlaceholder")}
                        autoFocus
                        style={{
                          flex: 1,
                          padding: "6px 10px",
                          background: "rgba(255,255,255,0.1)",
                          border: "1px solid rgba(255,255,255,0.2)",
                          borderRadius: "6px",
                          color: "#fff",
                          fontSize: "0.82rem",
                        }}
                      />
                      <button
                        onClick={() => handleCreateFolder()}
                        style={{
                          background: "var(--color-role-sp)",
                          border: "none",
                          color: "#fff",
                          padding: "6px 10px",
                          borderRadius: "6px",
                          cursor: "pointer",
                        }}
                      >
                        <i className="bx bx-check"></i>
                      </button>
                      <button
                        onClick={() => {
                          setShowNewFolderInput(false);
                          setNewFolderName("");
                        }}
                        style={{
                          background: "rgba(255,255,255,0.1)",
                          border: "none",
                          color: "#888",
                          padding: "6px 10px",
                          borderRadius: "6px",
                          cursor: "pointer",
                        }}
                      >
                        <i className="bx bx-x"></i>
                      </button>
                    </div>
                  </div>
                )}

                {/* New Project Input (root level) */}
                {showNewInput && !creatingInFolderId && (
                  <div
                    style={{
                      padding: "12px",
                      background: "rgba(255,255,255,0.02)",
                      border: "1px solid rgba(255,255,255,0.05)",
                      borderRadius: "8px",
                    }}
                  >
                    <div style={{ display: "flex", gap: "6px" }}>
                      <input
                        value={newProjectName}
                        onChange={(e) => setNewProjectName(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && handleCreateProject()}
                        placeholder={t("storyproducer.assembly.projectNamePlaceholder")}
                        autoFocus
                        style={{
                          flex: 1,
                          padding: "6px 10px",
                          background: "rgba(255,255,255,0.1)",
                          border: "1px solid rgba(255,255,255,0.2)",
                          borderRadius: "6px",
                          color: "#fff",
                          fontSize: "0.82rem",
                        }}
                      />
                      <button
                        onClick={() => handleCreateProject()}
                        style={{
                          background: "var(--color-role-sp)",
                          border: "none",
                          color: "#fff",
                          padding: "6px 10px",
                          borderRadius: "6px",
                          cursor: "pointer",
                        }}
                      >
                        <i className="bx bx-check"></i>
                      </button>
                      <button
                        onClick={() => {
                          setShowNewInput(false);
                          setNewProjectName("");
                        }}
                        style={{
                          background: "rgba(255,255,255,0.1)",
                          border: "none",
                          color: "#888",
                          padding: "6px 10px",
                          borderRadius: "6px",
                          cursor: "pointer",
                        }}
                      >
                        <i className="bx bx-x"></i>
                      </button>
                    </div>
                  </div>
                )}

                {/* Folder Navigator — CUTS root */}
                {explorerLayout === "list" ? (
                  <div
                    data-testid="assembly-folder-navigator"
                    style={{
                      padding: "16px",
                      flex: 1,
                      background: "var(--color-panel-bg)",
                      border: "1px solid var(--color-panel-border)",
                      borderRadius: "var(--border-radius-lg)",
                      overflowY: "auto",
                    }}
                  >
                    {/* Root CUTS folder */}
                    <div
                      onClick={() => setRootExpanded(!rootExpanded)}
                      onDragOver={(e) => {
                        if (draggedProjectId) {
                          e.preventDefault();
                          e.stopPropagation();
                          e.dataTransfer.dropEffect = "move";
                          setFolderDropTargetId("__root__");
                        }
                      }}
                      onDragLeave={(e) => {
                        if (!e.currentTarget.contains(e.relatedTarget as Node))
                          setFolderDropTargetId(null);
                      }}
                      onDrop={(e) => handleFolderDrop(e, null)}
                      style={{
                        padding: "8px 12px",
                        borderRadius: "6px",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        background:
                          folderDropTargetId === "__root__"
                            ? "rgba(16, 185, 129, 0.15)"
                            : "rgba(255,255,255,0.03)",
                        border:
                          folderDropTargetId === "__root__"
                            ? "1px dashed var(--color-role-sp)"
                            : "1px solid transparent",
                        transition: "all 0.15s ease",
                        marginBottom: "6px",
                      }}
                    >
                      <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        <i
                          className={`bx ${rootExpanded ? "bx-chevron-down" : "bx-chevron-right"}`}
                          style={{ color: "#888", fontSize: "1rem" }}
                        ></i>
                        <i
                          className={`bx ${rootExpanded ? "bx-folder-open" : "bx-folder"}`}
                          style={{ color: "var(--color-role-sp)", fontSize: "1.1rem" }}
                        ></i>
                        <span
                          style={{
                            color: "#fff",
                            fontSize: "0.88rem",
                            fontWeight: 700,
                            letterSpacing: "0.5px",
                          }}
                        >
                          {t("storyproducer.assembly.cuts")}
                        </span>
                      </div>
                      <span style={{ color: "#666", fontSize: "0.75rem" }}>
                        {filteredAssemblyProjects.length}
                      </span>
                    </div>

                    {/* CUTS content (expanded) */}
                    {rootExpanded && (
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "4px",
                          paddingLeft: "4px",
                          position: "relative",
                        }}
                      >
                        {/* Sub-Folders */}
                        {rootFolders.map((folder) => renderFolder(folder, 0))}

                        {/* Root (unfiled) projects */}
                        {rootProjects.map((p) => renderProjectRow(p, 0))}

                        {/* Loading state */}
                        {isDataLoading &&
                          folderSource.length === 0 &&
                          rootProjects.length === 0 && (
                            <div
                              style={{
                                display: "flex",
                                flexDirection: "column",
                                gap: "6px",
                                padding: "10px",
                              }}
                            >
                              {[1, 2, 3].map((i) => (
                                <div
                                  key={i}
                                  style={{
                                    height: "36px",
                                    borderRadius: "6px",
                                    background:
                                      "linear-gradient(90deg, rgba(255,255,255,0.03) 25%, rgba(255,255,255,0.06) 50%, rgba(255,255,255,0.03) 75%)",
                                    backgroundSize: "200% 100%",
                                    animation: "shimmer 1.5s ease-in-out infinite",
                                  }}
                                />
                              ))}
                              <style>{`@keyframes shimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }`}</style>
                            </div>
                          )}
                        {/* Empty state */}
                        {!isDataLoading &&
                          folderSource.length === 0 &&
                          rootProjects.length === 0 && (
                            <div
                              style={{
                                color: "#555",
                                textAlign: "center",
                                padding: "16px",
                                fontSize: "0.85rem",
                              }}
                            >
                              No hay proyectos. Pulsa <b>Nuevo</b> para empezar.
                            </div>
                          )}
                      </div>
                    )}
                  </div>
                ) : (
                  /* Cards View (Grid layout) */
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
                      gap: "16px",
                      padding: "4px",
                      overflowY: "auto",
                      flex: 1,
                    }}
                  >
                    {filteredAssemblyProjects.map((project) => {
                      const durationStr = getProjectEstimatedDuration(project);
                      const folder = folderSource.find((f) => f.id === project.folderId);

                      return (
                        <div
                          key={project.id}
                          onClick={() => {
                            setActiveProject(project.id);
                            setViewMode("editor");
                          }}
                          onDoubleClick={() => {
                            setActiveProject(project.id);
                            setViewMode("editor");
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.borderColor =
                              "var(--color-accent-primary, #f72d60)";
                            e.currentTarget.style.transform = "translateY(-3px)";
                            e.currentTarget.style.boxShadow = "0 8px 20px rgba(247, 45, 96, 0.15)";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.borderColor =
                              "var(--color-panel-border, #2a2a2a)";
                            e.currentTarget.style.transform = "none";
                            e.currentTarget.style.boxShadow = "none";
                          }}
                          style={{
                            background: "var(--color-panel-bg, #1a1a1a)",
                            border: "1px solid var(--color-panel-border, #2a2a2a)",
                            borderRadius: "12px",
                            padding: "16px",
                            display: "flex",
                            flexDirection: "column",
                            gap: "12px",
                            cursor: "pointer",
                            transition: "all 0.2s ease-in-out",
                            height: "140px",
                            justifyContent: "space-between",
                            position: "relative",
                          }}
                        >
                          <div>
                            {/* Title and Icon */}
                            <div style={{ display: "flex", alignItems: "flex-start", gap: "8px" }}>
                              <div
                                style={{
                                  width: "32px",
                                  height: "32px",
                                  borderRadius: "8px",
                                  background: "rgba(247, 45, 96, 0.1)",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  flexShrink: 0,
                                }}
                              >
                                <i
                                  className="bx bx-film"
                                  style={{
                                    color: "var(--color-accent-primary, #f72d60)",
                                    fontSize: "1.1rem",
                                  }}
                                ></i>
                              </div>
                              <span
                                style={{
                                  color: "#fff",
                                  fontWeight: 600,
                                  fontSize: "0.85rem",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  display: "-webkit-box",
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: "vertical",
                                  lineHeight: "1.2",
                                }}
                              >
                                {project.name}
                              </span>
                            </div>

                            {/* Folder indicator if present */}
                            {folder && (
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "4px",
                                  fontSize: "0.7rem",
                                  color: "#888",
                                  marginTop: "8px",
                                }}
                              >
                                <i className="bx bx-folder" style={{ color: "#DAA520" }}></i>
                                <span
                                  style={{
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {folder.name}
                                </span>
                              </div>
                            )}
                          </div>

                          {/* Metadata Footer */}
                          <div>
                            <div
                              style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                fontSize: "0.72rem",
                                color: "#888",
                                borderTop: "1px solid rgba(255,255,255,0.05)",
                                paddingTop: "8px",
                              }}
                            >
                              <span style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                                <i className="bx bx-list-check" style={{ color: "#888" }}></i>
                                {project.items?.length || 0} ítems
                              </span>
                              <span style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                                <i className="bx bx-time" style={{ color: "#888" }}></i>
                                {durationStr}
                              </span>
                            </div>

                            <div
                              style={{
                                fontSize: "0.65rem",
                                color: "#555",
                                textAlign: "right",
                                marginTop: "4px",
                              }}
                            >
                              {project.createdAt
                                ? new Date(project.createdAt).toLocaleDateString("es-ES", {
                                    day: "2-digit",
                                    month: "short",
                                    hour: "2-digit",
                                    minute: "2-digit",
                                  })
                                : ""}
                            </div>
                          </div>
                        </div>
                      );
                    })}

                    {filteredAssemblyProjects.length === 0 && (
                      <div
                        style={{
                          gridColumn: "1 / -1",
                          color: "#555",
                          textAlign: "center",
                          padding: "40px",
                          fontSize: "0.85rem",
                        }}
                      >
                        No hay proyectos para este día. Pulsa <b>Nuevo Proyecto</b> arriba para
                        crear uno.
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
            {viewMode === "editor" && (
              /* EDITOR VIEW */
              <div
                style={
                  isOnlyCutMode && !isMockPlayerCollapsed
                    ? {
                        flex: 1,
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        gap: "16px",
                        minWidth: 0,
                        overflow: "hidden",
                        width: "100%",
                        padding: "0 24px 24px 24px",
                      }
                    : {
                        flex: 1,
                        display: "flex",
                        flexDirection: "column" as const,
                        minWidth: 0,
                        overflow: "hidden",
                        maxWidth: "800px",
                        margin: "0 auto",
                        width: "100%",
                        padding: "0 24px 24px 24px",
                      }
                }
              >
                {/* ── Right Column in 2-col mode: Monitor + Timeline ── */}
                <div
                  style={
                    isOnlyCutMode
                      ? {
                          gridColumn: 2,
                          gridRow: "1 / -1",
                          display: "flex",
                          flexDirection: "column" as const,
                          minHeight: 0,
                          overflow: "auto",
                        }
                      : {
                          flexShrink: 0,
                          display: "flex",
                          flexDirection: "column" as const,
                        }
                  }
                >
                  {/* MOCK VIDEO PLAYER CONTAINER (only in CUT view) */}
                  {!showOnlyApproved && activeProject && playoutItems.length > 0 && (
                    <div style={{ width: "100%" }}>
                      <div
                        style={{
                          position: "sticky",
                          top: 0,
                          zIndex: 10,
                          background: "var(--color-panel-bg, #161616)",
                          paddingTop: "8px",
                          paddingBottom: "16px",
                          marginBottom: "16px",
                          borderBottom: "1px solid rgba(255,255,255,0.05)",
                          width: "100%",
                        }}
                      >
                        <div
                          style={{
                            background: "var(--color-bg-base)",
                            borderRadius: "8px",
                            border: "1px solid rgba(255,255,255,0.05)",
                            boxShadow: "0 10px 30px rgba(0,0,0,0.5)",
                            display: "flex",
                            flexDirection: "column",
                            overflow: "hidden",
                            transition: "all 0.3s ease",
                          }}
                        >
                          {/* Header to toggle mock player */}
                          <div
                            style={{
                              padding: "8px 16px",
                              background: "rgba(20,20,20,0.9)",
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              cursor: "pointer",
                              borderBottom: isMockPlayerCollapsed
                                ? "none"
                                : "1px solid rgba(255,255,255,0.05)",
                            }}
                            onClick={() => setIsMockPlayerCollapsed(!isMockPlayerCollapsed)}
                          >
                            {/* Left: title */}
                            <div
                              style={{
                                fontSize: "0.8rem",
                                color: "var(--color-role-sp)",
                                fontWeight: 600,
                                textTransform: "uppercase",
                                display: "flex",
                                alignItems: "center",
                                gap: "6px",
                                flexShrink: 0,
                              }}
                            >
                              <i className="bx bx-video"></i>
                              Stream Program
                            </div>
                            {/* Center: project info */}
                            {playoutItems.length > 0 &&
                              (() => {
                                const projLabel = activeProject?.name
                                  ? activeProject.name
                                  : formatMonitorTc(playoutItems[0]?.tcIn || playoutItems[0]?.tc)
                                      .date || "";
                                return (
                                  <div
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: "10px",
                                      fontSize: "0.72rem",
                                      color: "#666",
                                      flex: 1,
                                      justifyContent: "center",
                                      minWidth: 0,
                                    }}
                                  >
                                    {projLabel && (
                                      <span
                                        style={{
                                          color: "#888",
                                          fontWeight: 500,
                                          overflow: "hidden",
                                          textOverflow: "ellipsis",
                                          whiteSpace: "nowrap",
                                          maxWidth: "160px",
                                          textTransform: "none",
                                        }}
                                      >
                                        {projLabel}
                                      </span>
                                    )}
                                    {activeProject?.createdAt && (
                                      <>
                                        <span style={{ color: "#444" }}>·</span>
                                        <span style={{ color: "#555", fontSize: "0.68rem" }}>
                                          {new Date(activeProject.createdAt).toLocaleDateString(
                                            "es-ES",
                                            {
                                              day: "2-digit",
                                              month: "short",
                                              year: "numeric",
                                            },
                                          )}
                                        </span>
                                      </>
                                    )}
                                    <span style={{ color: "#444" }}>·</span>
                                    <span style={{ fontFamily: "monospace", color: "#666" }}>
                                      {playoutItems.length} corte
                                      {playoutItems.length !== 1 ? "s" : ""}
                                    </span>
                                    <span style={{ color: "#444" }}>·</span>
                                    <span
                                      style={{
                                        fontFamily: "monospace",
                                        color: "var(--color-role-sp)",
                                        fontWeight: 600,
                                        fontSize: "0.85rem",
                                      }}
                                    >
                                      {formatMonitorTc(activeMockTc).tc}
                                    </span>
                                  </div>
                                );
                              })()}
                            <i
                              className={`bx bx-chevron-${isMockPlayerCollapsed ? "down" : "up"}`}
                              style={{ color: "#aaa", flexShrink: 0 }}
                            ></i>
                          </div>

                          {!isMockPlayerCollapsed && (
                            <div
                              style={{
                                position: "relative",
                                width: "100%",
                                display: "flex",
                                flexDirection: "column",
                              }}
                            >
                              <div
                                style={{
                                  width: "100%",
                                  aspectRatio: "16/9",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  position: "relative",
                                  background: "#000",
                                  overflow: "hidden",
                                }}
                              >
                                {previewClip ? (
                                  <>
                                    {(["a", "b"] as VtrDeckId[]).map((deck) => {
                                      const clip = vtrDeckClips[deck];
                                      if (!clip) return null;
                                      const isActiveDeck = deck === activeVtrDeck;
                                      return (
                                        <div
                                          key={deck}
                                          style={{
                                            position: "absolute",
                                            inset: 0,
                                            opacity: isActiveDeck ? 1 : 0,
                                            pointerEvents: isActiveDeck ? "auto" : "none",
                                          }}
                                        >
                                          <RelayClipPlayer
                                            ref={deck === "a" ? vtrDeckARef : vtrDeckBRef}
                                            at={clip.at}
                                            channel={clip.channel}
                                            autoPlayOnLoad={isActiveDeck && isVtrPlaying}
                                            stopAtSeekWindowEnd={false}
                                            onLoadingChange={(loading) => {
                                              setVtrDeckLoading((prev) => ({
                                                ...prev,
                                                [deck]: loading,
                                              }));
                                              if (deck === activeVtrDeckRef.current)
                                                setVtrLoading(loading);
                                            }}
                                            onError={(msg) => {
                                              setVtrDeckErrors((prev) => ({
                                                ...prev,
                                                [deck]: msg,
                                              }));
                                              if (deck !== activeVtrDeckRef.current) return;
                                              setVtrError(msg);
                                              if (msg && isVtrPlayingRef.current) advanceVtr();
                                            }}
                                          />
                                        </div>
                                      );
                                    })}
                                    {vtrLoading && !vtrError && (
                                      <div
                                        style={{
                                          position: "absolute",
                                          inset: 0,
                                          display: "flex",
                                          alignItems: "center",
                                          justifyContent: "center",
                                          color: "rgba(255,255,255,0.65)",
                                          fontSize: "0.85rem",
                                          background: "rgba(0,0,0,0.35)",
                                          pointerEvents: "none",
                                        }}
                                      >
                                        {t("common.loading", "LOADING...")}
                                      </div>
                                    )}
                                    {vtrError && (
                                      <div
                                        style={{
                                          position: "absolute",
                                          inset: 0,
                                          display: "flex",
                                          flexDirection: "column",
                                          alignItems: "center",
                                          justifyContent: "center",
                                          gap: "8px",
                                          color: "rgba(255,255,255,0.75)",
                                          background: "rgba(0,0,0,0.65)",
                                          textAlign: "center",
                                          padding: "18px",
                                        }}
                                      >
                                        <i
                                          className="bx bx-error-circle"
                                          style={{ fontSize: "1.7rem", color: "#f72d60" }}
                                        />
                                        <span style={{ fontSize: "0.82rem" }}>{vtrError}</span>
                                      </div>
                                    )}
                                  </>
                                ) : (
                                  <div style={{ textAlign: "center" }}>
                                    <div
                                      style={{
                                        fontSize: "1.2rem",
                                        fontWeight: 700,
                                        color: "var(--color-role-sp)",
                                        marginBottom: "8px",
                                        textTransform: "uppercase",
                                        letterSpacing: "2px",
                                      }}
                                    >
                                      PROGRAM MONITOR
                                    </div>
                                  </div>
                                )}
                              </div>

                              {/* Controls Bar */}
                              <div
                                style={{
                                  background: "rgba(20,20,20,0.9)",
                                  display: "flex",
                                  flexDirection: "column",
                                  borderTop: "1px solid rgba(255,255,255,0.05)",
                                }}
                              >
                                {/* Seek bar */}
                                <div
                                  onPointerDown={(e) => {
                                    if (!previewClip) return;
                                    e.currentTarget.setPointerCapture(e.pointerId);
                                    const rect = e.currentTarget.getBoundingClientRect();
                                    const frac = Math.max(
                                      0,
                                      Math.min((e.clientX - rect.left) / rect.width, 1),
                                    );
                                    getActiveVtrPlayer()?.seekToFraction(frac);
                                  }}
                                  onPointerMove={(e) => {
                                    if (e.buttons !== 1 || !previewClip) return;
                                    if (!e.currentTarget.hasPointerCapture(e.pointerId)) return;
                                    const rect = e.currentTarget.getBoundingClientRect();
                                    const frac = Math.max(
                                      0,
                                      Math.min((e.clientX - rect.left) / rect.width, 1),
                                    );
                                    getActiveVtrPlayer()?.seekToFraction(frac);
                                  }}
                                  onPointerUp={(e) => {
                                    if (e.currentTarget.hasPointerCapture(e.pointerId))
                                      e.currentTarget.releasePointerCapture(e.pointerId);
                                  }}
                                  style={{
                                    position: "relative",
                                    height: "14px",
                                    display: "flex",
                                    alignItems: "center",
                                    padding: "0 16px",
                                    cursor: previewClip ? "pointer" : "default",
                                    touchAction: "none",
                                  }}
                                >
                                  <div
                                    style={{
                                      position: "relative",
                                      width: "100%",
                                      height: "4px",
                                      borderRadius: "2px",
                                      background: "rgba(255,255,255,0.15)",
                                    }}
                                  >
                                    <div
                                      style={{
                                        position: "absolute",
                                        left: 0,
                                        top: 0,
                                        bottom: 0,
                                        width: `${scrubPct}%`,
                                        borderRadius: "2px",
                                        background: "var(--color-role-sp)",
                                      }}
                                    />
                                    <div
                                      style={{
                                        position: "absolute",
                                        top: "50%",
                                        left: `${scrubPct}%`,
                                        width: "12px",
                                        opacity: previewClip ? 1 : 0,
                                        pointerEvents: "none",
                                      }}
                                    />
                                  </div>
                                </div>

                                {/* Transport row */}
                                <div
                                  style={{
                                    height: "48px",
                                    display: "flex",
                                    alignItems: "center",
                                    padding: "0 16px",
                                    justifyContent: "space-between",
                                    position: "relative",
                                  }}
                                >
                                  {/* Left: transport buttons */}
                                  <div
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: "12px",
                                      color: "#fff",
                                    }}
                                  >
                                    {/* |◀ Primer corte */}
                                    <i
                                      className="bx bx-first-page"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleStopVtr();
                                      }}
                                      style={{
                                        fontSize: "1.3rem",
                                        cursor: playoutItems.length ? "pointer" : "default",
                                        color: "#aaa",
                                        opacity: playoutItems.length ? 1 : 0.4,
                                      }}
                                      title="Ir al primer corte"
                                      onMouseEnter={(e) => {
                                        if (playoutItems.length)
                                          e.currentTarget.style.color = "var(--color-role-sp)";
                                      }}
                                      onMouseLeave={(e) => {
                                        e.currentTarget.style.color = "#aaa";
                                      }}
                                    />
                                    <i
                                      className="bx bx-rewind"
                                      onClick={() => getActiveVtrPlayer()?.seekBy(-5)}
                                      style={{
                                        fontSize: "1.3rem",
                                        cursor: previewClip ? "pointer" : "default",
                                        color: "#aaa",
                                        opacity: previewClip ? 1 : 0.4,
                                      }}
                                      title="Retroceder 5 segundos"
                                      onMouseEnter={(e) => {
                                        if (previewClip) e.currentTarget.style.color = "#fff";
                                      }}
                                      onMouseLeave={(e) => {
                                        e.currentTarget.style.color = "#aaa";
                                      }}
                                    />
                                    <i
                                      className="bx bx-chevron-left"
                                      onClick={() => getActiveVtrPlayer()?.seekBy(-1 / 29.97)}
                                      style={{
                                        fontSize: "1.4rem",
                                        cursor: previewClip ? "pointer" : "default",
                                        color: "#aaa",
                                        opacity: previewClip ? 1 : 0.4,
                                      }}
                                      title="Retroceder 1 frame"
                                      onMouseEnter={(e) => {
                                        if (previewClip) e.currentTarget.style.color = "#fff";
                                      }}
                                      onMouseLeave={(e) => {
                                        e.currentTarget.style.color = "#aaa";
                                      }}
                                    />
                                    <i
                                      className="bx bx-skip-previous"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handlePrevClip();
                                      }}
                                      style={{
                                        fontSize: "1.5rem",
                                        cursor: playoutItems.length ? "pointer" : "default",
                                        color: "#aaa",
                                        opacity: playoutItems.length ? 1 : 0.4,
                                      }}
                                      title="Corte anterior"
                                      onMouseEnter={(e) => {
                                        if (playoutItems.length)
                                          e.currentTarget.style.color = "var(--color-role-sp)";
                                      }}
                                      onMouseLeave={(e) => {
                                        e.currentTarget.style.color = "#aaa";
                                      }}
                                    />
                                    <i
                                      className={`bx ${isVtrPlaying ? "bx-pause" : "bx-play"}`}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        toggleVtrPlay();
                                      }}
                                      style={{
                                        fontSize: "1.6rem",
                                        cursor: playoutItems.length ? "pointer" : "default",
                                        color: "var(--color-role-sp)",
                                      }}
                                      title={t(
                                        "storyproducer.relayClip.playProgram",
                                        "Reproducir/Pausar programa",
                                      )}
                                    />
                                    <i
                                      className="bx bx-skip-next"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleNextClip();
                                      }}
                                      style={{
                                        fontSize: "1.5rem",
                                        cursor: playoutItems.length ? "pointer" : "default",
                                        color: "#aaa",
                                        opacity: playoutItems.length ? 1 : 0.4,
                                      }}
                                      title="Corte siguiente"
                                      onMouseEnter={(e) => {
                                        if (playoutItems.length)
                                          e.currentTarget.style.color = "var(--color-role-sp)";
                                      }}
                                      onMouseLeave={(e) => {
                                        e.currentTarget.style.color = "#aaa";
                                      }}
                                    />
                                    <i
                                      className="bx bx-chevron-right"
                                      onClick={() => getActiveVtrPlayer()?.seekBy(1 / 29.97)}
                                      style={{
                                        fontSize: "1.4rem",
                                        cursor: previewClip ? "pointer" : "default",
                                        color: "#aaa",
                                        opacity: previewClip ? 1 : 0.4,
                                      }}
                                      title="Avanzar 1 frame"
                                      onMouseEnter={(e) => {
                                        if (previewClip) e.currentTarget.style.color = "#fff";
                                      }}
                                      onMouseLeave={(e) => {
                                        e.currentTarget.style.color = "#aaa";
                                      }}
                                    />
                                    <i
                                      className="bx bx-fast-forward"
                                      onClick={() => getActiveVtrPlayer()?.seekBy(5)}
                                      style={{
                                        fontSize: "1.3rem",
                                        cursor: previewClip ? "pointer" : "default",
                                        color: "#aaa",
                                        opacity: previewClip ? 1 : 0.4,
                                      }}
                                      title="Avanzar 5 segundos"
                                      onMouseEnter={(e) => {
                                        if (previewClip) e.currentTarget.style.color = "#fff";
                                      }}
                                      onMouseLeave={(e) => {
                                        e.currentTarget.style.color = "#aaa";
                                      }}
                                    />
                                  </div>

                                  {/* Center: TCs */}
                                  <div
                                    style={{
                                      position: "absolute",
                                      left: "50%",
                                      top: "50%",
                                      transform: "translate(-50%, -50%)",
                                      pointerEvents: "none",
                                      display: "flex",
                                      flexDirection: "column",
                                      alignItems: "center",
                                      gap: "0px",
                                    }}
                                  >
                                    {/* PGM TC */}
                                    <div
                                      style={{
                                        fontSize: "1.05rem",
                                        fontFamily: "monospace",
                                        color: "#fff",
                                        textShadow: "0 1px 4px rgba(0,0,0,0.5)",
                                        fontWeight: 500,
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "8px",
                                      }}
                                    >
                                      {(() => {
                                        // TC global = posición del playhead en formato NLE MM:SS:FF (29.97fps)
                                        const FPS = 29.97;
                                        const totalProgMs =
                                          playoutItems.reduce(
                                            (acc, it) => acc + getScrubberDurationMs(it),
                                            0,
                                          ) || 1;
                                        const elapsedMs = (scrubPct / 100) * totalProgMs;
                                        // Formato MM:SS:FF — frames a 29.97fps
                                        const fmtGlobal = (ms: number) => {
                                          const totalSec = ms / 1000;
                                          const h = Math.floor(totalSec / 3600);
                                          const m = Math.floor((totalSec % 3600) / 60);
                                          const s = Math.floor(totalSec % 60);
                                          const f = Math.floor((totalSec % 1) * FPS);
                                          const ff = f.toString().padStart(2, "0");
                                          if (h > 0)
                                            return `${h}:${m.toString().padStart(2, "0")}:${s.toString().padStart(2, "0")};${ff}`;
                                          return `${m.toString().padStart(2, "0")}:${s.toString().padStart(2, "0")};${ff}`;
                                        };
                                        // Duración total en MM:SS;FF
                                        const fmtTotal = (ms: number) => {
                                          const ts = ms / 1000;
                                          const th = Math.floor(ts / 3600);
                                          const tm = Math.floor((ts % 3600) / 60);
                                          const tsec = Math.floor(ts % 60);
                                          const f = Math.floor((ts % 1) * FPS);
                                          const ff = f.toString().padStart(2, "0");
                                          if (th > 0)
                                            return `${th}:${tm.toString().padStart(2, "0")}:${tsec.toString().padStart(2, "0")};${ff}`;
                                          return `${tm.toString().padStart(2, "0")}:${tsec.toString().padStart(2, "0")};${ff}`;
                                        };
                                        return (
                                          <>
                                            <span style={{ letterSpacing: "0.5px" }}>
                                              {fmtGlobal(elapsedMs)}
                                            </span>
                                            <span
                                              style={{
                                                fontSize: "0.72rem",
                                                color: "#888",
                                                fontWeight: 500,
                                                whiteSpace: "nowrap",
                                                flexShrink: 0,
                                              }}
                                            >
                                              / {fmtTotal(totalProgMs)}
                                            </span>
                                          </>
                                        );
                                      })()}
                                    </div>

                                    {/* Source clip TC (timecode de la tarjeta activa) */}
                                    {activeMockTc && (
                                      <div
                                        style={{
                                          fontSize: "0.85rem",
                                          fontFamily: "monospace",
                                          color: "var(--color-role-sp)",
                                          fontWeight: 600,
                                          textShadow: "0 1px 3px rgba(0,0,0,0.5)",
                                          letterSpacing: "0.5px",
                                          marginTop: "-2px",
                                        }}
                                      >
                                        SRC {formatMonitorTc(activeMockTc).tc}
                                      </div>
                                    )}
                                  </div>

                                  {/* Right: shortcuts + fullscreen */}
                                  <div
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: "14px",
                                      flexShrink: 0,
                                    }}
                                  >
                                    {/* Shortcuts button */}
                                    <i
                                      className="bx bxs-keyboard"
                                      onClick={() => setShowVtrShortcutsModal((prev) => !prev)}
                                      title="Atajos de teclado del player"
                                      style={{
                                        color: showVtrShortcutsModal
                                          ? "var(--color-role-sp, #F72D60)"
                                          : "#aaa",
                                        cursor: "pointer",
                                        fontSize: "1.3rem",
                                        transition: "all 0.2s",
                                        opacity: showVtrShortcutsModal ? 1 : 0.45,
                                      }}
                                      onMouseEnter={(e) => {
                                        e.currentTarget.style.color =
                                          "var(--color-role-sp, #F72D60)";
                                        e.currentTarget.style.opacity = "1";
                                      }}
                                      onMouseLeave={(e) => {
                                        e.currentTarget.style.color = showVtrShortcutsModal
                                          ? "var(--color-role-sp, #F72D60)"
                                          : "#aaa";
                                        e.currentTarget.style.opacity = showVtrShortcutsModal
                                          ? "1"
                                          : "0.45";
                                      }}
                                    />

                                    {/* Fullscreen */}
                                    <i
                                      className="bx bx-fullscreen"
                                      onClick={() => {
                                        if (previewClip) getActiveVtrPlayer()?.requestFullscreen();
                                      }}
                                      style={{
                                        color: "#aaa",
                                        cursor: "pointer",
                                        fontSize: "1.15rem",
                                        transition: "color 0.2s",
                                      }}
                                      title={t(
                                        "storyproducer.relayClip.fullscreen",
                                        "Pantalla completa",
                                      )}
                                      onMouseEnter={(e) => (e.currentTarget.style.color = "#fff")}
                                      onMouseLeave={(e) => (e.currentTarget.style.color = "#aaa")}
                                    />
                                  </div>
                                </div>
                              </div>

                              {/* Shortcuts popover — no bloquea el video */}
                              {showVtrShortcutsModal && (
                                <div
                                  style={{
                                    position: "absolute",
                                    bottom: "58px",
                                    right: "12px",
                                    width: "300px",
                                    backgroundColor: "rgba(14, 14, 18, 0.97)",
                                    border: "1px solid rgba(255,255,255,0.1)",
                                    borderRadius: "10px",
                                    boxShadow: "0 8px 32px rgba(0,0,0,0.7)",
                                    backdropFilter: "blur(12px)",
                                    WebkitBackdropFilter: "blur(12px)",
                                    zIndex: 20,
                                    padding: "12px 16px",
                                    color: "#fff",
                                  }}
                                >
                                  <div
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: "6px",
                                      borderBottom: "1px solid rgba(255,255,255,0.07)",
                                      paddingBottom: "8px",
                                      marginBottom: "10px",
                                    }}
                                  >
                                    <i
                                      className="bx bxs-keyboard"
                                      style={{
                                        fontSize: "1rem",
                                        color: "var(--color-role-sp, #F72D60)",
                                      }}
                                    />
                                    <span style={{ fontWeight: 600, fontSize: "0.82rem" }}>
                                      Atajos del Player
                                    </span>
                                  </div>
                                  {(() => {
                                    const kb: React.CSSProperties = {
                                      background: "rgba(255,255,255,0.1)",
                                      border: "1px solid rgba(255,255,255,0.18)",
                                      borderRadius: "4px",
                                      padding: "1px 5px",
                                      fontFamily: "monospace",
                                      fontSize: "0.68rem",
                                      color: "#fff",
                                    };
                                    const row = (label: string, keys: React.ReactNode) => (
                                      <div
                                        style={{
                                          display: "flex",
                                          justifyContent: "space-between",
                                          alignItems: "center",
                                          padding: "3px 0",
                                        }}
                                      >
                                        <span style={{ color: "#999", fontSize: "0.72rem" }}>
                                          {label}
                                        </span>
                                        <div style={{ display: "flex", gap: "3px" }}>{keys}</div>
                                      </div>
                                    );
                                    return (
                                      <div
                                        style={{
                                          display: "flex",
                                          flexDirection: "column",
                                          gap: "1px",
                                        }}
                                      >
                                        {row("Retroceder (×1 ×1.5 ×2 ×4)", <kbd style={kb}>J</kbd>)}
                                        {row("Pausa", <kbd style={kb}>K</kbd>)}
                                        {row("Play (×1 ×1.5 ×2 ×4)", <kbd style={kb}>L</kbd>)}
                                        {row("Play / Pause", <kbd style={kb}>Espacio</kbd>)}
                                        {row(
                                          "±1 frame",
                                          <>
                                            <kbd style={kb}>←</kbd>
                                            <kbd style={kb}>→</kbd>
                                          </>,
                                        )}
                                        {row(
                                          "±5 segundos",
                                          <>
                                            <kbd style={kb}>Shift</kbd>
                                            <span style={{ color: "#555", margin: "0 2px" }}>
                                              +
                                            </span>
                                            <kbd style={kb}>←</kbd>
                                            <kbd style={kb}>→</kbd>
                                          </>,
                                        )}
                                        {row(
                                          "Corte anterior / siguiente",
                                          <>
                                            <kbd style={kb}>↑</kbd>
                                            <kbd style={kb}>↓</kbd>
                                          </>,
                                        )}
                                      </div>
                                    );
                                  })()}
                                </div>
                              )}
                            </div>
                          )}

                          {/* ── Timeline NLE ─────────────────────────────────────── */}
                          {!isMockPlayerCollapsed &&
                            (() => {
                              const totalMs =
                                playoutItems.reduce(
                                  (acc, it) => acc + getScrubberDurationMs(it),
                                  0,
                                ) || 1;
                              const fmtTc = (ms: number) => {
                                const h = Math.floor(ms / 3600000);
                                const m = Math.floor((ms % 3600000) / 60000);
                                const s = Math.floor((ms % 60000) / 1000);
                                if (h > 0)
                                  return `${h}:${m.toString().padStart(2, "0")}:${s.toString().padStart(2, "0")}`;
                                return `${m.toString().padStart(2, "0")}:${s.toString().padStart(2, "0")}`;
                              };
                              // Tick interval for ruler: aims for ~6-8 visible ticks
                              const baseIntervals = [
                                5000, 10000, 15000, 30000, 60000, 120000, 300000, 600000,
                              ];
                              const ruleTick =
                                baseIntervals.find((iv) => totalMs / iv <= 10) ?? 600000;
                              const ruleMarks: number[] = [];
                              for (let t = 0; t <= totalMs; t += ruleTick) ruleMarks.push(t);

                              const hoveredItem =
                                hoveredClipIdx !== null ? playoutItems[hoveredClipIdx] : null;
                              return (
                                <div
                                  style={{
                                    marginTop: "12px",
                                    padding: "0 8px 8px 8px",
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: "4px",
                                    position: "relative",
                                    userSelect: "none",
                                  }}
                                  onWheel={(e) => {
                                    e.preventDefault();
                                    setTimelineZoom((prev) =>
                                      Math.max(1, Math.min(6, prev * (e.deltaY < 0 ? 1.15 : 0.87))),
                                    );
                                  }}
                                >
                                  {/* Zoom indicator */}
                                  {timelineZoom > 1 && (
                                    <div
                                      style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "6px",
                                        marginBottom: "2px",
                                      }}
                                    >
                                      <span
                                        style={{
                                          fontSize: "0.65rem",
                                          color: "#555",
                                          fontFamily: "monospace",
                                        }}
                                      >
                                        ZOOM {timelineZoom.toFixed(1)}x
                                      </span>
                                      <button
                                        onClick={() => setTimelineZoom(1)}
                                        style={{
                                          background: "rgba(255,255,255,0.06)",
                                          border: "none",
                                          color: "#666",
                                          cursor: "pointer",
                                          padding: "1px 6px",
                                          borderRadius: "3px",
                                          fontSize: "0.62rem",
                                        }}
                                        onMouseEnter={(e) => (e.currentTarget.style.color = "#fff")}
                                        onMouseLeave={(e) => (e.currentTarget.style.color = "#666")}
                                      >
                                        reset
                                      </button>
                                    </div>
                                  )}

                                  {/* Scrollable inner */}
                                  <div
                                    style={{
                                      width: "100%",
                                      overflowX: "auto",
                                      overflowY: "hidden",
                                      paddingBottom: "4px",
                                    }}
                                  >
                                    <div
                                      ref={scrubberRef}
                                      style={{
                                        position: "relative",
                                        width: `${timelineZoom * 100}%`,
                                        minWidth: `max(100%, ${playoutItems.length * 60}px)`,
                                      }}
                                    >
                                      {/* ── Ruler ─────────────────── */}
                                      <div
                                        style={{
                                          position: "relative",
                                          height: "18px",
                                          marginBottom: "4px",
                                        }}
                                      >
                                        {ruleMarks.map((ms) => {
                                          const pct = (ms / totalMs) * 100;
                                          if (pct > 100) return null;
                                          return (
                                            <div
                                              key={ms}
                                              style={{
                                                position: "absolute",
                                                left: `${pct}%`,
                                                top: 0,
                                                display: "flex",
                                                flexDirection: "column",
                                                alignItems: "center",
                                                transform: "translateX(-50%)",
                                                pointerEvents: "none",
                                              }}
                                            >
                                              <div
                                                style={{
                                                  width: "1px",
                                                  height: "5px",
                                                  background: "rgba(255,255,255,0.2)",
                                                }}
                                              />
                                              <span
                                                style={{
                                                  fontSize: "0.58rem",
                                                  color: "#555",
                                                  fontFamily: "monospace",
                                                  whiteSpace: "nowrap",
                                                  marginTop: "1px",
                                                }}
                                              >
                                                {fmtTc(ms)}
                                              </span>
                                            </div>
                                          );
                                        })}
                                      </div>

                                      {/* ── Ruler scrub area (transparent overlay for drag) ── */}
                                      <div
                                        onMouseDown={(e) => {
                                          setIsScrubbing(true);
                                          const rect = scrubberRef.current?.getBoundingClientRect();
                                          if (!rect) return;
                                          let pct = ((e.clientX - rect.left) / rect.width) * 100;
                                          pct = Math.max(0, Math.min(100, pct));
                                          setScrubPct(pct);
                                          scrubPctRef.current = pct;
                                          seekVtrToScrubPct(pct);
                                        }}
                                        style={{
                                          position: "absolute",
                                          top: 0,
                                          left: 0,
                                          right: 0,
                                          height: "22px",
                                          cursor: "col-resize",
                                          zIndex: 11,
                                        }}
                                      />

                                      {/* ── Clip blocks ─────────────────── */}
                                      <div
                                        style={{
                                          display: "flex",
                                          width: "100%",
                                          height: "44px",
                                          background: "rgba(0,0,0,0.3)",
                                          borderRadius: "6px",
                                          overflow: "hidden",
                                          border: "1px solid rgba(255,255,255,0.07)",
                                          position: "relative",
                                        }}
                                      >
                                        {playoutItems.map((item, i) => {
                                          const ms = getScrubberDurationMs(item);
                                          const pct = (ms / totalMs) * 100;
                                          const isSelected =
                                            activeMockTc === (item.tcIn || item.tc);
                                          const realType = getItemRealType(item);
                                          const color = getLogColor(realType);
                                          const rawText = item.selectedText || item.title || "";
                                          const label = rawText
                                            .replace(/<[^>]*>/g, "")
                                            .trim()
                                            .slice(0, 40);
                                          return (
                                            <div
                                              key={i}
                                              onClick={() => {
                                                setActiveMockTc(item.tcIn || item.tc || "");
                                                openPreviewClip(item);
                                                let accMs = 0;
                                                for (let j = 0; j < i; j++)
                                                  accMs += getScrubberDurationMs(playoutItems[j]);
                                                setScrubPct((accMs / totalMs) * 100);
                                              }}
                                              style={{
                                                width: `${pct}%`,
                                                minWidth: "4px",
                                                height: "100%",
                                                background: isSelected
                                                  ? `${color}ee`
                                                  : `${color}99`,
                                                borderRight:
                                                  i < playoutItems.length - 1
                                                    ? "1px solid rgba(0,0,0,0.5)"
                                                    : "none",
                                                // Resaltado amarillo para el clip activo
                                                boxShadow: isSelected
                                                  ? "inset 0 0 0 2px #F59E0B"
                                                  : "none",
                                                borderTop: isSelected
                                                  ? "2px solid #F59E0B"
                                                  : "2px solid transparent",
                                                cursor: "pointer",
                                                transition: "filter 0.15s",
                                                display: "flex",
                                                flexDirection: "column",
                                                justifyContent: "center",
                                                padding: "2px 4px",
                                                overflow: "hidden",
                                                position: "relative",
                                              }}
                                              onMouseOver={(e) =>
                                                (e.currentTarget.style.filter = "brightness(1.3)")
                                              }
                                              onMouseOut={(e) =>
                                                (e.currentTarget.style.filter = "none")
                                              }
                                              title={label || `Corte ${i + 1}`}
                                            >
                                              {/* Número de orden */}
                                              <span
                                                style={{
                                                  fontSize: "0.55rem",
                                                  fontWeight: 700,
                                                  color: isSelected
                                                    ? "#F59E0B"
                                                    : "rgba(255,255,255,0.45)",
                                                  lineHeight: 1,
                                                  whiteSpace: "nowrap",
                                                  overflow: "hidden",
                                                  flexShrink: 0,
                                                }}
                                              >
                                                {i + 1}
                                              </span>
                                              {/* Texto de la tarjeta */}
                                              {label && (
                                                <span
                                                  style={{
                                                    fontSize: "0.62rem",
                                                    fontWeight: 500,
                                                    color: "rgba(255,255,255,0.9)",
                                                    whiteSpace: "nowrap",
                                                    overflow: "hidden",
                                                    textOverflow: "ellipsis",
                                                    lineHeight: 1.25,
                                                    marginTop: "1px",
                                                  }}
                                                >
                                                  {label}
                                                </span>
                                              )}
                                            </div>
                                          );
                                        })}
                                      </div>

                                      {/* ── Playhead ─────────────────── */}
                                      <div
                                        style={{
                                          position: "absolute",
                                          left: `${scrubPct}%`,
                                          top: "-4px",
                                          bottom: "0",
                                          width: "2px",
                                          background: "#ff2a2a",
                                          pointerEvents: "none",
                                          zIndex: 10,
                                          transform: "translateX(-1px)",
                                        }}
                                      >
                                        <svg
                                          width="12"
                                          height="10"
                                          viewBox="0 0 12 10"
                                          style={{
                                            position: "absolute",
                                            top: "-9px",
                                            left: "-5px",
                                            fill: "#ff2a2a",
                                          }}
                                        >
                                          <path d="M0,0 L12,0 L12,5 L6,10 L0,5 Z" />
                                        </svg>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              );
                            })()}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                {/* End right column wrapper */}

                {/* ── Left Column in 2-col mode: CortesEditor ── */}
                <div
                  style={
                    isOnlyCutMode
                      ? {
                          gridColumn: 1,
                          gridRow: "1 / -1",
                          display: "flex",
                          flexDirection: "column" as const,
                          minHeight: 0,
                          overflow: "auto",
                        }
                      : {
                          flex: 1,
                          display: "flex",
                          flexDirection: "column" as const,
                          minHeight: 0,
                          overflow: "auto",
                        }
                  }
                >
                  {canShowEditToggle ? (
                    <CortesEditor
                      onItemsChange={setCutLiveItems}
                      onTimecodeClick={handleCortesEditorTimecodeClick}
                      activeMockTc={activeMockTc}
                    />
                  ) : (
                    <>
                      {/* Add Selected Button */}
                      {!readOnly && selectedItems.length > 0 && (
                        <div
                          style={{
                            padding: "10px 12px",
                            borderBottom: "1px solid rgba(255,255,255,0.05)",
                          }}
                        >
                          <button
                            onClick={handleAddSelectedToProject}
                            style={{
                              width: "100%",
                              padding: "10px",
                              background: "#F59E0B",
                              border: "none",
                              borderRadius: "8px",
                              color: "#000",
                              fontWeight: 600,
                              cursor: "pointer",
                              fontSize: "0.82rem",
                            }}
                          >
                            <i className="bx bx-plus-circle" style={{ marginRight: "6px" }}></i>
                            Add {selectedItems.length} item(s) to "{activeProject?.name}"
                          </button>
                        </div>
                      )}
                      {/* readOnly check handled below in LogCard props */}

                      {/* Active Project Items */}
                      <div
                        data-testid="assembly-project-items"
                        style={{ flex: 1, overflow: "auto", padding: "10px" }}
                      >
                        {activeProject && (!showOnlyApproved || activeProject.isApproved) ? (
                          <>
                            <div
                              style={{
                                fontSize: "0.65rem",
                                color: "#555",
                                textTransform: "uppercase",
                                marginBottom: "10px",
                                letterSpacing: "0.5px",
                                display: "flex",
                                alignItems: "center",
                                gap: "6px",
                              }}
                            >
                              {activeProject.name} ({activeProject.items.length} items)
                              {!readOnly && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleToggleApproved(activeProject);
                                  }}
                                  title={
                                    activeProject.isApproved
                                      ? t("controller.layout.isApproved", "Approved")
                                      : t("controller.layout.markAsApproved", "Mark as approved")
                                  }
                                  style={{
                                    background: "transparent",
                                    border: "none",
                                    color: activeProject.isApproved ? "#10b981" : "#555",
                                    cursor: "pointer",
                                    padding: "2px",
                                    display: "flex",
                                    alignItems: "center",
                                  }}
                                >
                                  <i
                                    className={
                                      activeProject.isApproved
                                        ? "bx bx-check-circle"
                                        : "bx bx-circle"
                                    }
                                    style={{ fontSize: "1rem" }}
                                  ></i>
                                </button>
                              )}
                              {readOnly && activeProject.isApproved && (
                                <i
                                  className="bx bx-check-circle"
                                  style={{ fontSize: "1rem", color: "#10b981" }}
                                ></i>
                              )}
                              {/* VTR Tag picker button — only in edit mode */}
                              {!readOnly && (
                                <Popover>
                                  <PopoverTrigger
                                    title={t("controller.tags.vtrTagsConfig", "VTR Tags")}
                                    style={{
                                      background: "transparent",
                                      border: "none",
                                      color:
                                        (activeProject.vtrTags || []).length > 0
                                          ? "var(--color-role-sp)"
                                          : "#555",
                                      cursor: "pointer",
                                      padding: "2px",
                                      display: "flex",
                                      alignItems: "center",
                                    }}
                                  >
                                    <i
                                      className="bx bx-purchase-tag"
                                      style={{ fontSize: "1rem" }}
                                    ></i>
                                  </PopoverTrigger>
                                  <PopoverContent
                                    align="start"
                                    style={{
                                      background: "#1a1a1a",
                                      border: "1px solid #333",
                                      borderRadius: "8px",
                                      padding: "8px",
                                      minWidth: "180px",
                                      boxShadow: "0 8px 24px rgba(0,0,0,0.5)",
                                      display: "flex",
                                      flexDirection: "column",
                                      gap: "4px",
                                    }}
                                  >
                                    <div
                                      style={{
                                        fontSize: "0.6rem",
                                        color: "#666",
                                        textTransform: "uppercase",
                                        letterSpacing: "0.5px",
                                        marginBottom: "4px",
                                        padding: "0 4px",
                                      }}
                                    >
                                      {t("controller.tags.vtrTagsConfig", "VTR TAGS")}
                                    </div>
                                    {vtrTags.length === 0 ? (
                                      <div
                                        style={{
                                          padding: "4px",
                                          color: "#aaa",
                                          fontSize: "0.75rem",
                                        }}
                                      >
                                        {t("controller.tags.noVtrTags", "No VTR tags available")}
                                      </div>
                                    ) : (
                                      vtrTags.map((vt) => {
                                        const isSelected = (activeProject.vtrTags || []).includes(
                                          vt.id,
                                        );
                                        return (
                                          <button
                                            key={vt.id}
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              const currentTags = activeProject.vtrTags || [];
                                              const newTags = isSelected
                                                ? currentTags.filter((t) => t !== vt.id)
                                                : [...currentTags, vt.id];
                                              handleProjectUpdate(activeProject.id, {
                                                vtrTags: newTags,
                                              });
                                            }}
                                            style={{
                                              display: "flex",
                                              alignItems: "center",
                                              gap: "8px",
                                              padding: "5px 8px",
                                              background: isSelected
                                                ? "rgba(255,255,255,0.08)"
                                                : "transparent",
                                              border: "none",
                                              borderRadius: "4px",
                                              cursor: "pointer",
                                              width: "100%",
                                              textAlign: "left",
                                            }}
                                          >
                                            <span
                                              style={{
                                                width: "10px",
                                                height: "10px",
                                                borderRadius: "50%",
                                                background: vt.color,
                                                flexShrink: 0,
                                                border: isSelected
                                                  ? "2px solid #fff"
                                                  : "2px solid transparent",
                                              }}
                                            />
                                            <span
                                              style={{
                                                color: isSelected ? "#fff" : "#aaa",
                                                fontSize: "0.75rem",
                                                fontWeight: isSelected ? 600 : 400,
                                                textTransform: "uppercase",
                                              }}
                                            >
                                              {vt.label}
                                            </span>
                                            {isSelected && (
                                              <i
                                                className="bx bx-check"
                                                style={{
                                                  marginLeft: "auto",
                                                  color: "#10b981",
                                                  fontSize: "0.9rem",
                                                }}
                                              ></i>
                                            )}
                                          </button>
                                        );
                                      })
                                    )}
                                  </PopoverContent>
                                </Popover>
                              )}
                              {/* Display assigned VTR tag pills */}
                              {(activeProject.vtrTags || []).map((tagId) => (
                                <span
                                  key={tagId}
                                  style={{
                                    background: getVtrTagColor(tagId),
                                    color: "#fff",
                                    fontSize: "0.6rem",
                                    padding: "1px 6px",
                                    borderRadius: "4px",
                                    fontWeight: 600,
                                    textTransform: "uppercase",
                                    cursor: readOnly ? "default" : "pointer",
                                  }}
                                  title={
                                    readOnly
                                      ? getVtrTagLabel(tagId)
                                      : `${t("storyProducer.removeTag", "Remove tag")}: ${getVtrTagLabel(tagId)}`
                                  }
                                  onClick={(e) => {
                                    if (readOnly) return;
                                    e.stopPropagation();
                                    const newTags = (activeProject.vtrTags || []).filter(
                                      (t) => t !== tagId,
                                    );
                                    handleProjectUpdate(activeProject.id, { vtrTags: newTags });
                                  }}
                                >
                                  {getVtrTagLabel(tagId)}
                                </span>
                              ))}
                              {/* VTR Star Rating */}
                              {!readOnly ? (
                                <StarRating
                                  value={activeProject.vtrStars ?? 0}
                                  max={2}
                                  onChange={(val) =>
                                    handleProjectUpdate(activeProject.id, { vtrStars: val })
                                  }
                                  size="0.9rem"
                                  activeColor="#ffaa00"
                                  inactiveColor="#444"
                                />
                              ) : (
                                (activeProject.vtrStars ?? 0) > 0 && (
                                  <StarRating
                                    value={activeProject.vtrStars ?? 0}
                                    max={2}
                                    readOnly
                                    size="0.9rem"
                                    activeColor="#ffaa00"
                                  />
                                )
                              )}
                            </div>
                            {/* VTR Description */}
                            <VtrPopupField
                              value={activeProject.vtrDescription || ""}
                              readOnly={!!readOnly}
                              onSave={(val) =>
                                handleProjectUpdate(activeProject.id, { vtrDescription: val })
                              }
                              title="Descripción de Proyecto"
                              placeholder="Agregar descripción..."
                            />
                            {renderProjectItems({
                              projectId: activeProject.id,
                              items: activeProject.items,
                            })}
                          </>
                        ) : (
                          <div
                            style={{
                              color: "#555",
                              textAlign: "center",
                              padding: "24px",
                              fontSize: "0.85rem",
                            }}
                          >
                            {showOnlyApproved
                              ? t(
                                  "controller.layout.noApprovedSelected",
                                  "No approved VTR selected. Select a VTR.",
                                )
                              : t(
                                  "controller.layout.noProjectSelected",
                                  "No cut selected. Create or select a cut.",
                                )}
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </div>
                {/* End left column wrapper */}
              </div>
            )}
          </div>
        )}
        {/* END Main Content Area */}
        <CustomDialog
          isOpen={showExportDialog}
          title={t("storyproducer.assembly.exportSequenceTitle", "Export Sequence")}
          message={t(
            "storyproducer.assembly.exportSequenceMessage",
            `Do you want to export "{{projectName}}" with {{count}} items?`,
            { projectName: projectToExport?.name, count: projectToExport?.items.length },
          )}
          type="alert"
          hideFooter
          onConfirm={() => {}}
          onCancel={() => {
            setShowExportDialog(false);
            setProjectToExport(null);
          }}
        >
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "20px",
              marginTop: "24px",
            }}
          >
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: "12px",
                justifyContent: "space-between",
              }}
            >
              <button
                onClick={() => {
                  const target = projectToExport || activeProject;
                  if (target) exportActiveProjectTxt(target);
                  else toast.error("No hay VTR seleccionado para exportar");
                  setShowExportDialog(false);
                  setProjectToExport(null);
                }}
                style={{
                  flex: "1 1 calc(25% - 12px)",
                  padding: "16px",
                  background: "rgba(59, 130, 246, 0.05)",
                  color: "#93c5fd",
                  border: "1px solid rgba(59, 130, 246, 0.3)",
                  borderRadius: "8px",
                  cursor: "pointer",
                  fontSize: "0.85rem",
                  fontWeight: 600,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: "8px",
                  transition: "all 0.2s",
                }}
              >
                <i className="bx bx-file-blank" style={{ fontSize: "1.5rem" }}></i>
                TXT
              </button>
              <button
                onClick={() => {
                  const target = projectToExport || activeProject;
                  if (target) exportActiveProjectMarker(target);
                  else toast.error("No hay VTR seleccionado para exportar");
                  setShowExportDialog(false);
                  setProjectToExport(null);
                }}
                style={{
                  flex: "1 1 calc(25% - 12px)",
                  padding: "16px",
                  background: "rgba(16, 185, 129, 0.05)",
                  color: "#6ee7b7",
                  border: "1px solid rgba(16, 185, 129, 0.3)",
                  borderRadius: "8px",
                  cursor: "pointer",
                  fontSize: "0.85rem",
                  fontWeight: 600,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: "8px",
                  transition: "all 0.2s",
                }}
              >
                <i className="bx bx-bookmark" style={{ fontSize: "1.5rem" }}></i>
                Marcador
              </button>
              <button
                onClick={async () => {
                  const target = projectToExport || activeProject;
                  if (target)
                    await exportActiveProjectDOC(
                      target,
                      allTagsData,
                      categories,
                      (authUser as any)?.name,
                    );
                  else toast.error("No hay VTR seleccionado para exportar");
                  setShowExportDialog(false);
                  setProjectToExport(null);
                }}
                style={{
                  flex: "1 1 calc(25% - 12px)",
                  padding: "16px",
                  background: "rgba(139, 92, 246, 0.05)",
                  color: "#c4b5fd",
                  border: "1px solid rgba(139, 92, 246, 0.3)",
                  borderRadius: "8px",
                  cursor: "pointer",
                  fontSize: "0.85rem",
                  fontWeight: 600,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: "8px",
                  transition: "all 0.2s",
                }}
              >
                <i className="bx bxs-file-doc" style={{ fontSize: "1.5rem" }}></i>
                DOC
              </button>
            </div>

            <button
              onClick={() => {
                setShowExportDialog(false);
                setProjectToExport(null);
              }}
              style={{
                alignSelf: "flex-end",
                padding: "8px 24px",
                background: "transparent",
                color: "#aaa",
                border: "1px solid rgba(255,255,255,0.1)",
                borderRadius: "6px",
                cursor: "pointer",
                fontSize: "0.85rem",
                fontWeight: 500,
                transition: "all 0.2s",
              }}
            >
              {t("common.dialog.cancel", "Cancelar")}
            </button>
            <button
              onClick={() => {
                const target = projectToExport || activeProject;
                if (target) {
                  exportActiveProjectDoc(target);
                } else {
                  toast.error("No hay VTR seleccionado para exportar");
                }
                setShowExportDialog(false);
                setProjectToExport(null);
              }}
              style={{
                padding: "8px 16px",
                background: "#2563eb",
                color: "#fff",
                border: "none",
                borderRadius: "6px",
                cursor: "pointer",
                fontSize: "0.85rem",
                fontWeight: 600,
                display: "flex",
                alignItems: "center",
                gap: "6px",
              }}
            >
              <i className="bx bxs-file-doc"></i>{" "}
              {t("storyproducer.assembly.exportDoc", "Export DOC")}
            </button>
          </div>
        </CustomDialog>
        {/* Delete Project Confirmation */}
        <CustomDialog
          isOpen={!!pendingDeleteProjectId}
          title={t("storyproducer.assembly.deleteProject")}
          message={`Are you sure you want to delete the project "${assemblyProjects.find((p) => p.id === pendingDeleteProjectId)?.name || ""}"? This action cannot be undone.`}
          type="confirm"
          onConfirm={() => {
            if (pendingDeleteProjectId) deleteProject(pendingDeleteProjectId);
            setPendingDeleteProjectId(null);
          }}
          onCancel={() => setPendingDeleteProjectId(null)}
          confirmText={t("storyproducer.assembly.delete")}
          cancelText={t("storyproducer.assembly.cancel")}
        />
        {/* Delete Folder Confirmation */}
        <CustomDialog
          isOpen={!!pendingDeleteFolderId}
          title="DELETE FOLDER"
          message={`Are you sure you want to delete this folder? Projects inside it will be moved to the root.`}
          type="confirm"
          onConfirm={() => {
            if (pendingDeleteFolderId) deleteFolder(pendingDeleteFolderId);
            setPendingDeleteFolderId(null);
          }}
          onCancel={() => setPendingDeleteFolderId(null)}
          confirmText={t("storyproducer.assembly.delete")}
          cancelText={t("storyproducer.assembly.cancel")}
        />
      </div>
    );
  },
);

export default AssemblyManager;
